import React, { useCallback, useEffect, useRef, useState } from "react";
import { BuzerIcon } from "../../BuzerIcons";
import { LEVEL_ICON, TIER_ICON } from "../../LevelIcons";
import { AddCard } from "../../components/forms/AddCard";
import { BadgeGrid, Chk, CompletionBurst, ConfirmDelete, EffectBurst, FormShell, Glyph, HintBanner, IconPicker, InstallGuide, LevelUpModal, Modal, MoodFace, ProButton, ShareStatSheet, Timer, useModalBackClose, useProfileModal, useScrollLock } from "../../components/ui";
import { useIsNarrow } from "../../hooks/useIsNarrow";
import { useDragReorder } from "../../hooks/useDragReorder";
import { Icon } from "../../icons";
import { api, saveOrder } from "../../lib/api";
import { IS_TOUCH, MOODS, moodIconName, RECUR_PERIOD_UNIT } from "../../lib/constants";
import { fmtDuePretty } from "../../lib/date-sk";
import { WEEKDAYS_SK, fmtShortDate, todayKey, fmtDur } from "../../lib/format";
import { resolveIcon, iconChipStyle } from "../../lib/icons-resolve";
import { LEVELS, LEVEL_CATS, DEN_NULA, getLevel, gname, levelIndex, nextLevel } from "../../lib/levels";
import { enablePush, isStandalone } from "../../lib/pwa";
import { session } from "../../lib/session";
import { st } from "../../styles.js";
import { ChallengeBoard, ChallengeCreate, CompetitionDetail } from "../challenges/challenges";
import { ChallengeInfoModal } from "../challenges/ChallengeInfoModal";
import { GlobalLaunchModal } from "../challenges/GlobalLaunchModal";
import { ActionMenu, Button, Caption, CheckButton, GroupCard, IconButton, Lead, List, ListRow, Segmented, SectionHeader, Sheet, StateDot, StatTile, Tag } from "../../components/ui/ds";
import { HeatmapInline } from "./ActivityHeatmap";
import { EnneagramTile } from "./EnneagramTile";
import { TaskDetail } from "./TaskDetail";
import { DayRing, DayRingModal } from "./DayRing";
// v31.1 — bubliny dočasne skryté (kód ostáva); import odkomentuj pri zapnutí
// import { StatBubbles } from "./StatBubbles";
import { PrehladSwitch } from "./PrehladSwitch";
import { LeagueSection, ChallengeChatButton } from "../social/SocialFeature";
import { RoutineDetail, RoutineEdit, RoutineCreate } from "./RoutineModals";

// wt34 — súťaž (comp) v skupine sa ťahá cez PER-ROW dnd (rovnako ako výzva), ale nemá task_id.
// Jej riadok nesie data-taskid = COMP_DRAG + comp.id, aby sa identita ťahania (číselné dragId
// v computeDrop/commitDrop) NEKOLIDOVALA s reálnymi task_id. commitDrop podľa tohto prahu odbočí
// na comp-vetvu (perzistencia cez /api/challenges/<cid>/group, NIE /api/tasks/reorder).
const COMP_DRAG = 100000000;

export function SuggestCard({ onAdded, routines, open: openProp, onOpenChange }) {
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = controlled ? openProp : openState;
  const setOpen = (v) => { if (controlled) { onOpenChange && onOpenChange(v); } else setOpenState(v); };
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [err, setErr] = useState("");
  async function go(e) {
    e.preventDefault(); setErr(""); setBusy(true);
    try { setIdeas((await api("/api/ai/suggest", { method: "POST", body: { goal } })).ideas || []); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }
  async function add(idea) {
    // v17 — optionally file into a routine: match existing by name, else create the AI-proposed one
    let cat = (routines || []).find((r) => r.name.toLowerCase() === (idea.category || "").toLowerCase());
    let catId = cat ? cat.id : null;
    if (!cat && (idea.category || "").trim()) {
      try { const cr = await api("/api/categories", { method: "POST", body: { name: idea.category.trim(), icon: idea.icon || "folder" } }); if (cr && cr.id) catId = cr.id; } catch (e) { /* */ }
    }
    await api("/api/tasks", { method: "POST", body: {
      kind: "habit", icon: idea.icon, label: idea.label, desc: idea.desc, points: idea.points, category_id: catId } });
    setIdeas((p) => p.filter((x) => x !== idea));
    onAdded();
  }
  if (!open) return <button onClick={() => setOpen(true)} style={st.addToggle}><BuzerIcon name="ai" size={16} /> Navrhni mi návyky</button>;
  function close() { setOpen(false); setIdeas([]); }
  return (
    <FormShell modal={open} title="Návrh návykov od AI" icon="ai" onClose={close}>
    <form onSubmit={go} style={st.formCol}>
      <div style={st.fieldHint}>Na čom chceš popracovať?</div>
      <input className="field" style={st.field} placeholder="napr. lepší spánok, viac pohybu"
        value={goal} onChange={(e) => setGoal(e.target.value)} autoFocus />
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={close} style={st.ghostBtn}>Zavrieť</button>
        <button type="submit" disabled={busy} style={{ ...st.primaryBtn, flex: 1 }}>{busy ? "premýšľam…" : "Navrhni"}</button>
      </div>
      {err && <div style={st.err}>{err}</div>}
      {ideas.map((idea, i) => (
        <div key={i} className="task" style={{ ...st.task, ...st.remCard }}>
          <span style={st.taskIcon}><Glyph icon={idea.icon} fallback="star2" /></span>
          <span style={st.taskText}>
            <span style={st.taskLabel}>{idea.label}</span>
            <span style={st.taskDesc}>{idea.desc}{idea.desc ? " · " : ""}+{idea.points} b.</span>
            {idea.category && <span style={st.planTag}>{idea.category}</span>}
          </span>
          <button onClick={() => add(idea)} style={st.doneBtn}>Pridať</button>
        </div>
      ))}
    </form>
    </FormShell>
  );
}
// ─────────────────────────────────────────────────────────────
//  Tabuľkový (týždenný) pohľad — v16 §6 redizajn podľa mocku
//  days = [{ key, label, mood }], tasks = návyky s .week / .due_week
// ─────────────────────────────────────────────────────────────
// v25 §10.4 (variant B) — typový chip výzvy/súťaže: kruhový ring vo farbe typu + mini-badge
// (vlajka/trofej) v rožteku. chipStyle = základ chipu (st.taskIcon / st.routineIcon).
function TypeChip({ icon, comp = false, size = 22, chipStyle, className, done = false }) {
  // v25 — bez ringu (len mini-badge); na tintovanom done riadku jemne zvýrazni border chipu.
  // className umožní postaviť chip na rovnakom primitíve (bz-lead) ako ikona návyku,
  // aby sa výzva/súťaž v zozname vizuálne nerozišli s návykom (líši ich len rožný marker).
  // Rožný marker: sekundárne (brand-primary-surface) pozadie + primárna ikona (rovnaká paleta ako chip).
  return (
    <span className={className} style={{ ...chipStyle, position: "relative", ...(done ? { borderColor: "var(--neutral-border-strong)" } : {}) }}>
      <Glyph icon={icon} fallback={comp ? "trophy" : "flag"} size={size} />
      <span aria-hidden="true" style={{ position: "absolute", right: -4, bottom: -4, width: 16, height: 16, borderRadius: "50%", background: "var(--brand-primary-surface)", color: "var(--brand-primary)", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid var(--brand-primary-border)", boxShadow: "0 0 0 2px var(--surface-solid)" }}>
        <BuzerIcon name={comp ? "trophy" : "flag"} size={8} color="var(--brand-primary)" />
      </span>
    </span>
  );
}

// v25 §5 — História: mesačná matica splnení (riadky = návyky vrátane archívnych, stĺpce = dni).
// Všetko vidno naraz — žiadne klikanie po dňoch; mesiace sa prepínajú šípkami. Read-only.
export function HistorySheet({ onClose }) {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const scrollRef = useRef(null);
  const monthStr = ym.y + "-" + String(ym.m).padStart(2, "0");
  useEffect(() => {
    let live = true;
    setData(null);
    (async () => {
      try { const d = await api("/api/habit-history?month=" + monthStr); if (live) setData(d); }
      catch (e) { if (live) setData({ tasks: [] }); }
    })();
    return () => { live = false; };
  }, [monthStr]);
  // v29 — v aktuálnom mesiaci focus na DNEŠNÝ deň pri PRAVOM okraji viditeľnej plochy
  // (vidno maximum minulých dní, nie prázdnu budúcnosť); v minulých mesiacoch koniec mesiaca
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !data) return;
    const now2 = new Date();
    const cur = ym.y === now2.getFullYear() && ym.m === now2.getMonth() + 1;
    // v33h — namiesto odhadu šírky bunky (bol odrezaný pravý okraj dneška) zmeraj skutočnú
    // pozíciu dnešného stĺpca a doscrolluj tak, aby bol CELÝ vidno pri pravom okraji.
    const tc = cur && el.querySelector("[data-today]");
    if (tc) {
      const todayRight = (tc.getBoundingClientRect().right - el.getBoundingClientRect().left) + el.scrollLeft;
      el.scrollLeft = Math.max(0, todayRight - el.clientWidth + 14);
    } else {
      el.scrollLeft = el.scrollWidth;
    }
  }, [data]);  // eslint-disable-line react-hooks/exhaustive-deps
  const isCur = ym.y === now.getFullYear() && ym.m === now.getMonth() + 1;
  const daysIn = (data && data.days_in_month) || new Date(ym.y, ym.m, 0).getDate();
  const monthName = new Date(ym.y, ym.m - 1, 1).toLocaleDateString("sk-SK", { month: "long", year: "numeric" });
  const todayD = isCur ? now.getDate() : null;
  const shift = (d) => setYm((p) => { const dt = new Date(p.y, p.m - 1 + d, 1); return { y: dt.getFullYear(), m: dt.getMonth() + 1 }; });
  const tasks = (data && data.tasks) || [];
  const ordered = [...tasks.filter((t) => !t.archived), ...tasks.filter((t) => t.archived)];
  const dayNums = Array.from({ length: daysIn }, (_, i) => i + 1);
  const dayTotals = dayNums.map((d) => ordered.reduce((a, t) => a + (t.done_days.includes(d) ? 1 : 0), 0));
  const dowShort = (d) => WEEKDAYS_SK[(new Date(ym.y, ym.m - 1, d).getDay() + 6) % 7];
  const isWeekend = (d) => { const g = new Date(ym.y, ym.m - 1, d).getDay(); return g === 0 || g === 6; };
  const stickyTd = { position: "sticky", left: 0, background: "var(--neutral-surface)", zIndex: 1 };
  return (
    <Sheet full title="História" icon="calendar" onClose={onClose}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <IconButton variant="surface" size="sm" aria-label="Predchádzajúci mesiac" onClick={() => shift(-1)}><Icon name="chevron" size={15} color="currentColor" style={{ transform: "rotate(180deg)" }} /></IconButton>
        <span style={{ fontWeight: 700, fontSize: 15, textTransform: "capitalize" }}>{monthName}</span>
        <IconButton variant="surface" size="sm" aria-label="Nasledujúci mesiac" disabled={isCur} onClick={() => shift(1)}><Icon name="chevron" size={15} color="currentColor" /></IconButton>
      </div>
      {!data ? (
        <div style={st.taskDesc}>Načítavam…</div>
      ) : ordered.length === 0 ? (
        <div style={st.empty}>V tomto mesiaci nie sú žiadne záznamy.</div>
      ) : (
        // v27 §4 — Mali: mesačná mriežka so sticky stĺpcom potrebuje vlastnú GPU vrstvu (v26.2 pokryl len 7-dňovú)
        <div ref={scrollRef} style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", transform: "translateZ(0)", backfaceVisibility: "hidden" }}>
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...stickyTd, minWidth: 112 }} />
                {dayNums.map((d) => (
                  <th key={d} {...(d === todayD ? { "data-today": "1" } : {})} style={{ minWidth: 26, padding: "2px 1px", textAlign: "center" }}>
                    <div style={{ fontFamily: "var(--ff-mono)", fontSize: 10, fontWeight: 700, color: d === todayD ? "var(--brand-primary)" : isWeekend(d) ? "var(--neutral-text-subtle)" : "var(--neutral-text-muted)" }}>{d}</div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: "var(--neutral-text-subtle)", textTransform: "uppercase" }}>{dowShort(d)}</div>
                  </th>
                ))}
                <th style={{ minWidth: 34 }} />
              </tr>
              {/* v25 §5 — súčet splnení za deň („na kope“) */}
              <tr>
                <th style={{ ...stickyTd, textAlign: "left", fontFamily: "var(--ff-mono)", fontSize: 9, fontWeight: 600, color: "var(--neutral-text-subtle)", padding: "0 6px 5px 0" }}>SPOLU</th>
                {dayTotals.map((c, i) => (
                  <th key={i} style={{ fontFamily: "var(--ff-mono)", fontSize: 9.5, fontWeight: 600, color: "var(--neutral-text-muted)", padding: "0 1px 5px", textAlign: "center" }}>{c || "·"}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {/* v32.9 — riadok nálad: emoji histórie ako prvý riadok matice */}
              {data.moods && Object.keys(data.moods).length > 0 && (
                <tr>
                  <td style={{ ...stickyTd, padding: "4px 8px 4px 0" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span style={{ display: "inline-flex", flexShrink: 0 }}><BuzerIcon name="smile" size={14} color="var(--text)" /></span>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>Nálada</span>
                    </span>
                  </td>
                  {dayNums.map((d) => (
                    <td key={d} style={{ textAlign: "center", padding: "3px 1px", ...(d === todayD ? { background: "var(--ember-soft)" } : {}) }}>
                      {data.moods[d] ? <MoodFace k={moodIconName(data.moods[d])} size={15} />
                        : (todayD != null && d > todayD) ? null
                        : <span style={{ ...st.habDotMuted, width: 4, height: 4, opacity: 0.5 }} />}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
              {ordered.map((t) => (
                <tr key={t.id}>
                  {/* v26 fix — opacity len na OBSAHU, nie na bunke: sticky pozadie musí ostať
                      nepriehľadné, inak pri scrolle presvitajú krúžky splnení (archivovaný Plank) */}
                  <td style={{ ...stickyTd, padding: "4px 8px 4px 0" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: 140, opacity: t.archived ? 0.55 : 1 }}>
                      <span style={{ display: "inline-flex", flexShrink: 0 }}><Glyph icon={t.icon} size={14} /></span>
                      <span style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.label}</span>
                      {t.archived && <Tag tone="neutral" size="xs">archív</Tag>}
                    </span>
                  </td>
                  {dayNums.map((d) => (
                    <td key={d} style={{ textAlign: "center", padding: "3px 1px", ...(d === todayD ? { background: "var(--ember-soft)" } : {}) }}>
                      {t.done_days.includes(d) ? <StateDot state="done" size={13} />
                        : t.skip_days.includes(d) ? <StateDot state="skip" size={13} />
                        : (t.missed_days || []).includes(d) ? <StateDot state="miss" size={13} />
                        : (todayD != null && d > todayD) ? null
                        : <span style={{ ...st.habDotMuted, width: 4, height: 4, opacity: 0.5 }} />}
                    </td>
                  ))}
                  <td style={{ fontFamily: "var(--ff-mono)", fontSize: 10.5, fontWeight: 700, color: "var(--neutral-text-muted)", padding: "0 4px", whiteSpace: "nowrap" }}>{t.done_days.length}×</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ ...st.habLegend, flexWrap: "wrap", marginTop: 10 }}>
        <span style={st.habLegItem}><StateDot state="done" size={13} /> Splnené</span>
        <span style={st.habLegItem}><StateDot state="miss" size={13} /> Vynechané</span>
        <span style={st.habLegItem}><StateDot state="skip" size={13} /> Preskočené</span>
        <span style={st.habLegItem}><StateDot state="muted" /> Nebolo naplánované</span>
      </div>
      <div style={{ ...st.fieldHint, marginTop: 6 }}>História je len na čítanie — minulé dni upravíš v Týždni ceruzkou.</div>
    </Sheet>
  );
}

export function HabitTable({ tasks, days, onToggle, onCycle, onCellSet, pending, onMoodDay, editable = true, comps = [], onCompOpen }) {
  const [nameFor, setNameFor] = useState(null);  // B2 — tap an icon to reveal the habit name (mobile)
  // D1 — úvodný focus na dnes: dnešný stĺpec je posledný v `days` (pravý kraj) → scroll doprava.
  const scrollRef = useRef(null);
  useEffect(() => { const el = scrollRef.current; if (el) el.scrollLeft = el.scrollWidth; }, []);
  if (!tasks.length && !comps.length) return null;
  const tk = todayKey();
  const moodIconFor = (id) => MOODS.find((m) => m.id === id)?.icon || null;
  // v2 — buffered table edits: a local override wins over the server state
  const effState = (t, j, dkey) => {
    const raw = (t.week || [])[j] ? "done" : ((t.skip_week || [])[j] ? "skip" : "none");
    const p = pending && pending[t.id + "|" + dkey];
    return p || raw;
  };
  // v25 §4.2 — per-day "done/due" counters. Only habits actually due that day count; a
  // weekly_count habit has no fixed required day → it adds 1/1 only on days it was
  // completed; a skipped (excused) day is excluded entirely.
  const dayCounts = days.map((d, j) => {
    let total = 0, done = 0;
    for (const t of tasks) {
      if (t.start && d.key < t.start) continue;  // before the habit existed
      const eff = effState(t, j, d.key);
      if (t.week_target) { if (eff === "done") { total++; done++; } continue; }
      if (eff === "skip") continue;
      const due = t.due_calc_week ? t.due_calc_week[j] : (t.due_week || [])[j] !== false;
      if (due || eff === "done") { total++; if (eff === "done") done++; }
    }
    return { total, done };
  });
  return (
    <div style={st.habCard}>
      <div ref={scrollRef} style={{ overflowX: "auto" }}>
        <table style={st.habTable}>
          <thead>
            <tr>
              <th style={st.habThIcon} />
              {days.map((d) => {
                const today = d.key === tk;
                return (
                  <th key={d.key} style={st.habTh}>
                    <span style={today ? st.habTodayPill : undefined}>{d.label}</span>
                  </th>
                );
              })}
            </tr>
            {/* v25 §4.2 — splnené/naplánované pod každým dňom */}
            <tr>
              <th style={st.habThIcon} />
              {days.map((d, j) => (
                <th key={d.key} style={{ ...st.habTh, paddingTop: 0, fontFamily: "var(--ff-mono)", fontSize: 10, fontWeight: 600, color: "var(--muted)", textTransform: "none", letterSpacing: 0 }}
                  title={`${d.label}: splnené ${dayCounts[j].done} z ${dayCounts[j].total} naplánovaných`}>
                  {dayCounts[j].total > 0 ? `${dayCounts[j].done}/${dayCounts[j].total}` : "–"}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Nálada row */}
            <tr>
              <td style={st.habTdIcon}>
                <span style={st.habRowIcon}><BuzerIcon name="smile" size={18} color="var(--text)" /></span>
              </td>
              {days.map((d) => {
                const today = d.key === tk;
                const ic = moodIconFor(d.mood);
                return (
                  <td key={d.key} style={{ ...st.habCell, ...(today ? st.habColToday : {}) }}>
                    {/* v30 — miniatúry nálad = tá istá sada tvárí ako v pickri (MoodFace);
                        BuzerIcon „great" mal bodkové oči a nedal sa odlíšiť od „V pohode" */}
                    {editable ? (
                      <button type="button" onClick={() => onMoodDay && onMoodDay(d.key)} title="Doplniť/upraviť náladu"
                        style={{ border: "none", background: "transparent", padding: "4px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                        {ic ? <MoodFace k={ic} size={20} /> : <StateDot state="muted" />}
                      </button>
                    ) : (
                      <span style={{ padding: "4px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                        {ic ? <MoodFace k={ic} size={20} /> : <StateDot state="muted" />}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
            {/* Habit rows */}
            {tasks.map((t) => (
              <tr key={t.id}>
                {/* D1 fix — sticky name column: NEVER override position (sticky must win, else the
                    name cell scrolls off-screen with the today-focus auto-scroll on narrow phones).
                    position:sticky is itself a containing block, so habNamePop (absolute) still anchors here. */}
                <td style={st.habTdIcon}>
                  <button type="button" onClick={() => setNameFor((p) => p === t.id ? null : t.id)}
                    title={t.label} aria-label={"Názov: " + t.label}
                    style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ ...st.habRowIcon, ...iconChipStyle(t.icon_color) }}><Glyph icon={t.icon} size={18} /></span>
                    <span style={st.habRowName}>{t.label}</span>
                  </button>
                  {nameFor === t.id && (
                    <span style={st.habNamePop} onClick={() => setNameFor(null)}>{t.label}</span>
                  )}
                </td>
                {days.map((d, j) => {
                  const off = (t.due_week || [])[j] === false;  // B3 — not scheduled this weekday → disabled
                  const today = d.key === tk;
                  const eff = effState(t, j, d.key);
                  const done = eff === "done";
                  const skip = eff === "skip";
                  const wk = !!t.week_target;  // v25 §4.3 — N×/týždeň: no fixed required day → never red
                  const preStart = t.start && d.key < t.start;  // pred vznikom návyku → „nebolo naplánované“
                  const missed = !wk && !done && !skip && !off && d.key < tk && !preStart;  // past scheduled, not done → red
                  // green · blue(skip) · red · tiny grey (weekly past / pre-start) · empty = len dnešná ešte-nesplnená bunka
                  const dotNode = done ? <StateDot state="done" size={22} /> : skip ? <StateDot state="skip" size={22} /> : missed ? <StateDot state="miss" size={22} />
                    : ((wk && d.key < tk) || preStart) ? <StateDot state="muted" /> : <StateDot state="off" size={22} />;
                  const next = eff === "none" ? "done" : eff === "done" ? "skip" : "none";  // cycle
                  // H2: edit mode cycles the cell (splnené → preskočené → zmeškané); view is read-only
                  return (
                    <td key={d.key} style={{ ...st.habCell, ...(today ? st.habColToday : {}) }}>
                      {off ? (
                        <span title={t.label + " · " + d.label + " · v tento deň nie je naplánovaný"}
                          style={{ display: "inline-flex", padding: "4px", cursor: "not-allowed" }}><StateDot state="muted" /></span>
                      ) : editable ? (
                        <button type="button" onClick={() => (onCellSet ? onCellSet(t.id, d.key, next) : onCycle ? onCycle(t.id, d.key) : onToggle && onToggle(t.id, d.key))}
                          title={done ? "Splnené — klikni pre Preskočené" : skip ? "Preskočené — klikni pre Zmeškané" : "Klikni pre Splnené"}
                          aria-label={t.label + " · " + d.label + (done ? " · splnené" : skip ? " · preskočené" : missed ? " · zmeškané" : " · nesplnené")}
                          style={{ border: "none", cursor: "pointer", background: "transparent", padding: "4px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                          {dotNode}
                        </button>
                      ) : (
                        <span style={{ padding: "4px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                          {dotNode}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* #2i — súťaže ako riadky tabuľky: len ✓ (v ten deň zapísaný výsledok) alebo neutrálna
                gulička „nenaplánované" — žiadne preskočenie/vynechanie (súťaž sa nedá „zmeškať"). */}
            {comps.map((c) => (
              <tr key={"comp" + c.id}>
                {/* D1 fix — sticky name column (see habit row above): keep position:sticky */}
                <td style={st.habTdIcon}>
                  <button type="button" onClick={() => onCompOpen && onCompOpen(c)} title={c.name} aria-label={"Súťaž: " + c.name}
                    style={{ border: "none", background: "transparent", padding: 0, cursor: onCompOpen ? "pointer" : "default", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ ...st.habRowIcon, ...iconChipStyle(c.icon_color) }}><Glyph icon={c.icon} size={18} fallback="trophy" /></span>
                    <span style={st.habRowName}>{c.name}</span>
                  </button>
                </td>
                {days.map((d, j) => {
                  const today = d.key === tk;
                  const done = !!(c.week || [])[j];
                  return (
                    <td key={d.key} style={{ ...st.habCell, ...(today ? st.habColToday : {}) }}>
                      <span title={c.name + " · " + d.label + (done ? " · zapísané" : " · bez záznamu")}
                        style={{ padding: "4px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                        {done ? <StateDot state="done" size={22} /> : <StateDot state="muted" />}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Legend — v25 §4.1: 4 stavy (prázdny krúžok = len dnešná ešte-nesplnená bunka, netreba ho v legende) */}
      <div style={{ ...st.habLegend, flexWrap: "wrap" }}>
        <span style={st.habLegItem}><StateDot state="done" size={14} /> Splnené</span>
        <span style={st.habLegItem}><StateDot state="miss" size={14} /> Vynechané</span>
        <span style={st.habLegItem}><StateDot state="skip" size={14} /> Preskočené</span>
        <span style={st.habLegItem}><StateDot state="muted" /> Nebolo naplánované</span>
      </div>
    </div>
  );
}
// D2 — jeden zdieľaný mood-picker pre OBA modaly (denný „Ako sa dnes cítiš?" aj spätný
// „Nálada · <dátum>"). Referenčný dizajn = denný v33h modal (okrúhle 52px ikony, farba témy
// pri výbere, textové pole „prečo"). Parametrizované: selected, onPick, note/onNote/onSave.
function MoodPicker({ selected, onPick, note, onNote, onSave, saveLabel = "Uložiť", saveDisabled = false, noteDesc, notePlaceholder = "napr. dobre som sa vyspal, pekné počasie…" }) {
  return (
    <>
      <div style={{ ...st.moodRow, marginTop: 0 }}>
        {MOODS.map((m) => {
          const on = selected === m.id;
          return (
            <button key={m.id} type="button" onClick={() => onPick(m.id)} className="mood"
              style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer", padding: "4px 2px" }} title={m.label}>
              <span style={{ width: 52, height: 52, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
                background: on ? "var(--brand-primary)" : "var(--neutral-surface-3)",
                border: on ? "1px solid var(--brand-primary)" : "1px solid var(--neutral-border)",
                transition: "background .18s, transform .18s", transform: on ? "translateY(-2px)" : "none" }}>
                <MoodFace k={m.icon} size={26} color={on ? "var(--brand-on-primary)" : "var(--neutral-text-main)"} />
              </span>
              <span style={{ fontSize: 10.5, fontWeight: on ? 700 : 600, color: on ? "var(--brand-primary)" : "var(--neutral-text-muted)", fontFamily: "var(--ff-ui)", textAlign: "center", lineHeight: 1.2 }}>{m.label}</span>
            </button>
          );
        })}
      </div>
      {noteDesc && <div style={{ ...st.taskDesc, marginTop: 12 }}>{noteDesc}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: noteDesc ? 8 : 12 }}>
        <input className="field" style={{ ...st.field, flex: 1, minWidth: 0 }} placeholder={notePlaceholder}
          value={note} maxLength={280} onChange={(e) => onNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSave(); } }} />
        <Button variant="primary" disabled={saveDisabled} onClick={onSave}>{saveLabel}</Button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
//  Prehľad všetkých 60 levelov (12 kategórií) — otvorí sa klikom na level
// ─────────────────────────────────────────────────────────────
// v23 — zamkni skrolovanie pozadia, kým je otvorený modal/overlay (ref-counted kvôli vnoreným overlayom)
export function LevelsModal({ points, onClose }) {
  const curIdx = levelIndex(points);
  const nextMin = LEVELS[curIdx + 1] ? LEVELS[curIdx + 1].min : null;
  const curRef = useRef(null);
  useEffect(() => { if (curRef.current) curRef.current.scrollIntoView({ block: "center" }); }, []);
  const fmt = (n) => n.toLocaleString("sk-SK");
  // v33h — štandardný Modal (ako ostatné detail modaly) namiesto vlastného overlay/hlavičky;
  // aktuálny stav levelu je info-riadok navrchu tela.
  return (
    <Modal title="Všetkých 60 levelov" icon="trophy" onClose={onClose} maxWidth={480}>
      <div>
        <div style={{ ...st.infoNote, marginTop: 0, marginBottom: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <BuzerIcon name={LEVEL_ICON[curIdx + 1] || "star2"} size={16} color="var(--accent)" style={{ flexShrink: 0 }} />
          <span>Si <strong style={{ color: "var(--text)" }}>Lv {curIdx + 1}</strong> · {gname(getLevel(points))} · {fmt(points)} b.{nextMin != null ? <> · ešte <strong style={{ color: "var(--text)" }}>{fmt(nextMin - points)} b.</strong> do ďalšieho</> : <> · MAX! 🎉</>}</span>
        </div>
          {/* Level 0 — „Deň nula" (pred prvým levelom; získa sa splnením prvého návyku) */}
          <div style={{ marginBottom: 16 }}>
            <div ref={curIdx < 0 ? curRef : null} style={{ ...st.lvRow, ...(curIdx < 0 ? st.lvRowCur : {}) }}>
              <span style={{ ...st.lvRowNum, ...(curIdx < 0 ? st.lvRowNumCur : {}) }}>0</span>
              <span style={{ width: 26, display: "inline-flex", justifyContent: "center", flexShrink: 0 }}><BuzerIcon name={LEVEL_ICON[0] || "egg"} size={20} color="var(--text)" /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={st.lvRowName}>{gname(DEN_NULA)}</span>
                <span style={st.lvRowXp}>od 0 b.</span>
              </span>
              <span style={st.lvRowStatus}>{curIdx < 0 ? "TU" : <Icon name="check" size={16} color="var(--success)" stroke={3} />}</span>
            </div>
          </div>
          {LEVEL_CATS.map((cat, ci) => {
            const from = ci * 5;
            return (
              <div key={ci} style={{ marginBottom: 16 }}>
                <div style={st.lvCatHead}>
                  <span style={{ display: "inline-flex" }}><BuzerIcon name={TIER_ICON[gname(cat)] || "star2"} size={18} color="var(--text)" /></span>
                  <span style={st.lvCatName}>{gname(cat)}</span>
                  <span style={st.lvCatRange}>Lv {from + 1}–{from + 5}</span>
                </div>
                {LEVELS.slice(from, from + 5).map((lv, k) => {
                  const idx = from + k;
                  const done = idx < curIdx, cur = idx === curIdx, locked = idx > curIdx;
                  return (
                    <div key={idx} ref={cur ? curRef : null}
                      style={{ ...st.lvRow, ...(cur ? st.lvRowCur : {}), ...(locked ? st.lvRowLocked : {}) }}>
                      <span style={{ ...st.lvRowNum, ...(cur ? st.lvRowNumCur : {}) }}>{idx + 1}</span>
                      <span style={{ width: 26, display: "inline-flex", justifyContent: "center", flexShrink: 0 }}><BuzerIcon name={LEVEL_ICON[idx + 1] || "star2"} size={20} color="var(--text)" /></span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={st.lvRowName}>{gname(lv)}</span>
                        <span style={st.lvRowXp}>od {fmt(lv.min)} b.</span>
                      </span>
                      <span style={st.lvRowStatus}>{done ? <Icon name="check" size={16} color="var(--success)" stroke={3} /> : cur ? "TU" : <BuzerIcon name="lock" size={15} color="var(--muted)" />}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div style={{ ...st.taskDesc, textAlign: "center", padding: "4px 0 8px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%" }}>
            Každý level chce trochu viac — prvé idú rýchlo, neskôr je to poriadna makačka. <BuzerIcon name="dumbbell" size={14} color="var(--muted)" />
          </div>
      </div>
    </Modal>
  );
}
// v8 — pick a BuzerIcon, grouped by category (used for routines + habits)
// onAiSuggest: optional async () => iconName — when given, shows an "AI navrhne ikonu" button that
// picks an icon from whatever data is already in the form. canAi=false disables it (e.g. no name yet).
// v8 — manage habit routines/categories (create / rename / icon / reorder / delete)
export function RoutineManager({ categories, reload, onClose, habits }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("sunrise");
  const [pickFor, setPickFor] = useState(null);   // "new" | category id
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeIds, setIncludeIds] = useState([]);   // v18 §5 — existing standalone habits to move into the new routine
  const standalone = (habits || []).filter((h) => !h.challenge_id && h.category_id == null);
  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const cr = await api("/api/categories", { method: "POST", body: { name: name.trim(), icon } });
      const newId = cr && cr.id;
      if (newId && includeIds.length) {  // move chosen existing habits into the new routine
        for (const hid of includeIds) {
          try { await api("/api/tasks/" + hid, { method: "PATCH", body: { category_id: newId } }); } catch (e) { /* */ }
        }
      }
      setName(""); setIcon("sunrise"); setIncludeIds([]); reload();
    } finally { setBusy(false); }
  }
  async function rename(id) {
    if (editName.trim()) await api("/api/categories/" + id, { method: "PATCH", body: { name: editName.trim() } });
    setEditId(null); reload();
  }
  async function setCatIcon(id, nm) { await api("/api/categories/" + id, { method: "PATCH", body: { icon: nm } }); setPickFor(null); reload(); }
  async function del(id) { if (!confirm("Zmazať skupinu? Návyky ostanú — stanú sa samostatnými.")) return; await api("/api/categories/" + id, { method: "DELETE" }); reload(); }
  async function move(i, dir) {
    const arr = [...categories]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    await api("/api/categories/reorder", { method: "POST", body: { order: arr.map((c) => c.id) } });
    reload();
  }
  return (
    <Modal title="Skupiny" icon="folder" onClose={onClose}>
      <div style={st.taskList}>
        {categories.length === 0 && <div style={st.taskDesc}>Zatiaľ žiadne skupiny. Vytvor si prvú nižšie.</div>}
        {categories.map((c, i) => (
          <div key={c.id} className="task" style={st.task}>
            <button type="button" onClick={() => setPickFor(c.id)} style={st.routineIcon} title="Zmeniť ikonu">
              <BuzerIcon name={c.icon || "target"} size={20} />
            </button>
            {editId === c.id ? (
              <input className="field" style={{ ...st.field, flex: 1 }} value={editName} autoFocus
                onChange={(e) => setEditName(e.target.value)} onBlur={() => rename(c.id)}
                onKeyDown={(e) => e.key === "Enter" && rename(c.id)} />
            ) : (
              <button type="button" onClick={() => { setEditId(c.id); setEditName(c.name); }} style={{ ...st.taskText, textAlign: "left", background: "transparent", border: "none", cursor: "pointer" }}>
                <span style={st.taskLabel}>{c.name}</span>
                <span style={st.taskDesc}>{c.done_today}/{c.due_today} dnes</span>
              </button>
            )}
            <button onClick={() => move(i, -1)} disabled={i === 0} style={{ ...st.iconBtn, ...(i === 0 ? { opacity: 0.3 } : {}) }} title="Hore"><Icon name="chevron" size={14} color="currentColor" style={{ transform: "rotate(-90deg)" }} /></button>
            <button onClick={() => move(i, 1)} disabled={i === categories.length - 1} style={{ ...st.iconBtn, ...(i === categories.length - 1 ? { opacity: 0.3 } : {}) }} title="Dole"><Icon name="chevron" size={14} color="currentColor" style={{ transform: "rotate(90deg)" }} /></button>
            <button onClick={() => del(c.id)} style={st.del} title="Zmazať"><Icon name="delete" size={16} color="currentColor" /></button>
          </div>
        ))}
      </div>
      <div style={{ ...st.card, marginTop: 14, ...st.formCol }}>
        <div style={st.fieldHint}>Nová skupina</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => setPickFor("new")} style={st.routineIcon} title="Vyber ikonu">
            <BuzerIcon name={icon} size={20} />
          </button>
          <input className="field" style={{ ...st.field, flex: 1 }} placeholder="napr. Ranná rutina"
            value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} />
          <button type="button" onClick={create} disabled={busy} style={{ ...st.primaryBtn, ...(!name.trim() ? { opacity: 0.55 } : {}) }}>Pridať</button>
        </div>
        {/* v18 §5 — optionally move existing standalone habits into the new routine */}
        {standalone.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={st.fieldHint}>Pridať existujúce návyky:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {standalone.map((h) => {
                const on = includeIds.includes(h.id);
                return (
                  <button type="button" key={h.id} className="chip"
                    onClick={() => setIncludeIds((p) => on ? p.filter((x) => x !== h.id) : [...p, h.id])}
                    style={{ ...st.chip, flex: "none", ...(on ? st.chipOn : {}) }}>{on ? <Chk /> : null}{h.label}</button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {pickFor && (
        <Modal title="Vyber ikonu" icon="palette" onClose={() => setPickFor(null)} maxWidth={420}>
          <IconPicker value={pickFor === "new" ? icon : null}
            onPick={(nm) => { if (pickFor === "new") { setIcon(nm); setPickFor(null); } else setCatIcon(pickFor, nm); }} />
        </Modal>
      )}
    </Modal>
  );
}
// ─────────────────────────────────────────────────────────────
//  TAB: Buzerlístok (návyky + body)
// ─────────────────────────────────────────────────────────────
// One consistent dismissible hint banner (install / Lite / notifications all share this shell so
// the icon chip, action button and × close are identical by design).
// v15 §4 — Archív: ended limited-run návyky/výzvy + vyhodnotené súťaže, with results (in Štatistiky)
export function ArchiveSection({ reload, showTitle = true }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({ habits: true, challenges: false, competitions: false });
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("/api/archive").then(setData).catch(() => setData({ habits: [], challenges: [], competitions: [] })), []);
  useEffect(() => { load(); }, [load]);
  async function restore(id) {  // v21 — obnoviť návyk z archívu späť medzi aktívne
    setBusy(true);
    try { await api("/api/tasks/" + id + "/restore", { method: "POST" }); setDetail(null); load(); reload && reload(); }
    catch (e) { /* */ }
    setBusy(false);
  }
  if (!data) return null;
  const total = (data.habits || []).length + (data.challenges || []).length + (data.competitions || []).length;
  const habitLine = (it) => `${it.completions}/${it.expected} dní · ${it.success_pct} % · +${it.points_earned} b.`;
  // v2 — stopková súťaž: výsledok je v sekundách → formátuj ako čas bez jednotky
  const compResult = (c) => (c.result_timer ? fmtDur(c.result) : `${c.result} ${c.metric}`);
  const compLine = (c) => (c.is_winner ? "🏆 vyhral/a" : (c.rank ? `${c.rank}. miesto` : "—")) + (c.result != null ? ` · ${compResult(c)}` : "");
  function chevron(isOpen) { return <Icon name="chevron" size={12} color="var(--muted)" style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }} />; }
  function group(key, title, items, lineFn, fallbackIcon) {
    if (!items || !items.length) return null;
    const isOpen = open[key];
    return (
      <div style={{ marginTop: 10 }}>
        <SectionHeader label={title} count={items.length} trailing={chevron(isOpen)}
          onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))} style={{ margin: "0 2px 6px" }} />
        {isOpen && <div style={st.taskList}>
          {items.map((it, i) => (
            <button key={(it.id || it.name) + "-" + i} type="button" onClick={() => setDetail({ ...it, _kind: key })}
              className="task" style={{ ...st.task, width: "100%", textAlign: "left", cursor: "pointer" }}>
              <span style={st.taskIcon}><Glyph icon={it.icon} fallback={fallbackIcon} /></span>
              <span style={st.taskText}>
                <span style={st.taskLabel}>{it.label || it.name}</span>
                <span style={st.taskDesc}>{lineFn(it)}</span>
              </span>
              <span style={st.taskDesc}>›</span>
            </button>
          ))}
        </div>}
      </div>
    );
  }
  return (
    <div data-scroll="archiv" style={{ transform: "translateZ(0)" }}>{/* v26.2 §2 GPU layer (Mali) + §1 scroll anchor */}
      {/* v32 — prázdny archív bez nadpisu; v PRO Prehľade nadpis dáva sekciový prepínač („ARCHÍV")
          → showTitle=false, nech nie je dvojitý nadpis */}
      {showTitle && total > 0 && <SectionHeader label="Archív návykov" />}
      {total === 0 ? (
        /* v33c — rovnaký prázdny stav ako v Aktivite (ikona v krúžku + nadpis + popis) */
        <div style={{ textAlign: "center", padding: "26px 16px 20px", color: "var(--neutral-text-muted)" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 46, height: 46, borderRadius: "50%", background: "var(--brand-primary-surface)", color: "var(--brand-primary)" }}>
              <BuzerIcon name="folder" size={22} color="currentColor" />
            </span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 5 }}>Zatiaľ nič ukončené</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 280, margin: "0 auto" }}>Návyky a výzvy s trvaním sa sem presunú po dobehnutí.</div>
        </div>
      ) : (<>
        {group("habits", "Ukončené návyky", data.habits, habitLine, "target")}
        {group("challenges", "Ukončené výzvy", data.challenges, (it) => habitLine(it) + (it.participants ? ` · ${it.participants} ľudí` : ""), "flag")}
        {group("competitions", "Vyhodnotené výzvy", data.competitions, compLine, "trophy")}
      </>)}
      {detail && (
        <Modal center title={detail.label || detail.name} icon={detail._kind === "competitions" ? "trophy" : (detail._kind === "challenges" ? "flag" : "target")} onClose={() => setDetail(null)} maxWidth={400}>
          {detail._kind === "competitions" ? (
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              {detail.descr ? <div style={{ ...st.taskDesc, marginBottom: 8 }}>{detail.descr}</div> : null}
              <div><strong>{detail.is_winner ? "🏆 Vyhral/a si!" : (detail.rank ? `${detail.rank}. miesto z ${detail.total}` : "Bez zápisu — nezúčastnil/a si sa")}</strong></div>
              {detail.result != null && <div>Tvoj výsledok: <strong>{detail.result_timer ? fmtDur(detail.result) : detail.result + " " + detail.metric}</strong></div>}
              {detail.winner && !detail.is_winner && <div>Víťaz: <strong>{detail.winner}</strong></div>}
              {detail.points ? <div style={st.taskDesc}>Odmena pre víťaza: +{detail.points} b.</div> : null}
              {detail.leaderboard && detail.leaderboard.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <SectionHeader label="Rebríček" style={{ margin: "0 0 6px" }}
                    count={`${detail.total} ${detail.total === 1 ? "účastník" : (detail.total <= 4 ? "účastníci" : "účastníkov")}`} />
                  <div style={st.taskList}>
                    {detail.leaderboard.map((m, i) => (
                      <div key={i} className="task" style={{ ...st.task, ...st.remCard, padding: "7px 10px" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 7, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 12, color: "#fff", background: ["#C9A227", "#9AA0A6", "#B07A4B"][i] || "var(--muted)" }}>{i + 1}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600 }}>{m.name}</span>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text2)" }}>{detail.result_timer ? fmtDur(m.result) : m.result + " " + detail.metric}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.eval_date && <div style={{ ...st.taskDesc, marginTop: 8 }}>Vyhodnotené: {detail.eval_date}</div>}
            </div>
          ) : (
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <div>Splnené: <strong>{detail.completions}/{detail.expected} dní</strong> ({detail.success_pct} %)</div>
              <div>Body za behu: <strong>+{detail.points_earned}</strong></div>
              <div>Najdlhšia séria: <strong>{detail.best_streak}</strong></div>
              {detail.participants ? <div>Účastníci: <strong>{detail.participants}</strong></div> : null}
              <div style={{ ...st.taskDesc, marginTop: 8 }}>Trvanie {detail.duration_days ? `${detail.duration_days} dní` : "bez limitu"} · {detail.started} → {detail.ended}</div>
            </div>
          )}
          {/* v21 — obnoviť návyk z archívu (len návyky) */}
          {detail._kind === "habits" && (
            <button type="button" onClick={() => restore(detail.id)} disabled={busy}
              style={{ ...st.primaryBtn, width: "100%", marginTop: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <Icon name="refresh" size={16} color="currentColor" /> {busy ? "…" : "Obnoviť návyk"}</button>
          )}
        </Modal>
      )}
    </div>
  );
}
// v32.23 — extracted so it can register with useModalBackClose (Back closes it) like every
// other modal/sheet; was inline JSX before (a hook can't run inside a conditional block).
function CapUpsellSheet({ kind, onClose }) {
  useModalBackClose(onClose);
  return (
    <div style={st.sheetWrap} onClick={onClose}>
      <div style={st.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={st.sheetHead}>
          <span style={st.sheetTitle}><BuzerIcon name="shield" size={17} color="var(--text)" style={{ verticalAlign: "-3px", marginRight: 6 }} />Funkcia Community</span>
          <button onClick={onClose} style={st.sheetClose} aria-label="Zavrieť">✕</button>
        </div>
        <div style={{ ...st.taskDesc, fontSize: 14, marginBottom: 12 }}>
          {kind === "habit"
            ? "Vo free verzii máš maximálne 5 návykov. S Community ich máš neobmedzene — plus spoločné výzvy, priateľov, chat a ďalšie. 💛"
            : "Táto funkcia je súčasťou Community — spoločné výzvy, priatelia, chat a viac. 💛"}
        </div>
        <a href="/o-aplikacii#cennik" style={{ ...st.primaryBtn, textDecoration: "none", display: "block", textAlign: "center" }}>Zobraziť Community ›</a>
      </div>
    </div>
  );
}
// v23 — Prehľad štatistická karta s malým „i" (vpravo hore) → po kliknutí tooltip s vysvetlením
export function BuzerTab({ s, reload, quote, createWhat, createKey, homeView, setHomeView, homeReset, onOpenCommand, onGoFriends, onNotifNavigate, deepLinkCompId, onDeepLinkCompHandled, navGuard }) {
  const narrow = useIsNarrow();
  const [celebrate, setCelebrate] = useState(false);
  const prevAllDone = useRef(false);
  // v33 — level-up: `levelUp` je číslo dosiahnutého levelu (1–60), zobrazené v samostatnom
  // farebnom detail-modáli (LevelUpModal), NIE hneď — o pár sekúnd, nech neprekryje čerstvú
  // radosť z práve rozprsknutého návyku (burstFx/CompletionFx). `prevLevelIdx` je ref, takže
  // efekt beží pri KAŽDOM renderi, ale spustí sa len keď sa level naozaj zvýšil oproti
  // predošlému behu — vrátane prvého po mounte (ref sa inicializuje na aktuálny level, takže
  // pri mounte cur === prevLevelIdx.current a nič sa nespustí).
  const [levelUp, setLevelUp] = useState(null);
  const prevLevelIdx = useRef(levelIndex(s.totalPoints));
  const levelUpTimer = useRef(null);
  // ITEM 11 — keď sa perfektný deň a nový level dosiahnu naraz: oslava perfektného dňa má ísť
  // PRVÁ; level-up modal sa NESMIE otvoriť ponad ňu. Kým je perfektná oslava otvorená
  // (perfDayCelebRef = perfectOffer alebo jeho ShareStatSheet), odložíme level idx do
  // pendingLevelUp a vypálime ho až po zatvorení perfektnej oslavy (flushPendingLevelUp).
  const pendingLevelUp = useRef(null);
  const perfDayCelebRef = useRef(false);
  const flushPendingLevelUp = () => {
    if (pendingLevelUp.current != null) { setLevelUp(pendingLevelUp.current); pendingLevelUp.current = null; }
  };
  useEffect(() => {
    const cur = levelIndex(s.totalPoints);
    if (cur > prevLevelIdx.current && !s.lite_mode) {
      const idx = cur + 1;  // Lv 1..60 (levelIndex je 0-based)
      const lvl = getLevel(s.totalPoints);
      // zapíš do zvončeka HNEĎ (nech tam je aj keby appku medzičasom zavreli) — samotná
      // oslava (modal) sa ukáže až po odstupe nižšie.
      api("/api/notifications/levelup", { method: "POST",
        body: { level: idx, body: `Nový level: ${gname(lvl)} — úroveň ${idx}!` } })
        .then(() => reload()).catch(() => {});
      clearTimeout(levelUpTimer.current);
      // ITEM 11 — ak medzitým beží perfektná oslava, level idx odlož; inak zobraz level-up hneď.
      levelUpTimer.current = setTimeout(() => {
        if (perfDayCelebRef.current) pendingLevelUp.current = idx;
        else setLevelUp(idx);
      }, 2800);
    }
    prevLevelIdx.current = cur;
  }, [s.totalPoints]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearTimeout(levelUpTimer.current), []);
  const [coach, setCoach] = useState(null);
  const [coachBusy, setCoachBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [openChallenge, setOpenChallenge] = useState(null);
  // ITEM 3 — po zrušení splnenia z rodičovského uncheck-sheetu (toggle) treba znovu načítať
  // otvorenú ChallengeBoard (jej interné `data` sa inak neobnoví → tlačidlo ostane „Splnené dnes")
  const [chalRefreshKey, setChalRefreshKey] = useState(0);
  const [openComp, setOpenComp] = useState(null);  // H6: competition detail modal
  const [inviteDetail, setInviteDetail] = useState(null);  // #2e — challenge-invite preview before joining
  const [joinBusy, setJoinBusy] = useState(false);
  const [declineAsk, setDeclineAsk] = useState(null);  // #2a — confirm before declining an invite (the × on the banner)
  const [launchClosed, setLaunchClosed] = useState(false);  // in-app launch modal novej globálnej dávky (raz)
  const [addCat, setAddCat] = useState(null);      // v2 — routine to pre-select when adding a habit via its ＋Pridať návyk
  const [viewMode, setViewMode] = useState("list");  // 'list' | 'table'
  // v31.1 — bubliny dočasne skryté; stav ostáva zakomentovaný (zapneš s prepínačom)
  // const [statView, setStatView] = useState("cards");
  // v31 — Prehľad sekcie (PRO): guličkový prepínač medzi štatistikami / heat mapou / odznakmi /
  // archívom. v33i (task #4) — Lite dostáva TEN ISTÝ prepínač, len bez „Odznaky" (Lite body
  // ani levely nesleduje, takže odznaky nedávajú zmysel).
  const [prehSec, setPrehSec] = useState("stats");
  const prehSwipe = useRef(null);  // v32.14 — začiatok dotyku pre swipe medzi sekciami Prehľadu
  // v32.8 — sekcia „Týždeň a história" odišla z Prehľadu (týždeň + História žijú v Dnes) — platí
  // teraz rovnako pre Lite, keďže má od task #3 tú istú Zoznam⇄Týždeň lištu na Dnes ako PRO.
  const PREH_SECTIONS = [
    { key: "stats", label: "Štatistiky", icon: "chart" },
    { key: "heat", label: "Aktivita", icon: "pulse" },
    { key: "badges", label: "Odznaky", icon: "trophy" },
    { key: "archive", label: "Archív", icon: "folder" },
  ];
  const LITE_PREH_SECTIONS = PREH_SECTIONS.filter((sec) => sec.key !== "badges");  // task #4 — bez Odznakov
  // sekcia je viditeľná, keď je aktívna v prepínači (obidva režimy majú teraz vlastný prepínač)
  const inSec = (name) => prehSec === name;
  const [habitFilter, setHabitFilter] = useState("all");  // "all" | "solo" | "community"
  const [prehTableEdit, setPrehTableEdit] = useState(false);  // v32 — Týždeň a história: tabuľka editovateľná až po kliknutí na „Upraviť"
  const [openForm, setOpenForm] = useState(null);       // which create form is expanded (only one at a time)
  const [showLevels, setShowLevels] = useState(false);  // level overview overlay
  const [showLeague, setShowLeague] = useState(false);  // v32.21 — rebríček ako modal v Prehľade
  const { open: openProfileModal } = useProfileModal();  // globálny modal verejného profilu
  const [badgeInfo, setBadgeInfo] = useState(null);     // v20 §19 — odznaky presunuté do Prehľad
  const [league, setLeague] = useState(null);           // v30 — rebríček týždňa (dlaždica v Prehľade)
  const [shareMode, setShareMode] = useState(false);    // v32 — režim výberu štatistík na zdieľanie
  const [shareSel, setShareSel] = useState([]);          // vybrané kľúče dlaždíc
  const [shareOpen, setShareOpen] = useState(false);     // otvorený ShareStatSheet
  const [perfectOffer, setPerfectOffer] = useState(false);      // v32.22 — ponuka zdieľať perfektný deň
  const [perfectShareOpen, setPerfectShareOpen] = useState(false); // v32.22 — ShareStatSheet perfektného dňa
  // ITEM 11 — perfektná oslava „prebieha", kým je otvorený jej offer modal alebo jeho ShareStatSheet;
  // dovtedy sa prípadný level-up odkladá (pozri levelUpTimer vyššie).
  useEffect(() => { perfDayCelebRef.current = perfectOffer || perfectShareOpen; }, [perfectOffer, perfectShareOpen]);
  // v32.17 — jedno miesto na vypnutie režimu zdieľania (X modalu, prepnutie sekcie…)
  const exitShare = () => { setShareMode(false); setShareSel([]); };
  // prepnutie sekcie (klik na pilulku aj swipe) vždy zruší režim zdieľania
  const changeSec = (name) => { setPrehSec(name); exitShare(); };
  const [deleteId, setDeleteId] = useState(null);       // v20 §29 — mazať: archív / natrvalo
  const [editMode, setEditMode] = useState(false);      // v2 redesign — pencil toggles edit/rearrange (reveals add buttons + reorder + move controls)
  const [tableEdits, setTableEdits] = useState([]);     // v2 — Týždeň edit session: toggles made, for "Zrušiť zmeny"
  const [detailTask, setDetailTask] = useState(null);   // v2 redesign — tap a habit → TaskDetail sheet
  const [menuTask, setMenuTask] = useState(null);       // v2 — ⋯ action menu (from TaskDetail)
  const [uncheckTask, setUncheckTask] = useState(null); // v2 — confirm before undoing a completed habit
  // ITEM 6 — id návykov zrušených v tejto relácii. Slúži LEN na perfektný deň: N×/týždeň (period)
  // návyk splnený „navyše" (cieľ už dosiahnutý) po zrušení vypadne z dueTasks (due_today=false),
  // takže allDone by ostalo true a titulok „Perfektný deň". Kým je taký (weekMet) návyk v tejto
  // množine, perfektný deň sa zruší. Pri opätovnom splnení sa id odstráni. Denné návyky sú tu
  // neškodné (perfektný deň už rušia cez dueTasks); pred-splnené týždenné (nikdy dnes) tu nie sú.
  const [uncheckedIds, setUncheckedIds] = useState(() => new Set());
  const [unskipTask, setUnskipTask] = useState(null);   // v25 §10.8 — confirm before cancelling a skip
  const [flashId, setFlashId] = useState(null);         // v2 — scroll-to + flash a just-created habit
  useEffect(() => {
    if (flashId == null) return;
    const t = setTimeout(() => { const el = document.querySelector(`[data-taskid="${flashId}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 120);
    const clr = setTimeout(() => setFlashId(null), 1900);
    return () => { clearTimeout(t); clearTimeout(clr); };
  }, [flashId]);
  const [detailRoutine, setDetailRoutine] = useState(null);  // v2 — routine (skupina) detail sheet
  const [editRoutine, setEditRoutine] = useState(null);      // v2 — routine edit modal
  const [routineStartDel, setRoutineStartDel] = useState(false);  // v2 — open edit straight to the delete-choice
  // v23 — persist the comeback dismissal per lapse instance (keyed by lapsedSince) so once closed
  // it stays hidden after closing/reopening the app, but reappears if a *future* streak breaks.
  const [comebackDismissed, setComebackDismissed] = useState(() => { try { return localStorage.getItem("buzer_comeback_off") || ""; } catch { return ""; } });
  const [capUpsell, setCapUpsell] = useState(null);       // 'habit' | 'pro' — upsell when a gated create is attempted
  const [moodReflect, setMoodReflect] = useState("");     // ✨ Buzer reaction to today's mood
  const [moodReflectBusy, setMoodReflectBusy] = useState(false);
  const [why, setWhy] = useState(() => (s.moodNotes && s.moodNotes[todayKey()]) || "");  // today's "prečo?" note
  const [moodDetail, setMoodDetail] = useState(null);   // day clicked in the 7-day mood graph
  const [moodEdit, setMoodEdit] = useState(null);       // H1: backfill/edit a past day's mood (day key)
  const [moodModal, setMoodModal] = useState(false);    // v31 — rýchla nálada zo stripu (smajlík vedľa oka)
  const [moodEditNote, setMoodEditNote] = useState(""); // note draft inside the backfill modal
  const [dayRing, setDayRing] = useState(null);         // deň-kruh — snapshot pre oslavný modal pri splnení návyku (nahrádza burst)
  const [burstFx, setBurstFx] = useState(null);         // v32.21 — {key,x,y,icon,iconColor} — efekt „narastie + rozpŕskne sa" po splnení
  const [heldDone, setHeldDone] = useState(() => new Set());  // v32.21 — splnené úlohy dočasne DRŽANÉ na mieste (presun do SPLNENÉ až po pár sekundách)
  const clearBurst = useCallback(() => setBurstFx(null), []);  // stabilné onDone pre CompletionFx (inak by re-render resetoval časovač)
  // v18 §11 — collapse state (routine ids + named Dnes sections) persisted per user, survives refresh
  const _collapseKey = "buzer_dnes_collapsed_" + ((s.user && s.user.id) || 0);
  const _collapseInit = (() => { try { return JSON.parse(localStorage.getItem(_collapseKey) || "{}") || {}; } catch { return {}; } })();
  const [collapsed, setCollapsed] = useState(_collapseInit.routines || {});   // v8: collapsed routine ids
  const [collapsedSec, setCollapsedSec] = useState(_collapseInit.sections || {});  // named Dnes sections
  useEffect(() => { try { localStorage.setItem(_collapseKey, JSON.stringify({ routines: collapsed, sections: collapsedSec })); } catch { /* */ } }, [collapsed, collapsedSec, _collapseKey]);
  // v2 — Výzvy/súťaže custom order (synced to backend), reorderable in edit mode
  const chalOrder = (() => { try { return JSON.parse(s.chal_order || "[]"); } catch { return []; } })();
  const toggleSec = (k) => setCollapsedSec((p) => ({ ...p, [k]: !p[k] }));
  const SecChevron = ({ k }) => <span style={{ ...st.routineChevron, transform: collapsedSec[k] ? "rotate(-90deg)" : "rotate(90deg)", display: "inline-flex" }}><Icon name="chevron" size={18} color="var(--muted)" /></span>;
  const [manageRoutines, setManageRoutines] = useState(false);  // v8: routine-management modal
  const [routineCreate, setRoutineCreate] = useState(false);    // v2: focused "Nová skupina" modal (like Upraviť skupinu)
  const [statModal, setStatModal] = useState(null);     // H8/H9/H10: "streak" | "points" | "mood"
  const [whyModal, setWhyModal] = useState(false);      // H2: "Prečo sa dnes tak cítiš?" modal
  const [whySaved, setWhySaved] = useState(false);      // E3: brief ✓ before closing
  // v17 §1 — home is one axis: Zoznam (list) ⇄ Týždeň (table). Table view absorbs the old Štatistiky overview.
  // Lite has no Týždeň overview (no points/streaks) → always the list.
  const isTable = viewMode === "table";  // v22.7 — Lite users get a Prehľad too (table + nálady + archív, no gamifikácia)
  // v30 — rebríček týždňa pre dlaždicu v Prehľade (načíta sa raz pri otvorení prehľadu)
  useEffect(() => {
    if (!isTable || s.lite_mode || league != null) return;
    let live = true;
    api("/api/league").then((d) => { if (live) setLeague(d); }).catch(() => { if (live) setLeague({ entries: [] }); });
    return () => { live = false; };
  }, [isTable]);  // eslint-disable-line react-hooks/exhaustive-deps
  const isList = !isTable;
  const [liteCardHid, setLiteCardHid] = useState(() => localStorage.getItem("buzer_lite_card_hid") === "1");
  const [showInstall, setShowInstall] = useState(false);  // install-to-home-screen guide
  // gentle one-time "add to home screen" hint for existing users (no onboarding for them)
  const [installHid, setInstallHid] = useState(() => {
    try { return isStandalone() || localStorage.getItem("installHintHid") === "1"; }
    catch (e) { return isStandalone(); }
  });
  function hideInstallHint() {
    setInstallHid(true);
    try { localStorage.setItem("installHintHid", "1"); } catch (e) { /* */ }
  }
  // gentle "allow notifications" hint (only while the browser hasn't decided yet)
  const notifCanAsk = typeof Notification !== "undefined" && "serviceWorker" in navigator
    && "PushManager" in window && Notification.permission === "default";
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifHid, setNotifHid] = useState(() => {
    try { return localStorage.getItem("notifHintHid") === "1"; } catch (e) { return false; }
  });
  function hideNotifHint() {
    setNotifHid(true);
    try { localStorage.setItem("notifHintHid", "1"); } catch (e) { /* */ }
  }
  // v26.3 §4 — tip dňa: raz denne, dismiss per zariadenie (bz-tip-day = dnešný deň)
  const [tipHid, setTipHid] = useState(() => { try { return localStorage.getItem("bz-tip-day") === todayKey(); } catch (e) { return false; } });
  function hideTip() { setTipHid(true); try { localStorage.setItem("bz-tip-day", todayKey()); } catch (e) { /* */ } }
  // Jednorazový objaviteľský hint pre long-press (nahradil ceruzku v Zozname).
  const [lpHintHid, setLpHintHid] = useState(() => { try { return localStorage.getItem("buzer_lphint_seen") === "1"; } catch (e) { return true; } });
  function hideLpHint() { setLpHintHid(true); try { localStorage.setItem("buzer_lphint_seen", "1"); } catch (e) { /* */ } }
  // v27 §2 — starostlivý check-in od AI ako karta na Dnes (kým je v zvončeku, max 3 dni);
  // ✕ skrýva len kartu (per user, per notifikácia) — položka v zvončeku ostáva
  // v32.22 — „oko": splnené návyky sa NEPRESÚVAJÚ do sekcie (ostávajú na svojom mieste v zozname),
  // len sa DEFAULTNE skryjú. Oko ich zobrazí/skryje. Prvé splnenie ukáže jednorazový info-toast.
  const _hideDoneKey = "buzer_dnes_hidedone_" + ((s.user && s.user.id) || 0);
  const [hideDone, setHideDone] = useState(() => { try { const v = localStorage.getItem(_hideDoneKey); return v == null ? true : v === "1"; } catch (e) { return true; } });
  useEffect(() => { try { localStorage.setItem(_hideDoneKey, hideDone ? "1" : "0"); } catch (e) { /* */ } }, [hideDone, _hideDoneKey]);
  const _hideHintKey = "buzer_dnes_hidedone_hint_" + ((s.user && s.user.id) || 0);
  const hideHintSeen = useRef((() => { try { return localStorage.getItem(_hideHintKey) === "1"; } catch (e) { return true; } })());
  const _careKey = "buzer_care_hid_" + ((s.user && s.user.id) || 0);
  const [careHid, setCareHid] = useState(() => { try { return localStorage.getItem(_careKey) || ""; } catch (e) { return ""; } });
  const careNotif = (s.notifications || []).find((n) => n.kind === "care_checkin" && String(n.id) !== careHid);
  function hideCare() { if (!careNotif) return; setCareHid(String(careNotif.id)); try { localStorage.setItem(_careKey, String(careNotif.id)); } catch (e) { /* */ } }
  async function allowNotifs() {
    setNotifBusy(true);
    try { await enablePush(); } catch (e) { /* enablePush surfaces its own alerts */ }
    setNotifBusy(false);
    hideNotifHint();  // the browser has now decided — don't nag again
  }
  useEffect(() => { window.scrollTo(0, 0); }, [viewMode]);  // A4: reset scroll on Zoznam/Týždeň switch
  // v23 — "Prehľad" je samostatná položka v menu: keď naň klikneš (homeView="stats"), prepni na tabuľku
  useEffect(() => { setViewMode(homeView === "stats" ? "table" : "list"); }, [homeView]);
  const [toast, setToast] = useState("");               // transient confirmation toast
  const dateStr = new Date().toLocaleDateString("sk-SK", { weekday: "long", day: "numeric", month: "long" });
  // na Dnes prepneš zoznam návykov na 7-dňovú tabuľku (rovnaký HabitTable komponent ako v Prehľade);
  // nemení to celú stránku na Prehľad — ostáva hero „Dnes." aj nálada atď.
  const [habitTable, setHabitTable] = useState(false);  // v2 — Zoznam ⇄ Týždeň toggle lives in the header Segmented
  // v26.3 §9 — klik na „Buzerlístok" v hlavičke zhodí Týždeň späť na Zoznam (aj keď si už na Dnes)
  const firstHomeReset = useRef(true);
  useEffect(() => {
    if (firstHomeReset.current) { firstHomeReset.current = false; return; }
    // v34 — aj klik na logo zhadzuje Týždeň → Zoznam; pri neuložených úpravách sa najprv spýtaj
    if (guardTableLeave(() => { setHabitTable(false); setEditMode(false); })) return;
    setHabitTable(false); setEditMode(false);  // vráť Zoznam a vypni prípadný edit režim
  }, [homeReset]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [showHistory, setShowHistory] = useState(false);  // v25 §5 — mesačná matica splnení
  const loadCoach = useCallback(async (force) => {
    setCoachBusy(true);
    try { setCoach((await api("/api/ai/coach" + (force ? "?refresh=1" : ""))).message); } catch (e) { /* ticho */ }
    setCoachBusy(false);
  }, []);
  // v25 §3.3 — after every habit-completion change the coach line refreshes (debounced ~2.5 s,
  // so rapid ticking of several habits fires ONE request); Lite never calls the coach.
  const coachTimer = useRef(null);
  const scheduleCoachRefresh = useCallback(() => {
    if (!session.ai) return;  // v26.3 §10 — AI veta sa obnovuje aj v Lite (keď má user AI)
    if (coachTimer.current) clearTimeout(coachTimer.current);
    coachTimer.current = setTimeout(() => { coachTimer.current = null; loadCoach(true); }, 2500);
  }, [loadCoach]);
  useEffect(() => () => { if (coachTimer.current) clearTimeout(coachTimer.current); }, []);
  async function getMoodReflect() {
    setMoodReflectBusy(true);
    try { const r = await api("/api/ai/mood-reflect", { method: "POST", body: { mood: s.mood, note: why.trim() } }); setMoodReflect(r.message || ""); }
    catch (e) { /* ticho */ }
    setMoodReflectBusy(false);
  }
  useEffect(() => { if (session.ai) loadCoach(); }, [loadCoach]);  // v26.3 §10 — načítaj AI vetu aj v Lite
  useEffect(() => {
    if (!createKey) return;
    const cw = createWhat === "competition" ? "challenge" : createWhat;  // v26.2 §6 — jeden typ Výzva
    if (cw === "skupina") { setRoutineCreate(true); return; }  // FAB → nová skupina (create modal)
    if (!["habit", "challenge", "suggest"].includes(cw)) return;
    // v13 D1 — habits are unlimited for free now; only challenge/suggest stay premium
    const proGated = ["challenge", "suggest"].includes(cw) && !session.premium;
    if (proGated) { setCapUpsell("pro"); return; }
    setViewMode("list"); setHabitTable(false);  // v2 — switch off the week-table (Týždeň) so the create form is visible
    setEditMode(false);
    setEditingId(null); setOpenForm(cw);
  }, [createKey]);
  const TASKS = s.habits;
  // challenge habits are daily-trackable → shown under "Dnešný deň", pinned first; their points
  // count toward today. Only the personal ones are manually reorderable.
  const challengeHabits = TASKS.filter((t) => t.challenge_id);
  const personalHabits = TASKS.filter((t) => !t.challenge_id);
  const displayTasks = [...challengeHabits, ...personalHabits];
  const shownTasks = habitFilter === "solo" ? personalHabits : habitFilter === "community" ? challengeHabits : displayTasks;
  // H3 — the Dnes list & today's progress only count habits scheduled for today; off-day habits
  // are hidden (their streak still bridges). The table/history view keeps showing everything.
  const isDue = (t) => t.due_today !== false;
  // v25 §3.1 — N×/týždeň habit with the target already met stays visible in the list
  // (dimmed, re-achievable); it does NOT count into today's done/total numbers.
  const weekMet = (t) => !t.done && !!t.week_target && (t.week_done || 0) >= t.week_target;
  // v25 §4.4 — a habit skipped today stays visible (dimmed „Preskočené“) but is excluded
  // from today's done/total counters (rest day — the streak bridges).
  // v33b — „Máš čas" (zriedkavé) návyky sa NErátajú do dnešného X/Y ani perfektného dňa
  // (nie sú denné očakávanie); ostávajú viditeľné v samostatnej sekcii.
  // F1 — N×/obdobie (weekly_count · period) návyk blokuje perfektný deň LEN keď je „povinný dnes"
  // (runway/grace, počíta backend do t.perfect_required). Kým je čas ho dobehnúť, do menovateľa
  // perfektného dňa sa neráta (ostáva viditeľný a odškrtnuteľný). Fallback pre staršie payloady
  // (bez perfect_required) = pôvodné správanie (isDue). Toto zároveň opravuje C4 (Lite allDone).
  const perfectCounts = (t) => {
    const periodic = t.recur_mode === "weekly_count" || t.recur_mode === "period";
    if (periodic && typeof t.perfect_required === "boolean") return t.done || t.perfect_required;
    return true;
  };
  const countsToday = (t) => isDue(t) && !(t.skip_today && !t.done) && !t.mas_cas && perfectCounts(t);
  const dueTasks = TASKS.filter(countsToday);
  const listShownTasks = shownTasks.filter((t) => isDue(t) || weekMet(t));
  // v29 — aj Lite používatelia vidia súťaže (výzvy so značením výsledku): nesú metriku, nie body,
  // takže sedia do Lite. Bežné bodové výzvy (is_competition=0) ostávajú v Lite skryté.
  const openComps = (s.competitions || []).filter((c) => c.status !== "closed");
  const closedComps = (s.competitions || []).filter((c) => c.status === "closed");
  const shownComps = habitFilter === "solo" ? [] : openComps;  // competitions are "Spoločné"
  // v9 — active shared challenges (výzvy) also get a carousel card; dedupe to one card per challenge
  const shownChallenges = habitFilter === "solo" ? []
    : Object.values((challengeHabits || []).reduce((acc, t) => { if (!acc[t.challenge_id]) acc[t.challenge_id] = t; return acc; }, {}));
  // v26.2 §6 — Týždeň table matches the Zoznam order: everything (návyky, skupiny, výzvy) sorted by
  // the ONE merged order (dnes_order + legacy chal_order appended once). Challenge tasks map to chal:<id>.
  // Ties are NOT broken by sort_in_category — it drifts from `sort` (in-skupina arrows and AI reorder
  // write only `sort`), which made Týždeň diverge from Zoznam. The incoming list is already in the
  // server's (sort, id) order, so a stable sort by block rank alone reproduces Zoznam's row order.
  const orderedForTable = (list) => {
    const dnesOrd = (() => { try { return JSON.parse(s.dnes_order || "[]"); } catch { return []; } })();
    const hasChalKeys = dnesOrd.some((k) => k.startsWith("chal:") || k.startsWith("comp:"));
    const eff = (!hasChalKeys && chalOrder.length) ? [...dnesOrd, ...chalOrder] : dnesOrd;
    const pos = new Map(eff.map((k, i) => [k, i]));
    const catPos = new Map((s.categories || []).map((c, i) => ["cat:" + c.id, i]));
    const rank = (t) => {
      const key = t.challenge_id ? ("chal:" + t.challenge_id) : (t.category_id ? ("cat:" + t.category_id) : ("task:" + t.id));
      if (pos.has(key)) return pos.get(key);
      // blocks missing from dnes_order mirror Zoznam's fallback: skupiny first (in s.categories order),
      // then samostatné návyky, then výzvy
      if (t.challenge_id) return 3e9;
      if (t.category_id) return 1e9 + (catPos.get(key) || 0);
      return 2e9;
    };
    return [...list].sort((a, b) => rank(a) - rank(b));
  };
  const TOTAL_PTS = dueTasks.reduce((a, t) => a + t.pts, 0) || 1;  // habits due today (incl. challenges)
  const doneCount = dueTasks.filter((t) => t.done).length;
  const todayPts = dueTasks.reduce((a, t) => a + (t.done ? t.pts : 0), 0);
  const dayMax = dueTasks.reduce((a, t) => a + t.pts, 0);
  const level = getLevel(s.totalPoints);
  const next = nextLevel(s.totalPoints);
  // ITEM 6 — perfektný deň zruš aj vtedy, keď v tejto relácii zrušíš „navyše" splnený týždenný/period
  // návyk (ktorý inak vypadne z dueTasks). weekMet = teraz nesplnený, ale cieľ dosiahnutý (viditeľný,
  // dimmed). Pred-splnené týždenné (nikdy dnes nesplnené) tu nie sú → perfektný deň neblokujú.
  const pendingRevert = TASKS.some((t) => uncheckedIds.has(t.id) && weekMet(t) && !dueTasks.includes(t));
  const allDone = dueTasks.length > 0 && doneCount === dueTasks.length && !pendingRevert;
  if (allDone && !prevAllDone.current) {
    prevAllDone.current = true;
    // deň-kruh modal má pri all-done vlastné konfety — nespúšťaj druhý burst za scrimom
    if (!(dayRing && dayRing.allDone)) setTimeout(() => { setCelebrate(true); setTimeout(() => setCelebrate(false), 2600); }, 0);
  }
  if (!allDone) prevAllDone.current = false;
  // v32.19 — čerstvý AI citát len raz, keď sa deň skompletizuje (nie po každom splnení)
  useEffect(() => { if (allDone) scheduleCoachRefresh(); }, [allDone]);  // eslint-disable-line react-hooks/exhaustive-deps
  // v32.22 — perfektný deň: po oslavnom burste ponúkni zdieľanie s priateľmi. Spustí sa pri KAŽDOM
  // čerstvom dosiahnutí perfektného dňa (aj keď splnenie vrátiš a znova dosiahneš 100 %), ale NIE
  // pri prvom rendri / návrate na tab (perfOfferPrev sa inicializuje na aktuálny stav), aby to
  // nevyskakovalo pri každom otvorení Dnes s už hotovým dňom.
  const perfOfferInit = useRef(false);
  const perfOfferPrev = useRef(false);
  useEffect(() => {
    if (!perfOfferInit.current) { perfOfferInit.current = true; perfOfferPrev.current = allDone; return undefined; }
    if (!s.lite_mode && allDone && !perfOfferPrev.current) {
      perfOfferPrev.current = true;
      const t = window.setTimeout(() => setPerfectOffer(true), 1000);  // C2 — modal vyskočí 1 s po poslednom návyku (nesie „wow" napĺňaciu animáciu)
      return () => window.clearTimeout(t);
    }
    perfOfferPrev.current = allDone;
    return undefined;
  }, [allDone]);  // eslint-disable-line react-hooks/exhaustive-deps
  // G5 — deep-link z notifikácie „predbehli ťa vo výzve" (vyzva:<cid>): otvor detail tej výzvy.
  // App premapuje link na deepLinkCompId; keď sa zmení na platné id, nájdi výzvu a otvor jej detail.
  // Funguje pre súťaž (openComp) aj bežnú dennú výzvu (openChallenge) — napr. notifikácia
  // „chat výzvy ožil" vedie sem, nech sa dá rovno pridať do skupinového chatu z detailu.
  useEffect(() => {
    if (deepLinkCompId == null) return;
    const c = (s.competitions || []).find((x) => x.id === deepLinkCompId);
    if (c) setOpenComp(c);
    else {
      const h = (challengeHabits || []).find((x) => x.challenge_id === deepLinkCompId);
      if (h) setOpenChallenge({ cid: h.challenge_id, icon: h.icon, label: h.label });
    }
    onDeepLinkCompHandled && onDeepLinkCompHandled();  // reset v App, nech sa dá znova deep-linknúť tá istá výzva
  }, [deepLinkCompId]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function toggle(id, date, srcEl) {
    // v32.21 — namiesto oslavného DEŇ-KRUHU (modal): ikona úlohy „narastie" a rozpŕskne sa na
    // častice v mieste kliknutia, a AŽ POTOM sa doplní prstenec dňa (oneskorenie v effect vyššie).
    const cur = dueTasks.find((x) => x.id === id) || shownTasks.find((x) => x.id === id) || {};
    // ITEM 6 — sleduj dnešné zrušenia (uncheck) kvôli perfektnému dňu; splnenie id zase odstráni.
    // Len dnešný toggle (bez `date`); spätné doplnenie minulého dňa perfektný deň neovplyvňuje.
    if (!date) setUncheckedIds((prev) => { const n = new Set(prev); if (cur.done) n.add(id); else n.delete(id); return n; });
    if (!cur.done && !date && dueTasks.some((t) => t.id === id)) {
      // v33h — rozpŕsknuté častice v mieste ťuku AJ pre Lite (predtým „Lite bez animácie")
      if (srcEl && srcEl.getBoundingClientRect) {
        const row = (srcEl.closest && srcEl.closest(".bz-row")) || srcEl;  // burst CELEJ dlaždice, nie ikony
        const r = row.getBoundingClientRect();
        setBurstFx({ key: id + ":" + Math.round(performance.now()), rect: { x: r.left, y: r.top, w: r.width, h: r.height } });
      }
      if (!s.lite_mode) {
        // úloha ostane na mieste (splnená) a keď je oko zapnuté, po ~2–4 s (burst) sa skryje — len PRO
        setHeldDone((prev) => { const n = new Set(prev); n.add(id); return n; });
        const holdMs = 2400 + Math.round(Math.abs(Math.sin(id * 999)) * 1600);  // ~2,4–4 s (deterministické podľa id)
        window.setTimeout(() => setHeldDone((prev) => { const n = new Set(prev); n.delete(id); return n; }), holdMs);
        // v32.22 — prvé splnenie: jednorazovo vysvetli, že splnené návyky skrývame a dajú sa zobraziť okom.
        // Až keď je onboarding tutoriál aj úvodná prehliadka zavreté — inak toast prekryje ich overlay
        // (z 620 > toast 120) a nový užívateľ ho nikdy neuvidí (flag by sa spálil naprázdno).
        if (hideDone && !hideHintSeen.current && s.tutorial_done && s.seen_tour) {
          hideHintSeen.current = true;
          try { localStorage.setItem(_hideHintKey, "1"); } catch (e) { /* */ }
          window.setTimeout(() => showToast("Splnené návyky skrývame — zobrazíš ich ikonou oka 👁", 6000), holdMs - 300);
        }
      }
      /* deň-kruh modal dočasne VYPNUTÝ (na žiadosť):
      const tasks = dueTasks.map((t) => ({ id: t.id, icon: t.icon, icon_color: t.icon_color, label: t.label, pts: t.pts, done: t.id === id ? true : !!t.done }));
      const doneN = tasks.filter((t) => t.done).length;
      setDayRing({ tasks, justCompletedId: id, todayPts: todayPts + (cur.pts || 0), dayMax, doneCount: doneN,
        allDone: doneN === tasks.length, effect: s.done_effect, perfectStreak: s.perfectStreak || 0 }); */
    }
    // H2: backfill a past day (server awards 0 points but keeps the streak)
    await api("/api/toggle", { method: "POST", body: { task_id: id, date: date || todayKey() } });
    reload();
    // v32.19 — AI citát sa už NEregeneruje po každom splnení (šetrenie usage); obnoví sa
    // len raz, keď je deň kompletný (perfektný deň) — pozri effect nižšie.
  }
  // v25 §4.4 — quick "Preskočiť dnes" (excused rest day) from the habit detail; on=false undoes it
  async function skipToday(id, on) {
    await api("/api/cell-set", { method: "POST", body: { task_id: id, date: todayKey(), state: on ? "skip" : "none" } });
    reload();
  }
  // v2 — Týždeň edit session: record each toggle so "Zrušiť zmeny" can replay (revert) them.
  function recordToggle(id, date) { setTableEdits((e) => [...e, { id, date: date || todayKey() }]); toggle(id, date); }
  // v2 — buffered 3-state table edits: cell clicks stage a target state locally;
  // nothing is saved until Hotovo (commit) — the red button reverts (discards).
  const [pendingCells, setPendingCells] = useState({});
  function onCellSet(id, date, next) { setPendingCells((p) => ({ ...p, [id + "|" + (date || todayKey())]: next })); }
  const [committing, setCommitting] = useState(false);
  async function commitTableEdits() {
    const entries = Object.entries(pendingCells);
    if (!entries.length) { setEditMode(false); return; }
    setCommitting(true);
    for (const [key, state] of entries) {
      const [id, date] = key.split("|");
      try { await api("/api/cell-set", { method: "POST", body: { task_id: Number(id), date, state } }); } catch (e) { /* */ }
    }
    setPendingCells({}); setCommitting(false); setEditMode(false); reload();
  }
  function revertTableEdits() { setPendingCells({}); }  // v26 §3.18 — zahodí zmeny, edit mód ostáva
  // v34 — nedokončené úpravy tabuľky: odchod (prepnutie na Zoznam / iný tab) sa najprv spýta, či uložiť.
  const [leaveGuard, setLeaveGuard] = useState(null);   // { proceed } — čo spraviť po rozhodnutí
  const hasTableEdits = () => editMode && Object.keys(pendingCells).length > 0;
  // vráti true (a otvorí potvrdenie) ak sú neuložené zmeny — inak false a pokračuj rovno
  function guardTableLeave(proceed) {
    if (hasTableEdits()) { setLeaveGuard({ proceed }); return true; }
    return false;
  }
  // sprístupni strážcu App-u (spodná navigácia / prepnutie tabu); čerstvý closure cez efekt bez závislostí
  useEffect(() => {
    if (!navGuard) return;
    navGuard.current = guardTableLeave;
    return () => { if (navGuard) navGuard.current = null; };
  });
  // v2 — mesačný report (PDF s grafmi) — Community/premium
  const [reportBusy, setReportBusy] = useState(false);
  async function downloadReport() {
    if (!session.premium) { setToast("Mesačný report je súčasťou Community. 💛"); return; }
    setReportBusy(true);
    try {
      const res = await fetch("/api/report.pdf");
      if (res.status === 429) { const j = await res.json().catch(() => ({})); setToast(j.error || "Report si dnes už stiahol. Skús zajtra."); }
      else if (!res.ok) { setToast("Report sa nepodarilo vytvoriť."); }
      else { const url = URL.createObjectURL(await res.blob()); const a = document.createElement("a"); a.href = url; a.download = "buzerlistok-report.pdf"; a.click(); URL.revokeObjectURL(url); }
    } catch (e) { setToast("Report sa nepodarilo vytvoriť."); }
    setReportBusy(false);
  }
  async function cancelTableChanges() {
    const edits = tableEdits;
    setTableEdits([]);
    for (const e of edits) { try { await api("/api/toggle", { method: "POST", body: { task_id: e.id, date: e.date } }); } catch { /* */ } }
    reload();
    setEditMode(false);
  }
  async function pickMood(id) {
    const m = s.mood === id ? null : id;
    if (!m) setWhy("");  // deselecting clears today's note too
    // v31.7 — výber nálady už neotvára samostatný „Prečo…" modál (dôvod sa píše priamo v mood modáli)
    await api("/api/mood", { method: "POST", body: { mood_id: m, date: todayKey() } });
    reload();
  }
  // H1 — backfill / edit a past day's mood
  function openMoodEdit(dayKey) {
    setMoodEditNote((s.moodNotes && s.moodNotes[dayKey]) || "");
    setMoodDetail(null);
    setMoodEdit(dayKey);
  }
  async function pickMoodFor(dayKey, id) {
    const cur = s.moodHistory[dayKey] || null;
    const m = cur === id ? null : id;  // tap the selected mood again to remove it
    const body = m ? { mood_id: m, date: dayKey, note: moodEditNote.trim() } : { mood_id: null, date: dayKey };
    await api("/api/mood", { method: "POST", body });
    if (!m) { setMoodEdit(null); }
    reload();
  }
  async function saveMoodNoteFor(dayKey) {
    if (!s.moodHistory[dayKey]) return;  // a note attaches to a chosen mood
    await api("/api/mood", { method: "POST", body: { date: dayKey, note: moodEditNote.trim() } });
    setMoodEdit(null);
    reload();
  }
  const savedWhy = (s.moodNotes && s.moodNotes[todayKey()]) || "";
  const whyDirty = why.trim() !== savedWhy.trim();
  async function saveWhy() {
    if (!s.mood || !whyDirty) return;  // a note always attaches to a chosen mood
    await api("/api/mood", { method: "POST", body: { date: todayKey(), note: why.trim() } });
    setWhySaved(true);  // E3: show ✓ briefly, then close
    reload();
    setTimeout(() => { setWhyModal(false); setWhySaved(false); }, 650);
  }
  // open exactly one create form at a time; also closes any inline edit. Drafts are preserved
  // because each form keeps its own field state — collapsing only hides it.
  function openOnly(key) { setEditingId(null); setOpenForm(key); }
  // v20 §29 — mazanie ponúka voľbu archivovať / zmazať natrvalo
  function deleteHabit(id) { setDeleteId(id); }
  async function archiveHabit(id) { await api("/api/tasks/" + id + "?archive=1", { method: "DELETE" }); reload(); }
  async function doDeleteHabit(archive) {
    const id = deleteId;
    setDeleteId(null);
    if (!id) return;
    await api("/api/tasks/" + id + (archive ? "?archive=1" : ""), { method: "DELETE" }); reload();
  }
  // v33 — voliteľné dlhšie zobrazenie (napr. vysvetľujúci hint o skrytých splnených návykoch
  // potrebuje viac času na prečítanie než bežné krátke potvrdenie)
  function showToast(msg, ms = 2600) { setToast(msg); setTimeout(() => setToast(""), ms); }
  async function saveAsTemplate(t) {
    try { await api("/api/templates", { method: "POST", body: { icon: t.icon, label: t.label, desc: t.desc, points: t.pts, timer_sec: t.timer_sec } });
      showToast("Uložené ako šablóna"); } catch (e) { showToast("Nepodarilo sa uložiť šablónu"); }
  }
  async function joinCh(cid) {
    setJoinBusy(true);
    try { await api("/api/challenges/" + cid + "/join", { method: "POST" }); setInviteDetail(null); reload(); }
    finally { setJoinBusy(false); }
  }
  async function declineCh(cid) { await api("/api/challenges/" + cid + "/decline", { method: "POST" }); setInviteDetail(null); reload(); }
  // PATCH len zmenu skupiny návyku (poradie rieši commitDrop cez saveOrder / dnes-order).
  async function moveHabitPatch(id, catId) {
    await api("/api/tasks/" + id, { method: "PATCH", body: { category_id: catId } });
  }

  // Prehltne tap, ktorý nasleduje po dlhom podržaní (aby sa neotvoril detail/úprava).
  const swallow = (firedRef, fn) => (e) => { if (firedRef.current) { firedRef.current = false; return; } return fn && fn(e); };

  // ── Drag & drop NÁVYKOV (edit mode) ──────────────────────────────────────────
  // Podržanie návyku ho zdvihne a ťahá; mimo edit modu zdvih rovno zapne edit mode.
  // Prepoužité endpointy: tasks/reorder (poradie v rámci rutiny), PATCH category_id (presun
  // do skupiny), dnes-order (poradie samostatných blokov). Pri chybe = plný refetch (reload).
  const [draggingId, setDraggingId] = useState(null);
  const [dropAt, setDropAt] = useState(null);        // { cat: "_solo"|"<catId>", beforeId: number|null }
  const dragIdRef = useRef(null);
  const dropRef = useRef(null);
  const dndRef = useRef({});
  // habitsAll = všetky návyky (osobné + výzvové) v serverovom poradí `sort` — ITEM 7a: výzva sa
  // v skupine usporadúva medzi návykmi cez ten istý `sort` (endpoint /api/tasks/reorder, kind=habit).
  dndRef.current = { personalHabits, challengeHabits, habitsAll: TASKS, comps: openComps, categories: s.categories || [], dnes_order: s.dnes_order, chalOrder, editMode };

  // Poradie blokov ako v zozname (cat: / task: / chal: / comp:) — pre presun samostatných.
  function currentBlockKeys(d) {
    const saved = (() => { try { return JSON.parse(d.dnes_order || "[]"); } catch { return []; } })();
    const hasChal = saved.some((k) => k.startsWith("chal:") || k.startsWith("comp:"));
    const eff = (!hasChal && d.chalOrder.length) ? [...saved, ...d.chalOrder] : saved;
    const cats = (d.categories || []).map((c) => "cat:" + c.id);
    const solos = d.personalHabits.filter((t) => t.category_id == null).map((t) => "task:" + t.id);
    const chals = [...new Set(d.challengeHabits.map((t) => t.challenge_id))].map((cid) => "chal:" + cid);
    const comps = (d.comps || []).map((c) => "comp:" + c.id);
    const all = [...cats, ...solos, ...chals, ...comps];
    const pos = new Map(eff.map((k, i) => [k, i]));
    return all.slice().sort((a, b) => (pos.has(a) ? pos.get(a) : 1e9) - (pos.has(b) ? pos.get(b) : 1e9));
  }

  // v35 — jednotné poradie riadkov V SKUPINE (návyky + súťaže) ako pole kľúčov "task:"/"comp:".
  // Zrkadlí render (mergedItems): default = návyky v serverovom `sort` poradí, potom súťaže podľa
  // sort_in_category; uložené item_order to prebije (stabilný sort → neznáme položky ostávajú na konci).
  function groupItemKeys(d, catId) {
    const cat = (d.categories || []).find((c) => c.id === catId);
    const io = (() => { try { return JSON.parse((cat && cat.item_order) || "[]"); } catch { return []; } })();
    const pos = new Map(io.map((k, i) => [k, i]));
    const habitKeys = d.habitsAll.filter((t) => t.category_id === catId).map((t) => "task:" + t.id);
    const compKeys = (d.comps || []).filter((c) => c.category_id === catId)
      .sort((a, b) => (a.sort_in_category ?? 1e9) - (b.sort_in_category ?? 1e9) || a.id - b.id)
      .map((c) => "comp:" + c.id);
    const all = [...habitKeys, ...compKeys];
    return all.slice().sort((a, b) => (pos.has(a) ? pos.get(a) : 1e9) - (pos.has(b) ? pos.get(b) : 1e9));
  }
  // beforeId z computeDrop → item kľúč ("task:"/"comp:"); null = na koniec skupiny
  function beforeKeyOf(beforeId) {
    if (beforeId == null) return null;
    return beforeId >= COMP_DRAG ? "comp:" + (beforeId - COMP_DRAG) : "task:" + beforeId;
  }

  // Kam by návyk padol pri (x,y): { cat, beforeId } — cat rutiny/„_solo", beforeId=null = na koniec.
  function computeDrop(x, y) {
    const list = document.querySelector('[data-tour="habit"]');
    if (!list) return null;
    const dragId = dragIdRef.current;
    const rows = [...list.querySelectorAll("[data-taskid]")]
      .filter((el) => Number(el.dataset.taskid) !== dragId)
      .map((el) => { const r = el.getBoundingClientRect(); return { id: Number(el.dataset.taskid), cat: el.closest("[data-catid]")?.dataset.catid ?? "_solo", top: r.top, mid: r.top + r.height / 2 }; });
    // Karta skupiny pod kurzorom → cieľ je TÁ skupina: pozícia podľa najbližšieho jej
    // viditeľného riadku; zbalená/prázdna skupina (žiadne riadky — layout sa počas ťahu
    // NErozbaľuje, nech stránka nepreskakuje) = na jej koniec.
    const groupEls = [...list.querySelectorAll("[data-catid]")]
      .map((el) => { const r = el.getBoundingClientRect(); return { id: el.dataset.catid, top: r.top, bottom: r.bottom, left: r.left, right: r.right }; });
    let grp = groupEls.find((g) => y >= g.top && y <= g.bottom);
    // Skrytý zdroj (bz-drag-src, display:none) zmenší kartu skupiny, takže pás „na koniec skupiny"
    // pod posledným riadkom je len tenký prúžok tesne nad sólo-zónou — pri malom prestrelení sa
    // ťahaná položka namiesto preradenia VYSUNIE zo skupiny. To robilo preraďovanie súťaží v skupine
    // (typicky 2 súťaže → jediný cieľ je „na koniec") prakticky nemožné. Fix: keď kurzor NIE JE
    // striktne v karte, ale je TESNE POD ňou (v jej šírke) a ešte nezasiahol stred prvého riadka
    // nasledujúceho bloku, ber to ako „koniec tejto skupiny" (nie vysunutie). Vysunutie ostáva:
    // stačí ísť nižšie (do spodnej polovice ďalšieho riadka / pod zoznam).
    if (!grp) {
      const cand = groupEls
        .filter((g) => y > g.bottom && x >= g.left && x <= g.right)
        .sort((a, b) => b.bottom - a.bottom)[0];   // najbližšia skupina nad kurzorom
      if (cand) {
        const below = rows.filter((rw) => rw.top >= cand.bottom - 1).sort((a, b) => a.top - b.top)[0];
        const cap = Math.min(cand.bottom + 44, below ? below.mid : Infinity);
        if (y <= cap) grp = cand;
      }
    }
    if (!grp) {
      // MIMO kariet skupín (nad/pod/medzi blokmi) = sólo zóna → návyk/výzva sa VYSUNIE zo skupiny
      // aj keď nablízku nie je žiaden sólo riadok. Cieľ na úrovni blokov dnes_order:
      // pred najbližší blok POD kurzorom (beforeKey), inak na koniec zoznamu.
      // G2b — vlastný blok ťahanej položky: návyk = "task:<id>", výzva = "chal:<cid>",
      // wt34 — súťaž (dragId ≥ COMP_DRAG) = "comp:<id>".
      const dragChal = dndRef.current.challengeHabits.find((t) => t.id === dragId);
      const selfBlockKey = dragId >= COMP_DRAG ? ("comp:" + (dragId - COMP_DRAG))
        : dragChal ? ("chal:" + dragChal.challenge_id) : ("task:" + dragId);
      const blocks = [...list.querySelectorAll("[data-blockkey]")]
        .filter((el) => el.dataset.blockkey !== selfBlockKey)
        .map((el) => { const r = el.getBoundingClientRect(); return { key: el.dataset.blockkey, top: r.top, mid: r.top + r.height / 2 }; })
        .sort((a, b) => a.top - b.top);
      const below = blocks.find((b) => y < b.mid);
      return { cat: "_solo", beforeId: null, beforeKey: below ? below.key : null };
    }
    const catRows = rows.filter((rw) => rw.cat === grp.id);
    if (!catRows.length) return { cat: grp.id, beforeId: null };
    let near = catRows[0], best = Infinity;
    for (const rw of catRows) { const dd = Math.abs(rw.mid - y); if (dd < best) { best = dd; near = rw; } }
    if (y < near.mid) return { cat: grp.id, beforeId: near.id };
    const nx = catRows[catRows.indexOf(near) + 1];
    return { cat: grp.id, beforeId: nx ? nx.id : null };
  }

  async function commitDrop() {
    const id = dragIdRef.current, target = dropRef.current;
    dragIdRef.current = null; dropRef.current = null;
    // zdroj drž skrytý (a placeholder) až kým nedobehne reload — inak prvok blikne na starom mieste;
    // po presune vypni edit mód (je zapnutý len počas ťahania)
    const done = () => { setDraggingId(null); setDropAt(null); setEditMode(false); };
    if (id == null || !target) { done(); return; }
    const d = dndRef.current;
    // wt34 — ťahaná položka je SÚŤAŽ (encoded id ≥ COMP_DRAG), nie návyk/výzva. Vlastná perzistencia
    // cez /api/challenges/<cid>/group (NIKDY tasks/reorder — súťaž nemá task). Optimistic-lite:
    // rovnako ako výzva/návyk = reload po uložení, plný refetch pri chybe.
    if (id >= COMP_DRAG) {
      const compId = id - COMP_DRAG;
      const comp = (d.comps || []).find((c) => c.id === compId);
      if (!comp) { done(); return; }
      const tgt = target.cat;
      try {
        if (tgt === "_solo") {
          // VON zo skupiny → súťaž je zas top-level blok "comp:<id>" v dnes_order (mirror výzvy G2b)
          await api("/api/challenges/" + compId + "/group", { method: "POST", body: { category_id: null } });
          const selfKey = "comp:" + compId;
          const keys = currentBlockKeys(d).filter((k) => k !== selfKey);
          const beforeKey = target.beforeKey || (target.beforeId == null ? null
            : (target.beforeId >= COMP_DRAG ? "comp:" + (target.beforeId - COMP_DRAG) : "task:" + target.beforeId));
          const at = beforeKey ? keys.indexOf(beforeKey) : -1;
          if (at >= 0) keys.splice(at, 0, selfKey); else keys.push(selfKey);
          await api("/api/dnes-order", { method: "POST", body: { order: keys } });
        } else {
          // DO skupiny / v rámci nej → v35 jednotné poradie (návyky + súťaže) cez item_order skupiny,
          // takže súťaž sa dá umiestniť MEDZI návyky (nielen do vlastného bloku súťaží). Členstvo
          // (category_id) rieši /group; poradie výhradne item-order.
          const tgtNum = Number(tgt);
          if (comp.category_id !== tgtNum) {
            await api("/api/challenges/" + compId + "/group", { method: "POST", body: { category_id: tgtNum } });
          }
          const movedKey = "comp:" + compId;
          const keys = groupItemKeys(d, tgtNum).filter((k) => k !== movedKey);
          const beforeKey = beforeKeyOf(target.beforeId);
          const at = beforeKey ? keys.indexOf(beforeKey) : -1;
          if (at >= 0) keys.splice(at, 0, movedKey); else keys.push(movedKey);
          await api("/api/categories/" + tgtNum + "/item-order", { method: "POST", body: { order: keys } });
        }
        await reload();
      } catch (e) { showToast("Presun sa nepodaril"); await reload(); }
      done();
      return;
    }
    // G2b — zdroj môže byť aj výzva (challenge-backing task), nielen osobný návyk
    const src = d.personalHabits.find((t) => t.id === id) || d.challengeHabits.find((t) => t.id === id);
    if (!src) { done(); return; }
    const isChal = !!src.challenge_id;
    const srcCat = src.category_id == null ? "_solo" : String(src.category_id);
    const tgt = target.cat;
    try {
      if (srcCat !== tgt) await moveHabitPatch(id, tgt === "_solo" ? null : Number(tgt));
      if (isChal && tgt === "_solo") {
        // G2b — výzva mimo skupiny = top-level blok "chal:<cid>" v dnes_order
        const selfKey = "chal:" + src.challenge_id;
        const keys = currentBlockKeys(d).filter((k) => k !== selfKey);
        const beforeKey = target.beforeKey || (target.beforeId != null ? "task:" + target.beforeId : null);
        const at = beforeKey ? keys.indexOf(beforeKey) : -1;
        if (at >= 0) keys.splice(at, 0, selfKey); else keys.push(selfKey);
        await api("/api/dnes-order", { method: "POST", body: { order: keys } });
      } else if (tgt !== "_solo") {
        // v35 — do rutiny / v rámci nej: jednotné poradie (návyky + súťaže) cez item_order skupiny,
        // aby sa návyk/výzva dal usporiadať MEDZI súťaže (a naopak). Global `sort` (tasks/reorder)
        // ostáva aktualizovaný, nech tabuľka „Týždeň" (orderedForTable → sort) sedí so Zoznamom.
        const tgtNum = Number(tgt);
        const movedKey = "task:" + id;
        const keys = groupItemKeys(d, tgtNum).filter((k) => k !== movedKey);
        const beforeKey = beforeKeyOf(target.beforeId);
        const at = beforeKey ? keys.indexOf(beforeKey) : -1;
        if (at >= 0) keys.splice(at, 0, movedKey); else keys.push(movedKey);
        await api("/api/categories/" + tgtNum + "/item-order", { method: "POST", body: { order: keys } });
        // global sort: presúvaný návyk vlož pred NAJBLIŽŠÍ nasledujúci NÁVYK v novom poradí skupiny
        // (súťaže v `sort` nie sú); tým Týždeň-tabuľka drží rovnaké poradie návykov ako Zoznam.
        const mi = keys.indexOf(movedKey);
        let nextHabitId = null;
        for (let i = mi + 1; i < keys.length; i++) { if (keys[i].startsWith("task:")) { nextHabitId = Number(keys[i].slice(5)); break; } }
        const gids = d.habitsAll.map((t) => t.id).filter((x) => x !== id);
        const gat = nextHabitId != null ? gids.indexOf(nextHabitId) : -1;
        if (gat >= 0) gids.splice(gat, 0, id); else gids.push(id);
        await saveOrder("habit", gids);
      } else {
        // do/medzi samostatné — poradie blokov (dnes_order), blok návyku = "task:<id>";
        // cieľ môže byť pred ľubovoľným blokom (beforeKey zo sólo zóny) alebo pred sólo návykom
        const keys = currentBlockKeys(d).filter((k) => k !== "task:" + id);
        const beforeKey = target.beforeKey || (target.beforeId != null ? "task:" + target.beforeId : null);
        const at = beforeKey ? keys.indexOf(beforeKey) : -1;
        if (at >= 0) keys.splice(at, 0, "task:" + id); else keys.push("task:" + id);
        await api("/api/dnes-order", { method: "POST", body: { order: keys } });
      }
      await reload();
    } catch (e) { showToast("Presun sa nepodaril"); await reload(); }   // plný refetch namiesto ručného rollbacku
    done();
  }

  const dnd = useDragReorder({
    longPressMs: 320,   // svižnejší štart ťahu na dotyku (menej času na náhodný scroll)
    itemSelector: "[data-taskid]",   // úchytka chytá celý riadok návyku
    canLift: () => !openForm && editingId == null && !routineCreate && !habitTable,
    onLift: (el) => {
      const id = Number(el.dataset.taskid);
      dragIdRef.current = id; setDraggingId(id);
      // ŽIADNY editMode pri zdvihu — prepínal layout (skryté fajky, rozbalené skupiny,
      // zmiznuté bannery) a stránka „preskočila" pod prstom. Feedback ťahu robí duch,
      // placeholder a skrytý zdroj; klik po drope zožerie swallow(fired).
    },
    onHover: (x, y) => { const t = computeDrop(x, y); dropRef.current = t; setDropAt(t); },
    onDrop: () => commitDrop(),
    onCancel: () => { dragIdRef.current = null; dropRef.current = null; setDraggingId(null); setDropAt(null); setEditMode(false); },
  });

  // ── Drag & drop BLOKOV (hlavičky skupín, výzvy, súťaže) — dnes_order ──────────
  // Podrž hlavičku skupiny alebo kartu výzvy/súťaže → ťahá sa celý blok. Všetky top-level
  // bloky (skupiny, samostatné návyky, výzvy, súťaže) sa preusporiadajú spolu.
  const [draggingBlock, setDraggingBlock] = useState(null);   // ťahaný block key
  const [blockDropAt, setBlockDropAt] = useState(null);       // { beforeKey: string|null }
  const dragBlockRef = useRef(null);
  const blockDropRef = useRef(null);

  function computeBlockDrop(x, y) {
    const list = document.querySelector('[data-tour="habit"]');
    if (!list) return null;
    const dragKey = dragBlockRef.current;
    const blocks = [...list.querySelectorAll("[data-blockkey]")]
      .filter((el) => el.dataset.blockkey !== dragKey)
      .map((el) => { const r = el.getBoundingClientRect(); return { key: el.dataset.blockkey, mid: r.top + r.height / 2 }; });
    if (!blocks.length) return { beforeKey: null };
    let near = blocks[0], best = Infinity;
    for (const b of blocks) { const dd = Math.abs(b.mid - y); if (dd < best) { best = dd; near = b; } }
    if (y < near.mid) return { beforeKey: near.key };
    const nx = blocks[blocks.indexOf(near) + 1];
    return { beforeKey: nx ? nx.key : null };
  }

  async function commitBlockDrop() {
    const key = dragBlockRef.current, target = blockDropRef.current;
    dragBlockRef.current = null; blockDropRef.current = null;
    const done = () => { setDraggingBlock(null); setBlockDropAt(null); setEditMode(false); };
    if (!key || !target) { done(); return; }
    const d = dndRef.current;
    try {
      const keys = currentBlockKeys(d).filter((k) => k !== key);
      const at = target.beforeKey ? keys.indexOf(target.beforeKey) : -1;
      if (at >= 0) keys.splice(at, 0, key); else keys.push(key);
      await api("/api/dnes-order", { method: "POST", body: { order: keys } });
      await reload();
    } catch (e) { showToast("Presun sa nepodaril"); await reload(); }   // plný refetch
    done();   // blok drž skrytý až po reloade → žiadne bliknutie na starom mieste
  }

  const blockDnd = useDragReorder({
    longPressMs: 320,
    itemSelector: "[data-blockkey]",   // chytí celý blok (skupina/výzva/súťaž)
    ghostClass: "bz-drag-ghost--block",   // ťahaná skupina skomprimovaná na úzky riadok
    canLift: () => !openForm && editingId == null && !routineCreate && !habitTable,
    onLift: (el) => {
      const key = el.dataset.blockkey || (el.closest("[data-blockkey]") && el.closest("[data-blockkey]").dataset.blockkey);
      if (!key) return;
      dragBlockRef.current = key; setDraggingBlock(key);
      // bez editMode — viď dnd.onLift (layout ostáva stabilný počas ťahu)
    },
    onHover: (x, y) => { const t = computeBlockDrop(x, y); blockDropRef.current = t; setBlockDropAt(t); },
    onDrop: () => commitBlockDrop(),
    onCancel: () => { dragBlockRef.current = null; blockDropRef.current = null; setDraggingBlock(null); setBlockDropAt(null); setEditMode(false); },
  });
  const swallowBlock = (fn) => (e) => { if (blockDnd.fired.current) { blockDnd.fired.current = false; return; } return fn && fn(e); };

  // ── Drag & drop v sekcii „Máš čas" (zriedkavé návyky) — SCOPED v rámci sekcie ──
  // Vlastná inštancia dnd + vlastné poradie (users.mascas_order); nikdy sa nemieša
  // s bežnými návykmi (iný selektor [data-mascasid], iný kontajner [data-mascas]).
  const [draggingMc, setDraggingMc] = useState(null);
  const [mcDropAt, setMcDropAt] = useState(null);   // { beforeId: number|null }
  const dragMcRef = useRef(null);
  const mcDropRef = useRef(null);
  const mcRef = useRef({});   // { masCas: [...], mascas_order }
  function currentMasCasOrder(d) {
    const saved = (() => { try { return JSON.parse(d.mascas_order || "[]"); } catch { return []; } })();
    const keys = (d.masCas || []).map((t) => "task:" + t.id);
    const pos = new Map(saved.map((k, i) => [k, i]));
    return keys.slice().sort((a, b) => (pos.has(a) ? pos.get(a) : 1e9) - (pos.has(b) ? pos.get(b) : 1e9));
  }
  function computeMasCasDrop(x, y) {
    const list = document.querySelector('[data-mascas="1"]');
    if (!list) return null;
    const dragId = dragMcRef.current;
    const rows = [...list.querySelectorAll("[data-mascasid]")]
      .filter((el) => Number(el.dataset.mascasid) !== dragId)
      .map((el) => { const r = el.getBoundingClientRect(); return { id: Number(el.dataset.mascasid), mid: r.top + r.height / 2 }; })
      .sort((a, b) => a.mid - b.mid);
    if (!rows.length) return { beforeId: null };
    let near = rows[0], best = Infinity;
    for (const rw of rows) { const dd = Math.abs(rw.mid - y); if (dd < best) { best = dd; near = rw; } }
    if (y < near.mid) return { beforeId: near.id };
    const nx = rows[rows.indexOf(near) + 1];
    return { beforeId: nx ? nx.id : null };
  }
  async function commitMasCasDrop() {
    const id = dragMcRef.current, target = mcDropRef.current;
    dragMcRef.current = null; mcDropRef.current = null;
    const done = () => { setDraggingMc(null); setMcDropAt(null); setEditMode(false); };
    if (id == null || !target) { done(); return; }
    try {
      const keys = currentMasCasOrder(mcRef.current).filter((k) => k !== "task:" + id);
      const beforeKey = target.beforeId != null ? "task:" + target.beforeId : null;
      const at = beforeKey ? keys.indexOf(beforeKey) : -1;
      if (at >= 0) keys.splice(at, 0, "task:" + id); else keys.push("task:" + id);
      await api("/api/mascas-order", { method: "POST", body: { order: keys } });
      await reload();
    } catch (e) { showToast("Presun sa nepodaril"); await reload(); }
    done();
  }
  const mascasDnd = useDragReorder({
    longPressMs: 320,
    itemSelector: "[data-mascasid]",
    canLift: () => !openForm && editingId == null && !routineCreate && !habitTable,
    onLift: (el) => { const id = Number(el.dataset.mascasid); dragMcRef.current = id; setDraggingMc(id); },
    onHover: (x, y) => { const t = computeMasCasDrop(x, y); mcDropRef.current = t; setMcDropAt(t); },
    onDrop: () => commitMasCasDrop(),
    onCancel: () => { dragMcRef.current = null; mcDropRef.current = null; setDraggingMc(null); setMcDropAt(null); setEditMode(false); },
  });
  // H6: a competition rendered as a list row (tag + tap → detail modal)
  function renderCompRow(comp) {
    const closed = comp.status === "closed";
    return (
      <div key={"comp" + comp.id} className="task" style={st.task}>
        <button onClick={() => setOpenComp(comp)} style={st.taskMain}>
          <TypeChip comp icon={comp.icon} chipStyle={st.taskIcon} />
          <span style={st.taskText}>
            <span style={st.taskLabel}>{comp.name}</span>
            <span style={st.taskDesc}>
              {closed ? "Vyhodnotené" : "Zapíš dnešok"} · {comp.metric_label}
              {comp.my_result != null ? " · spolu " + (comp.result_timer ? fmtDur(comp.my_result) : comp.my_result) : ""}
            </span>
          </span>
          <span style={st.taskRight}><Icon name="chevron" size={18} color="var(--muted)" /></span>
        </button>
      </div>
    );
  }
  // v9 — compact competition card for the "Výzvy a súťaže" carousel
  function renderCompCard(comp) {
    const closed = comp.status === "closed";
    return (
      <button key={"compc" + comp.id} type="button" onClick={() => setOpenComp(comp)} style={st.compCard}>
        <div style={st.compCardTop}>
          <TypeChip comp={!!comp.is_competition} icon={comp.icon} chipStyle={{ ...st.routineIcon, ...iconChipStyle(comp.icon_color) }} />
        </div>
        <span style={{ ...st.taskLabel, fontSize: 17 }}>{comp.name}</span>
        <span style={st.taskDesc}>{closed ? "Vyhodnotené" : "Zapíš dnešok"} · {comp.metric_label}</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 700, color: "var(--text2)", marginTop: 2 }}>
          {comp.my_result != null ? "spolu " + (comp.result_timer ? fmtDur(comp.my_result) : comp.my_result) : "priebeh"}
        </span>
      </button>
    );
  }
  // v9 — a shared challenge (výzva) carousel card; tap opens the leaderboard, the check completes today
  function renderChallengeCard(t) {
    return (
      <div key={"chalc" + t.challenge_id} style={st.compCard} role="button"
        onClick={() => setOpenChallenge({ cid: t.challenge_id, icon: t.icon, label: t.label })}>
        <div style={st.compCardTop}>
          <TypeChip icon={t.icon} chipStyle={{ ...st.routineIcon, ...iconChipStyle(t.icon_color) }} />
        </div>
        <span style={{ ...st.taskLabel, fontSize: 17 }}>{t.label}</span>
        <span style={st.taskDesc}>{t.desc || "Spoločná výzva"}</span>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 2 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 700, color: "var(--text2)" }}>
            {t.done ? "splnené dnes" : "+" + (t.pts || 0) + " · rebríček"}
          </span>
          <button type="button" onClick={(e) => { e.stopPropagation(); toggle(t.id); }} aria-label="Označiť splnené"
            title="Označiť splnené" style={{ ...st.check, ...(t.done ? st.checkOn : {}), cursor: "pointer" }}>
            <Icon name="check" size={15} color={t.done ? "#fff" : "var(--field-border)"} stroke={3} />
          </button>
        </div>
      </div>
    );
  }
  // one habit row — v2 redesign: ListRow + CheckButton. V edit móde sa riadok ťahá
  // (useDragReorder); tap otvorí úpravu. Len osobné (non-challenge) návyky sem chodia.
  function renderHabitRow(t, opts = {}) {
    const noDrag = !!opts.noDrag;   // v32.21 — riadok v sekcii SPLNENÉ: bez ťahania, netreba data-taskid
    const reordering = editMode;
    const met = weekMet(t);  // v25 §3.1 — weekly target reached, shown dimmed + re-achievable
    const skippedToday = !!t.skip_today && !t.done;  // v25 §4.4 — dnes preskočený (rest day)
    const tags = [];
    if (skippedToday) tags.push(<Tag key="s" tone="neutral" size="xs">preskočené</Tag>);
    if (t.boost) tags.push(<Tag key="b" tone="neutral" size="xs">len dnes</Tag>);
    if (t.recur_mode === "interval") tags.push(<Tag key="i" tone="neutral" size="xs">každý {t.recur_n}. deň</Tag>);
    if (t.recur_mode === "weekdays" && t.recur_days) tags.push(<Tag key="d" tone="neutral" size="xs">{t.recur_days.split(",").map((n) => WEEKDAYS_SK[Number(n) - 1]).join("·")}</Tag>);
    // ITEM 1 — progres (N/M× týždeň · N/M× obdobie · N/N× dnes) sa presunul z riadka MENA do META
    // riadka (body · popis · progres). v33c — „viackrát za deň": tag ostáva aj po splnení.
    const progressTags = [];
    if (t.recur_mode === "weekly_count") progressTags.push(<Tag key="w" tone="brand" size="xs" icon={met ? "check" : "calendar"}>{t.week_done || 0}/{t.week_target}× týždeň</Tag>);
    if (t.recur_mode === "period") progressTags.push(<Tag key="p" tone="brand" size="xs" icon={met ? "check" : "calendar"}>{t.week_done || 0}/{t.week_target}× {RECUR_PERIOD_UNIT[t.recur_period] || "obdobie"}</Tag>);
    if ((t.per_day || 1) > 1) progressTags.push(<Tag key="pd" tone="brand" size="xs" icon={t.done ? "check" : "calendar"}>{t.done ? t.per_day : (t.reps_today || 0)}/{t.per_day}× dnes</Tag>);
    const title = (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span>{t.label}</span>
        {tags}
      </span>
    );
    // meta: body · popis · progres (v Lite bez body → popis · progres)
    const metaSep = <span style={{ width: 3, height: 3, borderRadius: 99, background: "var(--neutral-text-subtle)" }} />;
    const metaPieces = [
      !s.lite_mode ? (t.done
        ? <Tag tone="brand" size="xs" strong>+{t.pts}</Tag>
        : <Tag tone="brand" size="xs" bare>+{t.pts}</Tag>) : null,
      t.desc ? <span style={{ fontSize: 11.5, color: "var(--neutral-text-muted)" }}>{t.desc}</span> : null,
      ...progressTags,
    ].filter(Boolean);
    const metaNode = (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        {metaPieces.map((p, i) => <React.Fragment key={i}>{i > 0 && metaSep}{p}</React.Fragment>)}
      </span>
    );
    // počas ťahania (edit mód): bez ovládačov — riadok sa ťahá dlhým podržaním
    const trailing = reordering ? null : met ? (
      // v25 §3.1 — target met: green check badge; tap adds another completion this week
      <CheckButton done aria-label="Splniť znova aj dnes"
        onClick={(e) => { if (e) e.stopPropagation(); toggle(t.id, null, e && e.currentTarget); }} />
    ) : (
      <CheckButton done={t.done} steps={t.per_day || 1}
        fill={(t.per_day || 1) > 1 ? (t.reps_today || 0) / t.per_day : undefined}
        aria-label={t.done ? "Zrušiť splnenie" : "Označiť splnené"}
        onClick={(e) => { if (e) e.stopPropagation(); if (t.done) setUncheckTask(t); else toggle(t.id, null, e && e.currentTarget); }} />
    );
    if (noDrag) {
      // SPLNENÉ sekcia — bez drag handlerov/data-taskid (nech ich reorder neměria); tap = detail
      return (
        <ListRow key={t.id}
          onClick={() => setDetailTask(t)}
          style={{ background: "var(--page-grad)" }}
          lead={<Lead tone="outline" size="sm" style={{ ...iconChipStyle(t.icon_color), borderColor: "var(--neutral-border-strong)" }}><Glyph icon={t.icon} size={17} /></Lead>}
          title={title} meta={metaNode} trailing={trailing} />
      );
    }
    if (opts.mascas) {
      // v33b — riadok v sekcii „Máš čas": vlastný reorder (mascasDnd), scoped v rámci sekcie
      return (
        <ListRow key={t.id} data-mascasid={t.id}
          className={[t.id === flashId ? "bz-flash" : "", t.id === draggingMc ? "bz-drag-src" : ""].filter(Boolean).join(" ") || undefined}
          {...mascasDnd.handlers}
          onClick={swallow(mascasDnd.fired, () => setDetailTask(t))}
          style={t.done ? { background: "var(--page-grad)" } : (met ? { opacity: 0.55 } : undefined)}
          lead={<Lead tone="outline" size="sm" style={{ ...iconChipStyle(t.icon_color), ...(t.done ? { borderColor: "var(--neutral-border-strong)" } : {}) }}><Glyph icon={t.icon} size={17} /></Lead>}
          title={title} meta={metaNode} trailing={trailing} />
      );
    }
    return (
      <ListRow key={t.id} data-taskid={t.id}
        className={[t.id === flashId ? "bz-flash" : "", t.id === draggingId ? "bz-drag-src" : ""].filter(Boolean).join(" ") || undefined}
        {...dnd.handlers}
        onClick={swallow(dnd.fired, reordering ? () => setEditingId(t.id) : () => setDetailTask(t))}
        style={t.done && !reordering ? { background: "var(--page-grad)" } : ((met || skippedToday) && !reordering ? { opacity: 0.55 } : undefined)}
        lead={<Lead tone="outline" size="sm" style={{ ...iconChipStyle(t.icon_color), ...(t.done && !reordering ? { borderColor: "var(--neutral-border-strong)" } : {}) }}><Glyph icon={t.icon} size={17} /></Lead>}
        title={title} meta={metaNode} trailing={trailing} />
    );
  }
  // v32.21 — blok výzvy (top-level). G2b — výzva sa teraz ťahá cez PER-ROW dnd (rovnako ako
  // samostatný návyk: <List data-blockkey> obal + <ListRow data-taskid>), aby sa dala pretiahnuť
  // do skupiny (computeDrop/commitDrop nastavia category_id) aj von. data-blockkey ostáva, aby
  // sa ostatné bloky (skupiny/súťaže/návyky) vedeli usporiadať voči nej cez blockDnd.
  function renderChalBlock(t) {
    const met = weekMet(t);
    const dim = t.done && !editMode ? { background: "var(--page-grad)", boxShadow: "none" } : (met && !editMode ? { opacity: 0.55 } : undefined);
    // celú kartu skry počas ťahu (rovnako čisto ako doteraz blockDnd, nie prázdny obal)
    return (
      <List data-blockkey={"chal:" + t.challenge_id} className={t.id === draggingId ? "bz-drag-src" : undefined} style={dim}>
        {renderChalRow(t)}
      </List>
    );
  }
  // G2b — jeden riadok výzvy (per-row dnd, data-taskid). Použitý top-level (v obale renderChalBlock)
  // aj priamo ako riadok v karte skupiny. Zachováva správanie výzvy: tap → rebríček (setOpenChallenge),
  // fajka → splnenie, meta body·popis·frekvencia. Odlíšenie od návyku: štítok „výzva" + výzvový chip.
  function renderChalRow(t) {
    const met = weekMet(t);
    const openIt = () => setOpenChallenge({ cid: t.challenge_id, icon: t.icon, label: t.label });
    const dimRow = t.done && !editMode ? { background: "var(--page-grad)" } : (met && !editMode ? { opacity: 0.55 } : undefined);
    return (
      <ListRow key={t.id} data-taskid={t.id}
        className={[t.id === flashId ? "bz-flash" : "", t.id === draggingId ? "bz-drag-src" : ""].filter(Boolean).join(" ") || undefined}
        {...dnd.handlers}
        onClick={swallow(dnd.fired, openIt)}
        style={dimRow}
        lead={<TypeChip icon={t.icon} size={17} done={(!!t.done || met) && !editMode} className="bz-lead bz-lead--circle bz-lead--sm bz-lead--outline" chipStyle={iconChipStyle(t.icon_color)} />}
        title={<span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}><span>{t.label}</span></span>}
        meta={(() => {
          // ITEM 1 — poradie: body · popis · progres (žiadny rank vo výzvovom riadku); v Lite bez body
          const sep = <span style={{ width: 3, height: 3, borderRadius: 99, background: "var(--neutral-text-subtle)" }} />;
          const pieces = [
            (!s.lite_mode && t.pts != null) ? ((t.done || met)
              ? <Tag tone="brand" size="xs" strong>+{t.pts}</Tag>
              : <Tag tone="brand" size="xs" bare>+{t.pts}</Tag>) : null,
            t.desc ? <span style={{ fontSize: 11.5, color: "var(--neutral-text-muted)" }}>{t.desc}</span> : null,
            t.recur_mode === "weekly_count" ? <Tag tone="brand" size="xs" icon={met ? "check" : "calendar"}>{(t.week_done || 0)}/{t.week_target}× týždeň</Tag> : null,
          ].filter(Boolean);
          return <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>{pieces.map((p, i) => <React.Fragment key={i}>{i > 0 && sep}{p}</React.Fragment>)}</span>;
        })()}
        trailing={editMode ? null : (met
          ? <CheckButton done aria-label="Splniť znova aj dnes" onClick={(e) => { if (e) e.stopPropagation(); toggle(t.id, null, e && e.currentTarget); }} />
          : <CheckButton done={t.done} steps={t.per_day || 1} fill={(t.per_day || 1) > 1 ? (t.reps_today || 0) / t.per_day : undefined} aria-label="Označiť splnené" onClick={(e) => { if (e) e.stopPropagation(); if (t.done) setUncheckTask(t); else toggle(t.id, null, e && e.currentTarget); }} />)} />
    );
  }
  // wt34 — spoločný obsah riadka súťaže (lead/title/meta/trailing) pre TOP-LEVEL blok
  // (renderCompBlock, block dnd) aj riadok V SKUPINE (renderCompGroupRow, per-row dnd), aby sa
  // vzhľad ani meta nerozišli. Tap/fajka vždy otvoria detail (setOpenComp).
  function compRowProps(comp) {
    return {
      lead: <TypeChip comp={!!comp.is_competition} icon={comp.icon} size={17} done={!!comp.done_today && !editMode} className="bz-lead bz-lead--circle bz-lead--sm bz-lead--outline" chipStyle={iconChipStyle(comp.icon_color)} />,
      title: comp.name,
      meta: (<span style={{ display: "inline-flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        {/* O1 — poradie: body · popis(metric_label) · frekvencia · umiestnenie(my_rank) */}
        {!s.lite_mode && comp.points != null && <Tag tone="brand" size="xs" bare>+{comp.points}</Tag>}
        {comp.metric_label ? (<>{!s.lite_mode && comp.points != null && <span style={{ width: 3, height: 3, borderRadius: 99, background: "var(--neutral-text-subtle)" }} />}<span style={{ fontSize: 11.5, color: "var(--neutral-text-muted)" }}>{comp.metric_label}</span></>) : null}
        {/* v2 — koľkatý si v rebríčku, priamo v miniatúre (netreba otvárať detail) */}
        {comp.my_rank != null && comp.rank_total > 1 && <Tag tone={comp.my_rank === 1 ? "success" : "neutral"} size="xs" icon="trophy">{comp.my_rank}. z {comp.rank_total}</Tag>}</span>),
      trailing: editMode ? null : <CheckButton done={comp.done_today} optimistic={false} aria-label="Zapísať výsledok" onClick={(e) => { if (e) e.stopPropagation(); setOpenComp(comp); }} />,
    };
  }
  // v32.21 — blok súťaže (vlastný výsledok); noDrag = riadok v sekcii SPLNENÉ
  function renderCompBlock(comp, opts = {}) {
    const noDrag = !!opts.noDrag;
    const dimStyle = comp.done_today && !editMode ? { background: "var(--page-grad)" } : undefined;
    if (noDrag) {
      return (
        <List className="bz-done-enter" style={dimStyle}>
          <ListRow onClick={() => setOpenComp(comp)} style={dimStyle} {...compRowProps(comp)} />
        </List>
      );
    }
    // wt34 — TOP-LEVEL súťaž sa ťahá cez PER-ROW dnd (renderCompGroupRow: data-taskid =
    // COMP_DRAG + comp.id), NIE cez blockDnd — inak sa nedá pretiahnuť DO skupiny (blockDnd/
    // computeBlockDrop len prehadzuje bloky, nikdy nenastaví category_id). Zrkadlí výzvu
    // (renderChalBlock → renderChalRow). data-blockkey ostáva, aby sa ostatné bloky vedeli
    // usporiadať voči súťaži cez blockDnd; celú kartu skryjeme počas ťahu triedou bz-drag-src.
    const encoded = COMP_DRAG + comp.id;
    return (
      <List data-blockkey={"comp:" + comp.id} className={encoded === draggingId ? "bz-drag-src" : undefined} style={{ ...(dimStyle || {}), transform: "translateZ(0)" }}>
        {renderCompGroupRow(comp)}
      </List>
    );
  }
  // wt34 — súťaž ako riadok V KARTE SKUPINY (mirror renderChalRow): per-row dnd (data-taskid =
  // COMP_DRAG + comp.id → identita nekoliduje s task_id), tap → detail. Ťahá sa do inej skupiny /
  // von / v rámci skupiny; perzistencia v commitDrop cez comp-vetvu (NIE tasks/reorder).
  function renderCompGroupRow(comp) {
    const encoded = COMP_DRAG + comp.id;
    const dimRow = comp.done_today && !editMode ? { background: "var(--page-grad)" } : undefined;
    return (
      <ListRow key={"gc" + comp.id} data-taskid={encoded}
        className={[comp.id === flashId ? "bz-flash" : "", encoded === draggingId ? "bz-drag-src" : ""].filter(Boolean).join(" ") || undefined}
        {...dnd.handlers}
        onClick={swallow(dnd.fired, () => setOpenComp(comp))}
        style={dimRow}
        {...compRowProps(comp)} />
    );
  }
  // placeholder medzera na cieľovom mieste dropu
  function DropPh() { return <div className="bz-drop-ph" aria-hidden="true" />; }

  const last7 = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const k = d.toISOString().slice(0, 10);
    last7.push({ key: k, label: ["Ne", "Po", "Ut", "St", "Št", "Pi", "So"][d.getDay()],
      pts: k === todayKey() ? todayPts : (s.history[k] || 0),
      mood: k === todayKey() ? s.mood : (s.moodHistory[k] || null),
      note: k === todayKey() ? why : ((s.moodNotes && s.moodNotes[k]) || "") });
  }
  // E1 — jeden zdroj pravdy pre „najčastejšiu náladu": 7-dňové kĺzavé okno vrátane dnes (last7),
  // rovnaké v dlaždici Štatistík aj v modale Súhrn nálad (predtým dlaždica rátala z weekAgoK+moodHistory
  // s UTC posunom → 8-dňové okno bez dnes → nesúhlasila s modalom).
  const mood7Counts = {};
  last7.forEach((d) => { if (d.mood) mood7Counts[d.mood] = (mood7Counts[d.mood] || 0) + 1; });
  const mood7Top = Object.entries(mood7Counts).sort((a, b) => b[1] - a[1])[0];  // [id, count] | undefined
  const activeDays = (s.doneDays || []).length + (todayPts > 0 && !(s.doneDays || []).includes(todayKey()) ? 1 : 0);
  // v16 §6 — table (week) header: total completions this week + a date-range label
  const SK_MONTHS = ["január", "február", "marec", "apríl", "máj", "jún", "júl", "august", "september", "október", "november", "december"];
  const _wkA = new Date(last7[0].key), _wkB = new Date(last7[6].key);
  const weekRangeLabel = _wkA.getMonth() === _wkB.getMonth()
    ? `${_wkA.getDate()}.–${_wkB.getDate()}. ${SK_MONTHS[_wkB.getMonth()]}`
    : `${_wkA.getDate()}. ${SK_MONTHS[_wkA.getMonth()]} – ${_wkB.getDate()}. ${SK_MONTHS[_wkB.getMonth()]}`;
  const moodFace = (id, size = 24) => <MoodFace k={moodIconName(id)} size={size} />;

  // v31 — „dlaždica dňa": X/Y splnené + sparkle ikona + JEDNA motivačná veta od AI (coach,
  // v štýle zvolenej osobnosti). Kým sa AI veta načíta, ukáž jednoduchý fallback podľa stavu dňa.
  const _gTot = dueTasks.length;
  // v32.6 — dlaždica dňa BEZ pozadia: veľké X/Y vľavo (+ malé % úspešnosti vedľa),
  // pod tým sparkle vo farbe témy a hneď vedľa AI veta.
  // v32.10 — ROVNAKÝ element na Dnes aj v Prehľade (biela karta odišla).
  const _coachNode = _gTot === 0 ? "dnes nič naplánované"
    : coach
      ? <span>{coach}{s.ai_character_name ? <span style={{ color: "var(--neutral-text-subtle)", fontWeight: 700, whiteSpace: "nowrap" }}> — {s.ai_character_name}</span> : null}</span>
      : (allDone ? "všetko splnené — super deň!" : "pekný deň — daj tomu dnes všetko");
  const _pctToday = _gTot > 0 ? Math.round((100 * doneCount) / _gTot) : null;
  // v33i — koučovský riadok (sparkle + AI veta) je teraz zdieľaný medzi PRO dlaždicou dňa
  // a Lite hlavičkou „Dnes" (task #2 — Lite ukazuje presne ten istý prvok, žiadne body/X-Y).
  const _coachRow = _gTot > 0 ? (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12 }}>
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}><BuzerIcon name="sparkles" size={17} color="var(--brand-primary)" /></span>
      <span className="bz-aitext" style={{ minWidth: 0 }}>{_coachNode}</span>
    </div>
  ) : null;
  // v32.21 — štatistika dňa: prstenec pokroku (24/30 + %) vľavo, veľké % + „ešte N do plného dňa" vpravo
  // v32.17 — návrat pôvodnej hlavičky Dnes: veľké serif X/Y (splnené/naplánované) + malé %,
  // pod tým AI koučovská veta. Bez prstenca (celebračný deň-kruh ostáva pre stav „všetko hotové").
  const dayTile = s.lite_mode ? null : (() => {
    const pct = _pctToday || 0;
    return (
      <div data-tour="hero" style={{ margin: "12px 4px 25px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "var(--ff-display)", fontWeight: 500, fontSize: 60, lineHeight: 1, letterSpacing: "-.02em" }}>
            <span style={{ color: "var(--neutral-text-main)" }}>{doneCount}</span>
            <span style={{ color: "var(--neutral-text-subtle)" }}>/{_gTot}</span>
          </span>
          {_pctToday != null && (
            <span style={{ fontFamily: "var(--ff-mono)", fontWeight: 500, fontSize: 16, color: "var(--neutral-text-muted)" }}>{pct} %</span>
          )}
        </div>
        {_coachRow}
      </div>
    );
  })();
  // v33i — Lite hero dlaždica (task #2): žiadne X/Y ani body, len „Dnes" + guľôčka vo farbe témy
  // a pod tým presne ten istý koučovský riadok ako PRO (_coachRow vyššie).
  const liteDayTile = s.lite_mode ? (
    <div data-tour="hero" style={{ margin: "12px 4px 30px" }}>
      {/* v33h — „Dnes." (bodka vo farbe témy); pri perfektnom dni „Perfektný deň." */}
      <span key={allDone ? "perf" : "dnes"} className={allDone ? "bz-perfpop" : undefined}
        style={{ display: "inline-block", fontFamily: "var(--ff-display)", fontWeight: 700, fontSize: 42, lineHeight: 1.05, letterSpacing: "-.02em", color: "var(--neutral-text-main)" }}>
        {allDone ? "Perfektný deň" : "Dnes"}<span style={{ color: "var(--brand-primary)" }}>.</span>
      </span>
      {_coachRow}
    </div>
  ) : null;
  // šípka Dnes⇄Prehľad (nesie e2e stats-row); na Dnes ukazuje vpravo, v Prehľade späť
  // v32.9 — na Dnes ikona grafov (chart) namiesto šípky; v Prehľade šípka späť
  const page = (
    <>
      {capUpsell && <CapUpsellSheet kind={capUpsell} onClose={() => setCapUpsell(null)} />}
      {/* v25 §7.2 — comeback banner zjednotený s ostatnými (HintBanner); dismiss logika ostáva
          v33 — patril do rodiny „hint/notification banners žijú len na Dnes" nižšie, ale chýbal mu
          isList guard → unikal aj do Prehľadu. */}
      {isList && s.lapsed && comebackDismissed !== (s.lapsedSince || "?") && !s.lite_mode && (
        <HintBanner icon="wave" title="Séria sa prerušila — nič sa nedeje"
          desc={<>Žiadne výčitky. {s.bestStreak > 0 ? <>Tvoj rekord <strong>{s.bestStreak} dní</strong> ti nikto nezoberie — </> : ""}stačí dnes splniť <strong>jeden</strong> návyk a rozbehneš novú sériu.</>}
          onDismiss={() => { const k = s.lapsedSince || "?"; try { localStorage.setItem("buzer_comeback_off", k); } catch { /* */ } setComebackDismissed(k); }} />
      )}
      {/* v33 — level-up je teraz samostatný farebný detail-modal (overlay, nie banner v toku
          stránky) — dlaždica dňa sa preto nikdy neposunie; otvára sa s odstupom (pozri effect
          vyššie), zavretím sa len schová (notifikácia v zvončeku ostáva, dá sa otvoriť znova). */}
      {levelUp && <LevelUpModal idx={levelUp} onClose={() => setLevelUp(null)} />}
      {/* v33h — Lite nemá oslavný burst pri all-done (nahradila ho animácia nadpisu „Perfektný deň.") */}
      {(celebrate || levelUp) && !s.lite_mode && <EffectBurst effect="confetti" />}
      {dayRing && <DayRingModal snapshot={dayRing} lite={!!s.lite_mode} onClose={() => setDayRing(null)} />}
      {/* f-batch — jediný efekt splnenia (návyk, hocikde): rozpŕsknuté častice v mieste ťuku,
          tvar/farba podľa kúpeného s.done_effect (Obchod „po splnení návyku") — nikdy nepadá zhora. */}
      {burstFx && <CompletionBurst key={burstFx.key} effect={s.done_effect} rect={burstFx.rect} onDone={clearBurst} />}
      {/* v24 — doplnenie/úprava nálady (klik na bunku nálady v tabuľke) */}
      {moodEdit && (
        <Modal center noKbPin title={"Nálada · " + fmtDuePretty(moodEdit)} icon="feather" onClose={() => setMoodEdit(null)} maxWidth={420}>
          {/* D2 — presne ten istý modal ako denný „Ako sa dnes cítiš?" (center + noKbPin) a ten istý
              MoodPicker; jediný rozdiel je titulok s dátumom (spätný zápis s explicitným date). */}
          <MoodPicker selected={s.moodHistory[moodEdit] || null} onPick={(id) => pickMoodFor(moodEdit, id)}
            note={moodEditNote} onNote={setMoodEditNote} onSave={() => saveMoodNoteFor(moodEdit)}
            saveLabel="Uložiť" saveDisabled={!s.moodHistory[moodEdit]}
            noteDesc="Zapíš, prečo sa tak cítiš (nepovinné)." />
        </Modal>
      )}
      {/* Install / Lite / Notifications hints — all share HintBanner so they're consistent by design */}
      {/* v2 — hint/notification banners live only on Dnes (isList), not Prehľad */}
      {isList && !installHid && (
        <HintBanner icon="add" title="Pridaj si appku na plochu"
          desc="Otvor Buzerlístok jedným klikom a zapni upozornenia."
          actionLabel="Pridať" onAction={() => setShowInstall(true)} onDismiss={hideInstallHint} />
      )}
      {showInstall && <InstallGuide onClose={() => setShowInstall(false)} />}

      {s.lite_mode && isList && !liteCardHid && (
        <HintBanner icon="leaf" title="Žiadne sledovanie"
          desc="V Lite režime neukazujeme body ani série. Len ty a tvoj deň, vlastným tempom."
          onDismiss={() => { setLiteCardHid(true); try { localStorage.setItem("buzer_lite_card_hid", "1"); } catch (e) { /* */ } }} />
      )}

      {isList && !notifHid && notifCanAsk && (
        <HintBanner icon="bell" title="Zapni si upozornenia"
          desc="Nezmeškáš pripomienky ani správy od priateľov."
          actionLabel="Povoliť" onAction={allowNotifs} actionBusy={notifBusy} onDismiss={hideNotifHint} />
      )}

      {/* v26.3 §4 — tip dňa (ráno po otvorení; zmizne po ✕, znova až na druhý deň) */}
      {isList && s.daily_tip && !tipHid && (
        <HintBanner icon="bulb" title="Tip dňa" desc={s.daily_tip} descClassName="bz-aitext" onDismiss={hideTip} />
      )}

      {/* objaviteľnosť presúvania ťahaním (jednorazový, dismissnuteľný); ostáva viditeľný aj v edit
          móde (Upraviť v Týždni) — banner sa už neschováva pri prepnutí do úprav */}
      {isList && !lpHintHid && displayTasks.length > 0 && (
        <HintBanner icon="pin" title="Presúvaj podržaním" desc="Podrž návyk, skupinu či výzvu a potiahni na nové miesto." descClassName="bz-aitext" onDismiss={hideLpHint} />
      )}

      {/* v27 §2 — starostlivý check-in od AI ako karta (premium má tlačidlo do AI vlákna) */}
      {isList && careNotif && (
        <HintBanner icon="ai" title={s.ai_character_name || "Tvoja AI"} desc={careNotif.body}
          actionLabel={careNotif.link && careNotif.link.startsWith("ai:") ? "Porozprávame sa?" : undefined}
          onAction={careNotif.link && careNotif.link.startsWith("ai:")
            ? () => onNotifNavigate && onNotifNavigate(careNotif.link) : undefined}
          onDismiss={hideCare} />
      )}

      {/* v18 §4 — pending friend requests as a consistent notification banner → Priatelia/Ľudia */}
      {isList && s.drienok_welcome && (
        <div className="bz-hint">
          {/* v34 — Drienok privítanie používa ROVNAKÉ triedy ako ostatné notifikácie (konzistencia) */}
          <div className="bz-hint__row" style={{ alignItems: "flex-start" }}>
            <span className="bz-hint__icon" style={{ fontSize: 20 }}>🌸</span>
            <div className="bz-hint__body">
              <span className="bz-row__title" style={{ display: "block", marginBottom: 4 }}>Vitaj v rodine, {(s.user && s.user.name) || ""}!</span>
              <span className="bz-aitext">Sme radi, že si tu. Nenechaj si ujsť našu aktuálnu <strong>Drienok výzvu</strong> – je to najlepší spôsob, ako spoznať našu komunitu a začať naplno. Pridáš sa k nám?</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Button variant="primary" block onClick={async () => { await api("/api/drienok/dismiss", { method: "POST", body: { optout: false } }); reload(); }}>Áno, idem do toho</Button>
            <Button variant="ghost" onClick={async () => { await api("/api/drienok/dismiss", { method: "POST", body: { optout: true } }); reload(); }}>Nie, ďakujem</Button>
          </div>
        </div>
      )}
      {isList && (s.friend_requests || 0) > 0 && (
        <HintBanner icon="addfriend"
          title={s.friend_requests === 1 ? "Nová žiadosť o priateľstvo" : `${s.friend_requests} nové žiadosti o priateľstvo`}
          desc="Niekto ťa chce pridať medzi priateľov."
          actionLabel="Zobraziť" onAction={() => onGoFriends && onGoFriends()} />
      )}
      {/* #2a — challenge/competition invites: same HintBanner design + top-stack placement as the
          other Dnes notifications (was a distinct plum st.task card below the hero title). */}
      {isList && (s.challenge_invites || []).map((ci) => (
        <div key={ci.cid} data-chalinvite={ci.cid}>
          <HintBanner icon={<Glyph icon={ci.icon} size={19} fallback="challenge" />}
            title={ci.name}
            desc={`${ci.from_name} ťa pozýva do ${ci.is_competition ? "súťaže" : "výzvy"}`}
            actionLabel="Detail" onAction={() => setInviteDetail(ci.cid)}
            onDismiss={() => setDeclineAsk(ci)} dismissTitle="Odmietnuť" />
        </div>
      ))}

      {/* Globálne výzvy dávky sa NEohlásia bannerom — namiesto toho ich ukáže launch modal
          „Zrkadlová dvojtýždňovka" (GlobalLaunchModal nižšie), keď je dávka odoslaná. */}

      {/* PRO hlavička (non-Lite, podľa mockupu majiteľky): DNES·deň + veľké X/Y + % splnených
          + riadok séria·level·body s chevronom = preklik na tabuľkový prehľad (a späť tým istým
          riadkom s otočeným chevronom). Segmented Zoznam⇄Týždeň aj oko z hlavičky MIZNÚ —
          skrytie hotových ďalej funguje (escape hatch „Skrytých hotových — zobraziť" v zozname),
          umiestnenie ovládača dodá majiteľka. Lite ostáva kompletne po starom. */}
      {/* v30 — reorganizácia hlavičky: „DNES · deň"/„PREHĽAD" žije hore v TopBare (namiesto
          wordmarku), riadok séria·level·body zmizol a šípka na Prehľad/späť sa presunula
          hore k veľkému číslu (ten istý data-testid="stats-row"). */}
      {/* v31.1 — PRO hlavička (veľké X/Y + „b. dnes" + šípka) zrušená: navigáciu Dnes⇄Prehľad
          aj tour/e2e kotvy prevzala široká „dlaždica dňa" nižšie. Lite ostáva po starom. */}
      {/* v33h — Lite dátum/„PREHĽAD" + LITE štítok žije v TopBare (App.jsx), nie v tele stránky. */}
      {/* v31.3 — Dnes (PRO): dlaždica dňa (normálna karta, nad Dni v sérii) + strip: šípka vpravo.
          v32.17 — keď je všetko povinné splnené, „úvodná" dlaždica sa zväčší na oslavný deň-kruh. */}
      {isList && (<>
        {/* v32.22 — perfektný deň: keď je všetko splnené a hotové sú skryté (oko OFF), úvodná dlaždica
            sa zväčší na oslavný deň-kruh. Keď užívateľ klikne oko (zobrazí hotové), vráti sa bežná dlaždica
            a splnené návyky sa ukážu na svojich miestach v zozname. Lite oslavný kruh nemá — vždy ostáva
            pri jednoduchej „Dnes" dlaždici (task #2). */}
        {!s.lite_mode && allDone && !habitTable && hideDone ? (
          /* v33h — ťuk na deň-kruh otvorí zdieľanie perfektného dňa */
          <div className="bz-perfday-card" role="button" tabIndex={0} title="Ťukni a pochváľ sa perfektným dňom"
            onClick={() => setPerfectShareOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPerfectShareOpen(true); } }}
            style={{ background: "transparent", border: "none", padding: "8px 0 6px", marginBottom: 14, boxShadow: "none", cursor: "pointer" }}>
            <DayRing tasks={dueTasks} justCompletedId={null} lite={!!s.lite_mode} instant
              todayPts={todayPts} dayMax={dayMax} doneCount={doneCount} allDone={allDone} perfectStreak={s.perfectStreak || 0} />
          </div>
        ) : (s.lite_mode ? liteDayTile : dayTile)}
        {/* v33i — task #3: Lite ovládacia lišta zdieľa PRESNE tie isté ikonové tlačidlá ako PRO
            (nálada / oko / Zoznam⇄Týždeň) namiesto pôvodnej ceruzky+undo; úprava tabuľky funguje
            aj naďalej cez „Upraviť" nad samotnou tabuľkou (rovnaký mechanizmus ako PRO). */}
        <div className="bz-homectrl">
          {/* v32.22 — vľavo: emoji nálady + oko (skryť/zobraziť splnené); vpravo: Zoznam ⇄ Týždeň switch.
              Šípka na Prehľad odišla — Prehľad je samostatný tab v spodnej lište. */}
          <span className="bz-homectrl__slot" style={{ gap: 8 }}>
            {/* v32.15 — emoji pulzuje, KÝM nie je zaznačená nálada; v33h — po zaznačení: plná farba
                témy (primary) + biela ikona */}
            <IconButton variant={s.mood ? "primary" : "neutral"} size="sm" aria-label="Ako sa dnes cítiš?" title="Ako sa dnes cítiš?"
              className={s.mood ? undefined : "bz-pulse-attn"}
              data-tour="mood" onClick={() => setMoodModal(true)}>
              <MoodFace k={moodIconName(s.mood || "fajn")} size={18} color={s.mood ? "var(--brand-on-primary)" : "var(--neutral-text-main)"} />
            </IconButton>
            {/* v32.22 — oko: skryť/zobraziť splnené návyky (len Zoznam); v Týždeň mriežke sa vždy ukáže všetko */}
            {!habitTable && (
              <IconButton variant="neutral" size="sm" icon={hideDone ? "eyeoff" : "eye"}
                aria-label={hideDone ? "Zobraziť splnené" : "Skryť splnené"}
                title={hideDone ? "Zobraziť splnené" : "Skryť splnené"} onClick={() => setHideDone((v) => !v)} />
            )}
          </span>
          <span className="bz-homectrl__center" />
          <span className="bz-homectrl__slot bz-homectrl__slot--end">
            {/* v32.8 — Zoznam ⇄ Týždeň: malý tichý switch (pozadie appky + border, aktívny segment
                vo farbe icon buttonov); tá istá týždenná tabuľka ako v histórii, bez Prehľadu. */}
            <Segmented className="bz-seg--quiet" value={habitTable}
              options={[
                { value: false, label: <BuzerIcon name="list" size={15} color="currentColor" /> },
                { value: true, label: <BuzerIcon name="grid" size={15} color="currentColor" /> },
              ]}
              onChange={(v) => {
                // v34 — prepnutie na Zoznam s neuloženými úpravami tabuľky → najprv sa spýtaj
                if (!v && guardTableLeave(() => { setEditMode(false); setHabitTable(false); })) return;
                if (!v && editMode) revertTableEdits();
                setEditMode(false); setHabitTable(v);
              }} />
          </span>
        </div>
      </>)}
      {/* v31.6 — rýchla nálada zo smajlíka v stripe: emoji picker + „prečo" v jednom modáli;
          po výbere sa miniatúra ikony (v stripe) aktualizuje na zvolenú náladu (cez s.mood). */}
      {moodModal && (
        <Modal center noKbPin title="Ako sa dnes cítiš?" icon="feather" onClose={() => setMoodModal(false)} maxWidth={420}>
          {/* D2 — zdieľaný MoodPicker (referenčný v33h dizajn); denný zápis + zatvorenie po „Hotovo" */}
          <MoodPicker selected={s.mood} onPick={pickMood}
            note={why} onNote={setWhy} onSave={() => { saveWhy(); setMoodModal(false); }}
            saveLabel="Hotovo" saveDisabled={!s.mood}
            noteDesc="Zapíš, prečo sa tak cítiš (nepovinné)." />
        </Modal>
      )}
      {/* v31.4 — Prehľad (PRO): dlaždica dňa hore (rovnaké miesto ako v Dnes, nad navigáciou,
          ostáva vo všetkých sekciách), potom hlavička: riadok = navigácia (bodky) + šípka späť,
          nadpis sekcie až v ďalšom riadku. */}
      {/* v32.16 — navigácia sekcií = pilulkové taby s ikonou (podľa mocku)
          v32.22 — Prehľad je samostatný tab: pilulky sú na plnú šírku (vlastný riadok), tlačidlo
          Zdieľať je pod nimi a len v sekcii Štatistiky. V zdieľacom móde ikonu nahradí „Odoslať". */}
      {isTable && (
        <div style={{ marginBottom: 4 }}>
          {/* v33i (task #4) — Lite dostáva ten istý pilulkový prepínač, len bez Odznakov */}
          <PrehladSwitch sections={s.lite_mode ? LITE_PREH_SECTIONS : PREH_SECTIONS} value={prehSec} onChange={changeSec} />
          {prehSec === "stats" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: shareMode ? "space-between" : "flex-end", gap: 8, margin: "0 2px 8px" }}>
              {/* v32.22 — distinct key-e, aby React nerecykloval to isté <button> medzi režimami
                  (recyklovaný uzol si niesol „prischnutý" :hover → Zdieľať ostávalo vysivené) */}
              {shareMode ? (
                <>
                  <Button key="cancel" variant="ghost" size="sm" onClick={exitShare}>Zrušiť</Button>
                  <Button key="send" variant="primary" size="sm" disabled={!shareSel.length} onClick={() => setShareOpen(true)}
                    title="Odoslať vybrané štatistiky do chatu">
                    Odoslať{shareSel.length ? ` (${shareSel.length})` : ""} <Icon name="chevron" size={14} color="currentColor" />
                  </Button>
                </>
              ) : (
                <Button key="share" variant="ghost" size="sm" onClick={() => { setShareMode(true); setShareSel([]); }} title="Zdieľať štatistiky">
                  <BuzerIcon name="share" size={15} color="currentColor" /> Zdieľať
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {/* v33i — task #3: samostatná Lite ovládacia lišta (ceruzka+undo+textové Zoznam/Týždeň) zrušená —
          Lite teraz zdieľa presne tú istú lištu ako PRO (bz-homectrl vyššie, v isList bloku). */}

      {/* v20 §1 — denná štatistika presunutá z Dnes do Prehľad (DNES + CELKOVO karty nižšie) */}

      {inviteDetail != null && (
        <ChallengeInfoModal cid={inviteDetail} me={s.user} onClose={() => setInviteDetail(null)}
          onJoin={(id) => joinCh(id)} onDecline={(id) => setDeclineAsk((s.challenge_invites || []).find((x) => x.cid === id) || { cid: id })} joinBusy={joinBusy}
          invitedBy={(s.challenge_invites || []).find((x) => x.cid === inviteDetail)?.from_name} />
      )}
      {/* In-app launch modal novej globálnej dávky (napr. „Zrkadlová dvojtýždňovka") — ukáž raz, keď
          je dávka odoslaná (s.global_launch). Zavretie: dismissne zvyšné výzvy + ak sa user do ničoho
          nezapojil, pripomeň, že výzvy nájde v Priatelia → Zapoj sa. */}
      {s.global_launch && !launchClosed && (
        <GlobalLaunchModal launch={s.global_launch}
          onJoin={(cid) => joinCh(cid)}
          onClose={async () => {
            const anyJoined = (s.global_launch.challenges || []).some((c) => c.joined);
            setLaunchClosed(true);
            try { await api("/api/global-launch/dismiss", { method: "POST" }); } catch (e) { /* best-effort */ }
            if (!anyJoined) showToast("Výzvy nájdeš v Priatelia → Zapoj sa.", 4000);
            reload();
          }} />
      )}
      {/* #2a — confirm before declining an invite (from the detail modal) */}
      {declineAsk && (
        <Sheet center title="Odmietnuť pozvánku?" icon={<Glyph icon={declineAsk.icon} size={20} fallback="challenge" />} onClose={() => setDeclineAsk(null)}
          footer={<>
            <Button variant="ghost" onClick={() => setDeclineAsk(null)}>Späť</Button>
            <Button variant="destructive" block onClick={() => { const id = declineAsk.cid; setDeclineAsk(null); setInviteDetail(null); declineCh(id); }}><Icon name="close" size={16} color="currentColor" />Odmietnuť</Button>
          </>}>
          <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5 }}>Naozaj sa nechceš pridať do {declineAsk.is_competition ? "súťaže" : "výzvy"}{declineAsk.name ? <> <strong>{declineAsk.name}</strong></> : ""}? Pozvánka zmizne z Dnes.</div>
        </Sheet>
      )}

      {/* v34 — neuložené úpravy tabuľky (Týždeň · edit mód): pri odchode sa spýtaj, či uložiť */}
      {leaveGuard && (
        <Sheet center title="Uložiť zmeny?" icon="pencil" onClose={() => setLeaveGuard(null)}
          footer={<>
            <Button variant="ghost" onClick={() => { const p = leaveGuard.proceed; setLeaveGuard(null); revertTableEdits(); setEditMode(false); p(); }}>
              <BuzerIcon name="undo" size={16} color="currentColor" />Zahodiť
            </Button>
            <Button variant="primary" block onClick={async () => { const p = leaveGuard.proceed; setLeaveGuard(null); await commitTableEdits(); p(); }}>
              <BuzerIcon name="check" size={16} color="currentColor" />Uložiť
            </Button>
          </>}>
          <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5 }}>Máš neuložené úpravy v tabuľke. Chceš ich pred odchodom uložiť?</div>
        </Sheet>
      )}

      {/* v20 §2 — AI denný citát presunutý úplne na koniec Dnes (nižšie) */}
      {openChallenge && (
        <Sheet center icon={<Glyph icon={openChallenge.icon} size={20} fallback="flag" />} subtitle="Výzva · rebríček" title={openChallenge.label}
          headerRight={<ChallengeChatButton cid={openChallenge.cid} name={openChallenge.label} me={s.user} />}
          onClose={() => setOpenChallenge(null)}>
          <ChallengeBoard cid={openChallenge.cid} reload={reload} inModal lite={!!s.lite_mode} refreshKey={chalRefreshKey}
            onRequestUncheck={() => { const bt = shownChallenges.find((t) => t.challenge_id === openChallenge.cid); if (bt) setUncheckTask(bt); }}
            onChanged={() => { setOpenChallenge(null); reload(); }} />
        </Sheet>
      )}
      {openComp && (
        <Sheet center title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}><span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{openComp.name}</span><Tag tone="brand" size="xs">VÍŤAZÍ SÚČET</Tag></span>} icon={<Glyph icon={openComp.icon} size={20} fallback="trophy" />} subtitle="Výzva · rebríček"
          headerRight={<ChallengeChatButton cid={openComp.id} name={openComp.name} me={s.user} />}
          onClose={() => setOpenComp(null)}>
          <CompetitionDetail comp={openComp} reload={reload} onClose={() => setOpenComp(null)} />
        </Sheet>
      )}

      {/* v22.7 — Lite Prehľad kedysi malo vlastnú súhrn-nálad+séria kartu tu; task #4 (v33i) ju
          nahradilo zdieľanou StatTile mriežkou nižšie (rovnaká dlaždicová sústava ako PRO, len
          zúžená sada dlaždíc: séria, perfektné dni, nálada + kvet kategórií + Zdieľať). */}

      {/* v31.1 — guličkový prepínač sekcií presunutý do stripu pod dlaždicou dňa (vyššie) */}

      {/* v30 — Prehľad úvodné štatistiky podľa mockupu majiteľky: StatTile dlaždice
          (chip + veľké číslo + label + ľudský podtitul + progres) vo farbách
          per-témovej palety --stat-flame/--stat-growth/--stat-sun.
          Poradie: séria (hero) → perfektné dni + nálada → rebríček + level. */}
      {isTable && prehSec === "stats" && (() => {
        const dniText = (n) => (n === 1 ? "deň" : n >= 2 && n <= 4 ? "dni" : "dní");
        // séria vs. rekord
        const best = s.bestStreak || 0;
        const serSub = s.streak <= 0
          ? "splň dnes aspoň jeden návyk a rozbehni sériu"
          // Lite = pokojnejší tón (bez „nezastavuj!", nech to netlačí)
          : (s.streak >= best ? (s.lite_mode ? "toto je tvoj rekord" : "toto je tvoj rekord — nezastavuj!") : `ešte ${best - s.streak} ${dniText(best - s.streak)} a prekonáš svoj rekord`);
        const serPct = best > 0 ? (100 * Math.min(1, s.streak / best)) : (s.streak > 0 ? 100 : 0);
        // perfektné dni (celkový počet; dnešok sa pripočíta, keď je všetko splnené)
        // v32.17 — dlaždica ukazuje PODIEL perfektných dní zo všetkých aktívnych; presné
        // počty (koľko aktívnych, koľko z toho perfektných) sú v podtitule.
        const perf = (s.perfectDays || 0) + (allDone ? 1 : 0);
        const perfPct = activeDays > 0 ? Math.min(100, Math.round((100 * perf) / activeDays)) : 0;
        // v33h — dlaždica ukazuje POČET perfektných dní; podiel z aktívnych je v podtitule.
        const perfSub = activeDays > 0
          ? `${perfPct} % z ${activeDays} aktívnych dní`
          : "zatiaľ žiadny aktívny deň";
        // najčastejšia nálada za POSLEDNÝCH 7 DNÍ (kĺzavé okno, bez pondelkového resetu) + prezývka
        // E1 — jeden zdroj pravdy: mood7Top (z last7, vrátane dnes) — rovnaké okno ako modal Súhrn nálad
        const MOOD_WORD = { skvele: "šťastlivec", fajn: "pohodička", neutral: "rovnováha", vykyvy: "hojdačka", tazko: "bojovník" };
        const topW = mood7Top;  // [id, count] | undefined
        // v31 — jeden zdroj pravdy pre obidva pohľady (dlaždice aj bubliny)
        // v31.7 — poradie: séria → perfektné dni → nálada → rebríček → level → odznaky → víťazstvá → body
        // v32 — dlaždice bez vlastnej navigácie sa dajú zdieľať do chatu (klik → ShareStatSheet);
        // share = serializovateľná podoba dlaždice (názov ikony zo sady namiesto React uzla)
        // task #4 (v33i) — Lite Štatistiky ukazujú len sériu, perfektné dni a náladu (+ kvet
        // kategórií a Zdieľať nižšie, mimo tejto mriežky) — žiadny rebríček/level/odznaky/body.
        const stats = [
          // v33 — dlaždica zúžená z celého riadku na štandardnú šírku (3 v rade); badge
          // ukazuje, či je dnešná séria už predĺžená (streakExtendedToday z /api/state)
          { key: "streak", accent: "var(--stat-flame)", tour: "hero-seria",
            icon: <Icon name="streak" size={24} color="currentColor" />,
            badge: s.streakExtendedToday ? <Tag tone="brand" size="xs" icon="check">dnes</Tag> : null,
            share: { icon: "flame", value: s.streak, label: "Dni v sérii", sub: serSub, accent: "var(--stat-flame)" },
            value: s.streak, short: s.streak, label: "Dni v sérii", sub: serSub, pct: serPct },
          { key: "perf", accent: "var(--stat-growth)",
            icon: <Icon name="target" size={24} color="currentColor" />,
            share: { icon: "target", value: perf, label: "Perfektné dni", sub: perfSub, accent: "var(--stat-growth)" },
            value: perf, short: perf, label: "Perfektné dni", sub: perfSub,
            pct: perfPct },
          { key: "mood", accent: "var(--stat-sun)",
            // v33h — „Súhrn nálad" otvárajú aj PRO používatelia (nielen Lite)
            title: "Súhrn nálad",
            onClick: () => setStatModal("mood"),
            icon: topW ? <MoodFace k={moodIconName(topW[0])} size={26} color="currentColor" /> : <MoodFace k="neutral" size={26} color="currentColor" />,
            share: topW ? { icon: "smile", value: MOOD_WORD[topW[0]] || "", label: "Najčastejšia nálada", sub: `${topW[1]}× za posledných 7 dní`, accent: "var(--stat-sun)" } : null,
            value: topW ? <span className="bz-stattile__word">{MOOD_WORD[topW[0]] || ""}</span> : "—",
            short: null, label: "Najčastejšia nálada",
            sub: topW ? `${topW[1]}× za posledných 7 dní` : "zaznač si dnešnú náladu",
            pct: topW ? (100 * topW[1]) / 7 : 0 },
          ...(s.lite_mode ? [] : (() => {
            // rebríček týždňa
            const entries = (league && league.entries) || [];
            const meE = entries.find((e) => e.is_me);
            let rVal = "—", rSub = "pridaj si priateľov a súťažte spolu", rPct = 0;
            if (league == null) rSub = "načítavam…";
            else if (meE && entries.length > 1) {
              rVal = `#${meE.rank}`;
              const leader = entries[0];
              rPct = leader && leader.score > 0 ? (100 * meE.score) / leader.score : 0;
              if (meE.rank === 1) {
                const second = entries[1];
                rSub = second ? `vedieš — ${second.name} je ${meE.score - second.score} b. za tebou` : "vedieš!";
              } else {
                const ahead = entries[meE.rank - 2];
                rSub = ahead ? `${ahead.name} je o ${Math.max(1, ahead.score - meE.score)} b. pred tebou` : `si ${meE.rank}. z ${entries.length}`;
              }
            }
            // level
            const lvN = levelIndex(s.totalPoints) + 1;
            // odznaky (získané / celkom)
            const badgesEarned = (s.badges || []).filter((b) => b.earned).length;
            const badgesTotal = (s.badges || []).length;
            return [
              { key: "league", accent: "var(--stat-sun)", title: "Rebríček týždňa — otvoriť",
                onClick: () => setShowLeague(true),
                icon: <BuzerIcon name="medal" size={24} color="currentColor" />,
                share: { icon: "medal", value: rVal, label: "Rebríček týždňa", accent: "var(--stat-sun)" },
                value: rVal, short: rVal, label: "Rebríček týždňa", sub: rSub, pct: rPct },
              { key: "level", accent: "var(--stat-growth)", title: "Zobraziť všetky levely", tour: "level",
                onClick: () => setShowLevels(true),
                icon: <BuzerIcon name={LEVEL_ICON[lvN] || "star2"} size={24} color="currentColor" />,
                share: { icon: LEVEL_ICON[lvN] || "star2", value: lvN, label: `Level ${lvN} · ${gname(level)}`, accent: "var(--stat-growth)" },
                value: lvN, short: lvN, label: `Level ${lvN} · ${gname(level)}`,
                sub: next ? `ešte ${next.min - s.totalPoints} b. do Lv ${lvN + 1}` : "MAX úroveň 🏆",
                pct: next ? (100 * (s.totalPoints - level.min)) / (next.min - level.min) : 100 },
              { key: "badges", accent: "var(--stat-growth)", title: "Otvoriť sekciu Odznaky",
                onClick: () => setPrehSec("badges"),
                icon: <Icon name="badge" size={24} color="currentColor" />,
                share: { icon: "trophy", value: `${badgesEarned}/${badgesTotal}`, label: "Odznaky", accent: "var(--stat-growth)" },
                value: badgesEarned, short: badgesEarned, label: "Odznaky",
                sub: badgesTotal > 0 ? `z ${badgesTotal} získaných` : "získavaj plnením návykov",
                pct: badgesTotal > 0 ? (100 * badgesEarned / badgesTotal) : 0 },
              { key: "compFirst", accent: "var(--stat-flame)",
                icon: <Icon name="crown" size={24} color="currentColor" />,
                share: (s.compFirst || 0) > 0 ? { icon: "crown", value: s.compFirst, label: "Víťazstvá vo výzvach", sub: `${s.compFirst}× 1. miesto`, accent: "var(--stat-flame)" } : null,
                value: s.compFirst || 0, short: s.compFirst || 0, label: "Víťazstvá vo výzvach",
                sub: (s.compFirst || 0) === 0 ? "zatiaľ žiadne 1. miesto — zapoj sa do výzvy"
                  : `${s.compFirst}× 1. miesto`, pct: null },
              { key: "points", accent: "var(--stat-sun)",
                icon: <Icon name="points" size={24} color="currentColor" />,
                share: { icon: "star2", value: s.totalPoints, label: "Body spolu", sub: "za celý čas", accent: "var(--stat-sun)" },
                value: s.totalPoints, short: s.totalPoints, label: "Body spolu", sub: "za celý čas" },
            ];
          })()),
        ];
        // v32 — zdieľanie: podčiarknuté „Zdieľať" vpravo → režim výberu (jedna alebo viac
        // dlaždíc), potom „Odoslať (n)" otvorí ShareStatSheet. Dlaždice bez share sa stlmia.
        const toggleShareSel = (key) => setShareSel((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);
        const selectedShares = stats.filter((t) => t.share && shareSel.includes(t.key)).map((t) => t.share);
        return (
          <>
            {/* v31.1 — bublinový pohľad dočasne skrytý (kód ostáva v StatBubbles.jsx);
                zobrazujeme len dlaždice. Späť zapneš odkomentovaním prepínača + vetvy nižšie.
            <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
              <Segmented options={[{ value: "cards", label: "Dlaždice" }, { value: "bubbles", label: "Bubliny" }]}
                value={statView} onChange={setStatView} />
            </div>
            */}
            {/* v32.22 — ovládanie zdieľania (Zdieľať / Zrušiť / Odoslať) žije v riadku pod pilulkami;
                tu ostáva len nápoveda, čo vybrať */}
            {shareMode && <div style={{ ...st.fieldHint, margin: "2px 2px 8px" }}>Vyber štatistiky, ktorými sa chceš pochváliť.</div>}
            {/* v33 — centrované dlaždice sú kompaktnejšie než predošlý „chip + číslo" riadok;
                2 v rade by pôsobili príliš naťahovane, tak 3 v rade (aj séria zúžená z celého riadku) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
              {stats.map((t) => {
                const sel = shareMode && shareSel.includes(t.key);
                return (
                  <StatTile key={t.key} accent={t.accent} icon={t.icon} value={t.value}
                    label={t.label} sub={t.sub} pct={t.pct} badge={t.badge}
                    onClick={shareMode ? (t.share ? () => toggleShareSel(t.key) : undefined) : t.onClick}
                    title={shareMode ? (t.share ? "Vybrať na zdieľanie" : undefined) : t.title}
                    data-tour={t.tour}
                    style={{
                      ...(sel ? { boxShadow: "0 0 0 2.5px var(--brand-primary)" } : {}),
                      ...(shareMode && !t.share ? { opacity: 0.4 } : {}),
                    }} />
                );
              })}
              {/* v33d — „kvetina" (EnneagramChart) na KONCI mriežky, cez celú šírku: rozloženie
                  návykov podľa životných kategórií. V zdieľacom móde skrytá. */}
              {!shareMode && <EnneagramTile habits={s.habits || []} competitions={s.competitions || []} />}
            </div>
            {/* <StatBubbles stats={stats} /> */}
            {shareOpen && selectedShares.length > 0 && (
              <ShareStatSheet stats={selectedShares}
                onClose={() => { setShareOpen(false); setShareMode(false); setShareSel([]); }} />
            )}
          </>
        );
      })()}

      {statModal === "mood" && (() => {
        const counts = {};
        Object.values(s.moodHistory || {}).forEach((m) => { if (m) counts[m] = (counts[m] || 0) + 1; });
        const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        // v33h — najčastejšia nálada za posledných 7 dní (samostatná dlaždica nad celkovou)
        // E1 — zdieľaný mood7Top (rovnaké okno ako dlaždica Štatistík)
        const top7 = mood7Top;
        const recent = [...last7].reverse();
        return (
          <Modal center title="Súhrn nálad" icon="chart" iconNode={top7 ? <MoodFace k={moodIconName(top7[0])} size={20} /> : undefined} onClose={() => setStatModal(null)}>
            {top7 && (
              <div style={{ ...st.moodTop, marginBottom: 8 }}>
                <span>{moodFace(top7[0], 34)}</span>
                <div><div style={{ fontWeight: 800, fontSize: 15 }}>{MOODS.find((m) => m.id === top7[0])?.label}</div>
                  <div style={st.taskDesc}>Najčastejšia za posledných 7 dní · {top7[1]}×</div></div>
              </div>
            )}
            {top ? (
              <div style={st.moodTop}>
                <span>{moodFace(top[0], 34)}</span>
                <div><div style={{ fontWeight: 800, fontSize: 15 }}>{MOODS.find((m) => m.id === top[0])?.label}</div>
                  <div style={st.taskDesc}>Celková najčastejšia nálada · {top[1]}×</div></div>
              </div>
            ) : <div style={st.taskDesc}>Zatiaľ žiadne nálady — vyber si dnešnú náladu. 🙂</div>}
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {recent.map((d) => (
                <div key={d.key} style={st.moodSumRow}>
                  <span style={{ width: 26, textAlign: "center", flexShrink: 0, display: "inline-flex", justifyContent: "center" }}>{d.mood ? moodFace(d.mood, 20) : "·"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="bz-row__title" style={{ textTransform: "capitalize" }}>{d.label} · {fmtShortDate(d.key)}</div>
                    {d.note ? <div className="bz-aitext">„{d.note}"</div> : null}
                  </div>
                </div>
              ))}
            </div>
          </Modal>
        );
      })()}

      {/* v30 — samostatná Level karta zmizla: level žije v dlaždici so „šatkou" vyššie */}
      {isTable && !s.lite_mode && (<>
      {badgeInfo && (
          <Modal center title={badgeInfo.name} icon={badgeInfo.earned ? "party" : "lock"} maxWidth={360} onClose={() => setBadgeInfo(null)}>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>{badgeInfo.desc}</div>
            {!badgeInfo.earned && badgeInfo.target != null && badgeInfo.value != null && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, fontFamily: "var(--ff-ui)", fontWeight: 700, color: "var(--text2)", marginBottom: 5 }}>{badgeInfo.value}/{badgeInfo.target}</div>
                <div style={{ height: 6, borderRadius: 99, background: "var(--accent-tint)", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (badgeInfo.value / Math.max(1, badgeInfo.target)) * 100)}%`, background: "var(--accent-grad)" }} />
                </div>
              </div>
            )}
            <div style={{ ...st.infoNote, marginTop: 12, marginBottom: 0, display: "flex", alignItems: "center", gap: 7, ...(badgeInfo.earned ? { color: "var(--brand-primary)" } : {}) }}>
              {badgeInfo.earned
                ? <><BuzerIcon name="party" size={16} color="currentColor" /> Máš ho získaný</>
                : <><BuzerIcon name="lock" size={16} color="currentColor" /> Zatiaľ nezískaný — splň podmienku a je tvoj.</>}
            </div>
          </Modal>
        )}
      </>)}
      {showLevels && <LevelsModal points={s.totalPoints} onClose={() => setShowLevels(false)} />}
      {/* v32.21 — rebríček týždňa ako modal (klik na dlaždicu poradia v Prehľade); po zatvorení ostáva Prehľad */}
      {showLeague && (
        <Modal center title="Rebríček týždňa" icon="medal" onClose={() => setShowLeague(false)} maxWidth={480}>
          {league && (league.entries || []).length > 1
            ? <LeagueSection preloaded={league} hideHeader onOpenProfile={(e) => openProfileModal(e.id)} />
            : <div style={st.empty}>Zatiaľ nemáš s kým súťažiť — pridaj si priateľov v sekcii Priatelia a rebríček sa naplní.</div>}
        </Modal>
      )}
      {showHistory && <HistorySheet onClose={() => setShowHistory(false)} />}

      {/* v17 §1 — main content (mood + AI presunuté na koniec Dnes — §2) */}
      {(<>

      {/* task list section: competitions → habits → add → completed (H1: progress bar removed — it's in the Dnes card) */}
      {manageRoutines && <RoutineManager categories={s.categories || []} habits={s.habits || []} reload={reload} onClose={() => setManageRoutines(false)} />}
      {routineCreate && <RoutineCreate habits={s.habits || []} reload={reload} onClose={() => setRoutineCreate(false)} />}

      {/* v32.17 — perfektný deň: oslavný deň-kruh sa presunul HORE (zväčšená úvodná dlaždica,
          nad ovládačmi); nesplnené výzvy/súťaže idú do sekcie „Nepovinné" nižšie v zozname. */}

      {/* v33i — task #4: sekcia „habits" (7-dňová tabuľka + História) tu bola donedávna Lite-only
          (staré inSec bypassovalo Lite cez všetko); Lite teraz má vlastný pilulkový prepínač bez
          tejto sekcie (rovnaká 7-dňová Týždeň tabuľka + História žijú na Dnes → Zoznam⇄Týždeň,
          zdieľané s PRO od task #3), takže inSec("habits") je teraz mŕtva vetva pre oba režimy
          (rovnako ako pred touto zmenou bola už len pre PRO). */}
      {viewMode === "table" ? (inSec("habits") ? (
        <>
          {/* v32 — bez počtu splnení a bez trvalého hintu: vľavo „Upraviť" (ceruzka) zapne editáciu
              tabuľky (až potom sa ukáže hint), vpravo prelink do mesačnej Histórie */}
          {(shownTasks.length > 0 || openComps.length > 0) && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "10px 2px 8px" }}>
              <button type="button" onClick={() => setPrehTableEdit((e) => !e)}
                style={{ ...st.linkUnderline, display: "inline-flex", alignItems: "center", gap: 4 }}
                title={prehTableEdit ? "Ukončiť úpravu tabuľky" : "Upraviť tabuľku"}>
                {prehTableEdit ? "Hotovo" : "Upraviť"} <BuzerIcon name={prehTableEdit ? "check" : "pencil"} size={13} color="currentColor" />
              </button>
              <button type="button" onClick={() => setShowHistory(true)} style={{ ...st.linkUnderline, display: "inline-flex", alignItems: "center", gap: 3, flexShrink: 0 }} title="Mesačná história splnení">História <Icon name="chevron" size={13} color="currentColor" /></button>
            </div>
          )}
          {prehTableEdit && (shownTasks.length > 0 || openComps.length > 0) && (
            <div style={{ ...st.fieldHint, marginBottom: 8 }}>Klikni na deň a doplň si splnený návyk.{!s.lite_mode ? " Za predošlé dni sa body nezapočítajú, ale séria sa udrží." : ""}</div>
          )}
          <div data-tour="table" style={{ transform: "translateZ(0)" }}><HabitTable tasks={orderedForTable(shownTasks)} days={last7} onToggle={toggle} onMoodDay={openMoodEdit} editable={prehTableEdit} comps={openComps} onCompOpen={setOpenComp} /></div>
        </>
      ) : null) : (
        <div>
          {(() => {
            // v8 — group today's habits into routines (collapsible) + a standalone section
            // v9 — challenge habits (výzvy) live in the carousel only, so don't repeat them as rows
            // v32.22 — splnené návyky sa NEPRESÚVAJÚ do samostatnej sekcie: ostávajú na svojom mieste
            // v zozname (v rutine aj samostatne). „Oko" (hideDone) ich len skryje/zobrazí; mimo edit módu
            // a keď je oko zapnuté, splnené položky z renderu vypadnú (ostanú len tie práve „držané" po burste).
            const hideActive = hideDone && !editMode;
            const isDone = (t) => t.done || weekMet(t) || (t.skip_today && !t.done);
            const dueByCat = {};      // návyky viditeľné v rutine / samostatne (aktívne + zobrazené splnené)
            const masCasVisible = [];  // v33b — zriedkavé samostatné návyky do sekcie „Máš čas"
            let hiddenCount = 0;      // koľko splnených sa práve skrýva (na drobný „zobraziť" hint)
            // G2b — výzva s platnou skupinou (category_id) sa renderuje DNU v karte skupiny ako
            // bežný riadok (per-row drag). Výzva bez skupiny (alebo s neplatnou) ostáva top-level blok.
            // V Lite sú bodové výzvy skryté všade (rovnako ako doteraz), preto ich do skupín nepúšťame.
            const catIdSet = new Set((s.categories || []).map((c) => c.id));
            const chalInGroup = (t) => !!t.challenge_id && !s.lite_mode && t.category_id != null && catIdSet.has(t.category_id);
            // wt34 — súťaž s platnou (per-user) skupinou sa renderuje DNU v karte skupiny (per-row
            // drag), inak ostáva top-level blok. Na rozdiel od výziev NIE je Lite-gated: súťaže sa
            // v Lite zobrazujú, takže ich Lite používateľ smie tiež zoskupiť.
            const compInGroup = (c) => c.category_id != null && catIdSet.has(c.category_id);
            for (const t of listShownTasks) {
              if (t.challenge_id && !chalInGroup(t)) continue;
              // držané (práve splnené) úlohy renderujeme ešte pár sekúnd (burst), potom pri zapnutom oku zmiznú
              if (hideActive && isDone(t) && !heldDone.has(t.id)) { hiddenCount++; continue; }
              // v33b — „Máš čas": samostatný (mimo rutiny) zriedkavý návyk ide do vlastnej sekcie nižšie
              if (t.mas_cas && t.category_id == null) { masCasVisible.push(t); continue; }
              const k = t.category_id || "_solo";
              (dueByCat[k] = dueByCat[k] || []).push(t);
            }
            // ITEM 7a — v skupine usporiadaj návyky AJ výzvy podľa spoločného serverového `sort`
            // (s.habits príde v poradí `sort, id`), aby výzva sedela na svojom pretiahnutom mieste
            // MEDZI návykmi (predtým sa výzvy renderovali vždy pred návykmi → re-drop bol no-op).
            // _solo neriešime (samostatné bloky si poradie držia cez dnes_order nižšie).
            const taskPos = new Map((s.habits || []).map((t, i) => [t.id, i]));
            for (const k of Object.keys(dueByCat)) {
              if (k === "_solo") continue;
              dueByCat[k].sort((a, b) => (taskPos.has(a.id) ? taskPos.get(a.id) : 1e9) - (taskPos.has(b.id) ? taskPos.get(b.id) : 1e9));
            }
            // poradie sekcie „Máš čas" (mascas_order); nové návyky na koniec
            const mcPos = (() => { try { const o = JSON.parse(s.mascas_order || "[]"); return new Map(o.map((k, i) => [k, i])); } catch { return new Map(); } })();
            const masCasSorted = [...masCasVisible].sort((a, b) => {
              const pa = mcPos.has("task:" + a.id) ? mcPos.get("task:" + a.id) : 1e9;
              const pb = mcPos.has("task:" + b.id) ? mcPos.get("task:" + b.id) : 1e9;
              return pa - pb;
            });
            mcRef.current = { masCas: masCasSorted, mascas_order: s.mascas_order };
            const catHabitCount = {};
            for (const h of (s.habits || [])) { if (h.category_id != null) catHabitCount[h.category_id] = (catHabitCount[h.category_id] || 0) + 1; }
            // wt34 — súťaže patriace do skupiny (per-user), zoradené podľa sort_in_category (potom id).
            // Oko (hideActive) skryje splnené aj tu; hidden sa počíta do spoločného hiddenCount.
            const compByCat = {};
            for (const c of shownComps) {
              if (!compInGroup(c)) continue;
              if (hideActive && c.done_today && !heldDone.has(c.id)) { hiddenCount++; continue; }
              (compByCat[c.category_id] = compByCat[c.category_id] || []).push(c);
            }
            for (const k of Object.keys(compByCat)) {
              compByCat[k].sort((a, b) => (a.sort_in_category ?? 1e9) - (b.sort_in_category ?? 1e9) || a.id - b.id);
            }
            // rutina sa zobrazí, ak má dnes viditeľné návyky/súťaže ALEBO je celkom prázdna (na pridanie);
            // rutina s návykmi, ktoré sú všetky splnené a skryté, z Dnes vypadne (kým sa nezapne oko).
            const cats = (s.categories || []).filter((c) => (dueByCat[c.id] || []).length > 0 || (compByCat[c.id] || []).length > 0 || !(catHabitCount[c.id] > 0));
            const solo = dueByCat["_solo"] || [];
            // Tabuľka na Dnes: 7-dňová mriežka všetkých návykov (rovnaký HabitTable ako v Prehľade)
            if (habitTable) {
              return (
                <>
                  {/* v32.8 — riadok prebraný z Prehľadu: vľavo „Upraviť" (+ Zahodiť pri
                      nepotvrdených zmenách), vpravo „História" — nad tabuľkou
                      v33 — zjednotené s „Zdieľať" (ghost Button + ikona), nie podčiarknuté odkazy */}
                  {/* v33h — kompaktné tlačidlá (menší padding/gap/font), nech sa v edit-móde
                      (Hotovo · Zahodiť · História) nezalomia do druhého riadku na úzkych mobiloch */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "2px 2px 8px", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Button variant="ghost" size="sm" onClick={() => { if (editMode) commitTableEdits(); else setEditMode(true); }}
                        style={st.tblEditBtn} title={editMode ? "Uložiť zmeny tabuľky" : "Upraviť tabuľku"}>
                        <BuzerIcon name={editMode ? "check" : "pencil"} size={14} color="currentColor" /> {editMode ? "Hotovo" : "Upraviť"}
                      </Button>
                      {editMode && Object.keys(pendingCells).length > 0 && (
                        /* v32.9 — Zahodiť rovno vypne aj mód úpravy */
                        <Button variant="ghost" size="sm" onClick={() => { revertTableEdits(); setEditMode(false); }}
                          style={{ ...st.tblEditBtn, color: "var(--danger)" }} title="Zahodiť nepotvrdené zmeny">
                          <BuzerIcon name="undo" size={14} color="currentColor" /> Zahodiť
                        </Button>
                      )}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setShowHistory(true)} style={st.tblEditBtn} title="Mesačná história splnení">
                      <BuzerIcon name="calendar" size={14} color="currentColor" /> História
                    </Button>
                  </div>
                  {/* ITEM 14 — mimo edit módu krátky helper (pod „Upraviť"); v edit móde ho nahradí
                      návod na úpravu buniek nižšie. */}
                  {!editMode && (
                    <span style={{ ...st.taskDesc, display: "block", marginBottom: 8 }}>Potiahni doľava pre posledných 7 dní. Zapni Upraviť, ak chceš meniť už splnené.</span>
                  )}
                  {editMode && (
                    <span style={{ ...st.taskDesc, display: "block", marginBottom: 8 }}>Klikni na deň (splnené → preskočené → zmeškané). Zmeny sa uložia po potvrdení <strong>Hotovo</strong>.</span>
                  )}
                  <div data-tour="table"><HabitTable tasks={orderedForTable(shownTasks)} days={last7} onCellSet={onCellSet} pending={pendingCells} onToggle={toggle} onMoodDay={openMoodEdit} editable={editMode} comps={openComps} onCompOpen={setOpenComp} /></div>
                </>
              );
            }
            // v2 — routines + standalone habits merged into ONE ordered block list
            // (routine-task-routine possible). Order persisted per-user in dnes_order.
            const savedOrder = (() => { try { return JSON.parse(s.dnes_order || "[]"); } catch { return []; } })();
            // v26.2 §6 — výzvy/súťaže sú teraz bloky hlavného zoznamu; jednorazový merge starého chal_order
            // (ak dnes_order ešte neobsahuje žiaden chal:/comp: kľúč, priraď ich poradie na koniec).
            const hasChalKeys = savedOrder.some((k) => k.startsWith("chal:") || k.startsWith("comp:"));
            const effOrder = (!hasChalKeys && chalOrder.length) ? [...savedOrder, ...chalOrder] : savedOrder;
            const posOf = new Map(effOrder.map((k, idx) => [k, idx]));
            // v32.22 — splnené výzvy/súťaže sa (rovnako ako návyky) len skryjú okom, ostávajú na mieste
            const shownChals = [];
            // ITEM 4 — výzvy zobraz aj Lite používateľom (ako súťaže); body sa v Lite nikde neukazujú
            // (renderChalRow gate-uje +body cez !s.lite_mode). V Lite sú výzvy vždy top-level (bez skupín).
            for (const t of shownChallenges) {
              if (chalInGroup(t)) continue;   // G2b — výzva v skupine sa renderuje DNU, nie ako top-level blok
              if (hideActive && (t.done || weekMet(t)) && !heldDone.has(t.id)) { hiddenCount++; continue; }
              shownChals.push(t);
            }
            const shownCompsList = [];
            for (const c of shownComps) {
              if (compInGroup(c)) continue;   // wt34 — súťaž v skupine sa renderuje DNU (hidden už započítaný v compByCat)
              if (hideActive && c.done_today && !heldDone.has(c.id)) { hiddenCount++; continue; }
              shownCompsList.push(c);
            }
            const blocks = [
              ...cats.map((c) => ({ type: "cat", key: "cat:" + c.id, cat: c })),
              ...solo.map((t) => ({ type: "task", key: "task:" + t.id, task: t })),
              ...shownChals.map((t) => ({ type: "chal", key: "chal:" + t.challenge_id, t })),
              ...shownCompsList.map((c) => ({ type: "comp", key: "comp:" + c.id, comp: c })),
            ].sort((a, b) => (posOf.has(a.key) ? posOf.get(a.key) : 1e9) - (posOf.has(b.key) ? posOf.get(b.key) : 1e9));
            // v30 — kachličky ostávajú (bz-plain zrušený: skupina sa nedala odlíšiť od návykov)
            return (
              <>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-tour="habit">
                {blocks.map((blk) => {
                  // placeholder pred blokom: samostatný návyk (habit drag) ALEBO celý blok (block drag)
                  const phBefore = dropAt && dropAt.cat === "_solo"
                    && (dropAt.beforeId != null ? blk.key === "task:" + dropAt.beforeId : dropAt.beforeKey === blk.key);
                  const blockPh = blockDropAt && blockDropAt.beforeKey === blk.key;
                  const blockHidden = draggingBlock === blk.key;
                  // skry ťahaný blok len TRIEDOU (nie obalením do divu) — obal by zmenil strom a
                  // React by element remountoval → uvoľnil pointer capture → pointercancel → drag padne
                  const wrap = (el) => <React.Fragment key={blk.key}>{(phBefore || blockPh) && <DropPh />}{blockHidden ? React.cloneElement(el, { className: [el.props.className, "bz-drag-src"].filter(Boolean).join(" ") || undefined }) : el}</React.Fragment>;
                  if (blk.type === "chal") { return wrap(renderChalBlock(blk.t)); }
                  if (blk.type === "comp") { return wrap(renderCompBlock(blk.comp)); }
                  if (blk.type === "task") {
                    const t = blk.task;
                    // v25 — done tint na celej karte; samostatný návyk sa ťahá ako NÁVYK, ale je aj blok (cieľ)
                    // v32.22 — hotový návyk bez tieňa (splýva s pozadím, nepôsobí ako aktívna karta)
                    return wrap(<List data-blockkey={blk.key} style={t.done && !editMode ? { background: "var(--page-grad)", boxShadow: "none" } : undefined}>{renderHabitRow(t)}</List>);
                  }
                  const c = blk.cat;
                  const items = dueByCat[c.id] || [];
                  const groupComps = compByCat[c.id] || [];   // wt34 — súťaže v tejto skupine
                  // v35 — návyky a súťaže v skupine sa PREPLIETAJÚ podľa jednotného item_order
                  // (kľúče "task:<id>"/"comp:<id>"); default = návyky (podľa sort) potom súťaže.
                  // Stabilný sort → položky mimo item_order držia default poradie (nové na koniec).
                  const gio = (() => { try { return JSON.parse(c.item_order || "[]"); } catch { return []; } })();
                  const gioPos = new Map(gio.map((k, i) => [k, i]));
                  const mergedItems = [
                    ...items.map((t) => ({ isComp: false, key: "task:" + t.id, dragId: t.id, t })),
                    ...groupComps.map((cp) => ({ isComp: true, key: "comp:" + cp.id, dragId: COMP_DRAG + cp.id, comp: cp })),
                  ].sort((a, b) => (gioPos.has(a.key) ? gioPos.get(a.key) : 1e9) - (gioPos.has(b.key) ? gioPos.get(b.key) : 1e9));
                  const ord = editMode;  // in edit mode: routines expand all habits (drag to reorder)
                  const open = !collapsed[c.id] && !ord;
                  const rpct = c.due_today ? Math.round((c.done_today / c.due_today) * 100) : 0;
                  const routineDone = c.due_today > 0 && c.done_today >= c.due_today;  // v2 — celá rutina hotová
                  const catKey = String(c.id);
                  return wrap(
                    <div data-catid={c.id} data-blockkey={blk.key}>
                    <GroupCard
                      /* v25 §10.4 — skupina bez ikony v Dnes (návrh B); ikona ostáva v detaile/úprave */
                      title={c.name}
                      done={!ord && routineDone}
                      count={ord ? undefined : `${c.done_today} / ${c.due_today}`} pct={ord ? null : rpct}
                      collapsed={ord ? false : !!collapsed[c.id]}
                      /* hlavička sa ťahá ako celý blok (blockDnd); tap = detail skupiny.
                         onHeaderClick je VŽDY nastavený, aby hlavička ostala ten istý element
                         (button) aj keď sa počas ťahu zapne editMode — inak by React remountoval
                         hlavičku, uvoľnil pointer capture a drag by padol (pointercancel). */
                      headerProps={{ ...blockDnd.handlers, role: "button" }}
                      onHeaderClick={swallowBlock(() => setDetailRoutine(c))}
                      headerRight={ord ? null : (
                        <span role="button" tabIndex={0} aria-label={open ? "Zbaliť" : "Rozbaliť"}
                          onClick={(e) => { e.stopPropagation(); setCollapsed((p) => ({ ...p, [c.id]: !p[c.id] })); }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setCollapsed((p) => ({ ...p, [c.id]: !p[c.id] })); } }}
                          style={{ display: "inline-flex", cursor: "pointer", padding: 4, color: "var(--neutral-text-subtle)", transform: open ? "rotate(90deg)" : "rotate(-90deg)", transition: "transform .15s" }}><Icon name="chevron" size={18} color="currentColor" /></span>
                      )}
                      onAdd={undefined}>
                      {/* v35 — jeden preplietaný zoznam: návyk/výzva (renderHabitRow/renderChalRow)
                          aj súťaž (renderCompGroupRow) v poradí item_order; placeholder podľa dragId
                          (task id, resp. COMP_DRAG+comp.id — presne to, čo vracia computeDrop.beforeId) */}
                      {mergedItems.map((it) => (
                        <React.Fragment key={it.key}>
                          {dropAt && dropAt.cat === catKey && dropAt.beforeId === it.dragId && <DropPh />}
                          {it.isComp
                            ? renderCompGroupRow(it.comp)
                            : (it.t.challenge_id ? renderChalRow(it.t) : renderHabitRow(it.t))}
                        </React.Fragment>
                      ))}
                      {dropAt && dropAt.cat === catKey && dropAt.beforeId == null && mergedItems.length > 0 && <DropPh />}
                      {ord && !mergedItems.length && dropAt && dropAt.cat === catKey && <DropPh />}
                    </GroupCard>
                    </div>
                  );
                })}
                {/* koniec zoznamu (drop na spodok) — samostatný návyk aj celý blok */}
                {dropAt && dropAt.cat === "_solo" && dropAt.beforeId == null && !dropAt.beforeKey && <DropPh />}
                {blockDropAt && blockDropAt.beforeKey == null && <DropPh />}
              </div>
              {/* v33b — sekcia „Máš čas": zriedkavé návyky (N× za mesiac/pol rok/rok) POD bežnými.
                  Reorder je scoped v rámci sekcie (mascasDnd), nemieša sa s bežnými návykmi. */}
              {masCasSorted.length > 0 && (
                <div data-mascas="1" style={{ marginTop: 4 }}>
                  <SectionHeader label="Máš čas" icon="clock" count={masCasSorted.length} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {masCasSorted.map((t) => (
                      <React.Fragment key={t.id}>
                        {mcDropAt && mcDropAt.beforeId === t.id && <DropPh />}
                        <List style={t.done ? { background: "var(--page-grad)", boxShadow: "none" } : undefined}>{renderHabitRow(t, { mascas: true })}</List>
                      </React.Fragment>
                    ))}
                    {mcDropAt && mcDropAt.beforeId == null && <DropPh />}
                  </div>
                </div>
              )}
              {/* v32.22 — pri zapnutom oku drobný riadok „koľko splnených je skrytých → zobraziť";
                  pri perfektnom dni ho kryje oslavný deň-kruh hore, tu ho preto neukazujeme. */}
              {hideActive && hiddenCount > 0 && !allDone && (
                <button type="button" onClick={() => setHideDone(false)}
                  style={{ ...st.fieldHint, marginBottom: 0, marginTop: 12, background: "none", border: 0, padding: "2px 2px", cursor: "pointer", textAlign: "left" }}>
                  Skrytých splnených: {hiddenCount} — zobraziť
                </button>
              )}
              </>
            );
          })()}
          {/* v26.2 §6 — samostatná sekcia „Výzvy a súťaže" zrušená: výzvy/súťaže sú teraz bloky hlavného zoznamu vyššie. */}
        </div>
      )}
      {/* v17 §1 — empty states, add-habit, create-challenge, closed comps: Zoznam only (skryté v tabuľke) */}
      {isList && !habitTable && (<>
      {/* H3 — all habits exist but none scheduled today */}
      {viewMode !== "table" && !editMode && displayTasks.length > 0 && listShownTasks.length === 0 && shownComps.length === 0 && shownTasks.length > 0 && (
        <div style={st.empty}>Dnes nemáš naplánovaný žiadny návyk — uži si voľno, séria beží ďalej.</div>
      )}
      {displayTasks.length === 0 && openComps.length === 0 && (
        <div data-tour="habit-empty" style={{ padding: "6px 0 2px" }}>
          <div style={{ ...st.empty, marginBottom: editMode ? 0 : 12 }}>Zatiaľ žiadne návyky.</div>
          {/* v27 — plnošírkové tlačidlo ako edit-mode „Nový návyk"; v edit-mode ho skry, nech sa nebije s ním */}
          {!editMode && <Button variant="outline" block onClick={() => { setAddCat(null); openOnly("habit"); }}><BuzerIcon name="add" size={17} color="currentColor" strokeWidth={2.2} />Pridať prvý návyk</Button>}
        </div>
      )}
      {displayTasks.length > 0 && shownTasks.length === 0 && shownComps.length === 0 && !editMode && (
        <div style={st.empty}>V tejto kategórii zatiaľ nič — prepni filter vyššie.</div>
      )}
      {/* v31.6 — deň-kruh presunutý NAD zoznam (do karty), nesplnené súťaže ostávajú pod ním */}

      {/* v31.7 — sekcia „Ako sa dnes cítiš?" z Dnes odstránená: náladu zapíšeš smajlíkom v stripe
          (mood modál s emoji + „prečo"). whyModal ostáva zatiaľ nedostupný (rezerva). */}
      {whyModal && (
        <Modal title="Prečo sa dnes tak cítiš?" icon="feather" onClose={() => setWhyModal(false)} maxWidth={420}>
          <div style={st.taskDesc}>
            Zapíš si, čo ovplyvnilo tvoju náladu. Neskôr klikni na náladu v grafe a uvidíš,
            prečo si sa daný deň tak cítil{session.gender === "m" ? "" : session.gender === "x" ? "/a" : "a"}.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input className="field" style={{ ...st.field, flex: 1, minWidth: 0 }}
              placeholder="napr. dobre som sa vyspal, pekné počasie…" value={why} maxLength={280} autoFocus={!IS_TOUCH}
              onChange={(e) => setWhy(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveWhy(); } }} />
            <button type="button" onClick={saveWhy} disabled={!whyDirty && !whySaved}
              style={{ ...st.primaryBtn, opacity: (whyDirty || whySaved) ? 1 : 0.55, cursor: whyDirty ? "pointer" : "default" }}>
              {whySaved ? <><Chk />Uložené</> : (whyDirty ? (savedWhy ? "Upraviť" : "Uložiť") : "Uložiť")}
            </button>
          </div>
        </Modal>
      )}
      {/* v31 — sekcia „AI asistent" na dne Dnes ZRUŠENÁ (AI veta žije v dlaždici dňa hore);
          používatelia bez AI naďalej vidia denný citát. */}
      {!session.ai && (
        <p style={{ ...st.quote, marginTop: 18, marginBottom: 18 }}>„{quote}"</p>
      )}

      {/* v20 — tlačidlá Pridať/Vytvoriť na konci Dnes */}
      <div style={{ marginTop: 18 }} />
      <AddCard key={"addhabit-" + (addCat ?? "x")} kind="habit" onAdded={async (id) => { await reload(); if (id != null) setFlashId(id); }} cta="Pridať návyk" routines={s.categories || []} lite={!!s.lite_mode}
        initial={addCat != null ? { category_id: addCat, icon: "target" } : undefined}
        open={openForm === "habit"} onOpenChange={(v) => { openOnly(v ? "habit" : null); if (!v) setAddCat(null); }} />
      {/* v17 — "Navrhni mi návyky" (AI), now files suggestions into routines */}
      {openForm === "suggest" && <SuggestCard onAdded={reload} routines={s.categories || []}
        open onOpenChange={(v) => openOnly(v ? "suggest" : null)} />}
      {/* v26.2 §6 — jeden vstup „Nová výzva" (spôsob zapisovania sa volí vo formulári); výzvy sú v Lite skryté */}
      {!s.lite_mode && (session.premium
        ? <ChallengeCreate onAdded={reload} mode="challenge"
            open={openForm === "challenge"} onOpenChange={(v) => openOnly(v ? "challenge" : null)} />
        : <ProButton label="Spoločné výzvy" />)}

      {/* v20 — „Ukončené súťaže" odstránené z Dnes (sú v Prehľade → Vyhodnotené súťaže) */}
      </>)}
      </>)}

      {/* v20 §19 — Odznaky presunuté vyššie (hneď za Úroveň) */}

      {/* v17 — Týždeň overview footer; v29 poradie: História → Aktivita → Odznaky → Archív */}
      {isTable && (<>
      {/* v25 §5 — vstup do Histórie z Prehľadu; v31 — sekcia „Týždeň a história" odišla z Prehľadu
          (viď poznámka pri inSec("habits") vyššie) — táto vetva je teraz mŕtva pre oba režimy,
          História žije na Dnes → Zoznam⇄Týždeň (task #3/#4, v33i). */}
      {inSec("habits") && (
      <button type="button" onClick={() => setShowHistory(true)} title="Mesačná história splnení"
        style={{ ...st.ovCard, width: "100%", marginTop: 12, display: "flex", flexDirection: "row", alignItems: "center", gap: 12, textAlign: "left" }}>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: "50%", background: "var(--brand-primary-surface)", color: "var(--brand-primary)", flexShrink: 0 }}><BuzerIcon name="calendar" size={18} color="currentColor" /></span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontWeight: 700, fontSize: 14.5, color: "var(--text)" }}>História</span>
          <span style={st.taskDesc}>Kedy si čo splnil/a — celý mesiac naraz, aj archivované návyky</span>
        </span>
        <span style={{ display: "inline-flex", color: "var(--muted)" }}><Icon name="chevron" size={18} color="currentColor" /></span>
      </button>
      )}
      {/* v32.15 — Heat mapa „Aktivita" priamo v Prehľade (bez ďalšieho modalu).
          v33i (task #5) — Lite dostáva zjednodušenú verziu (lite prop): len % úspešnosti + nálada
          na deň, plus súhrn série a perfektných dní; žiadne odznaky/body. */}
      {prehSec === "heat" && <HeatmapInline lite={!!s.lite_mode}
        streak={s.streak} streakActive={(s.doneDays || []).includes(todayKey())}
        best={s.bestStreak} bestEnd={s.bestStreakEnd}
        perfectDays={(s.perfectDays || 0) + (allDone ? 1 : 0)}
        moods={s.moodHistory || {}} moodNotes={s.moodNotes || {}} />}
      {/* v33b — Odznaky: VŠETKY odznaky priamo v sekcii (bez modalu „Všetky"); zoskupené po
          kategóriách s progresom k ďalšiemu tieru (BadgeGrid), zamknuté stlmené.
          Modal „Všetky" zrušený → tým padá aj samostatné tlačidlo, ktoré sa mal reštylovať
          na ghost „Zdieľať" (task #4 nahradený task #3). */}
      {!s.lite_mode && prehSec === "badges" && (s.badges || []).length > 0 && (<>
        {/* v33h — horný súhrn x/y zrušený (počty sú pri každej kategórii v BadgeGride); kotva ostáva */}
        <div data-scroll="odznaky" />
        {/* BadgeGrid so `cat` payloadom sa sám zoskupí po kategóriách + vlastnú GPU vrstvu si
            drží interne (nevkladať do levelBoxu — vnorená vrstva = Mali glitch) */}
        <BadgeGrid badges={s.badges} onPick={setBadgeInfo} />
        <div style={{ marginBottom: 22 }} />
      </>)}
      {inSec("archive") && <ArchiveSection reload={reload} showTitle={!!s.lite_mode} />}
      {prehSec === "stats" && <div style={st.footer}>Aktívnych dní celkom: <strong>{activeDays}</strong> · Séria rastie každý deň, keď splníš aspoň jeden návyk.</div>}
      </>)}

      {/* v2 redesign — tap a habit → detail sheet (splniť / časovač / upraviť / zmazať) */}
      {detailTask && (
        <TaskDetail
          task={detailTask}
          routineName={(s.categories || []).find((c) => c.id === detailTask.category_id)?.name || null}
          lite={!!s.lite_mode}
          onClose={() => setDetailTask(null)}
          // f-batch — posielaj tap element ďalej, nech „Splniť" z detailu dostane ROVNAKÝ
          // rozpŕskavajúci efekt ako riadok v zozname (predtým bez neho — jediný viditeľný efekt
          // bol príležitostné padajúce konfety pri dokončení celého dňa).
          onToggle={(e) => toggle(detailTask.id, undefined, e && e.currentTarget)}
          onSkip={(on) => { if (on) skipToday(detailTask.id, true); else setUnskipTask(detailTask); }}
          onEdit={() => { setOpenForm(null); setEditingId(detailTask.id); }}
          onDelete={() => { const id = detailTask.id; setDetailTask(null); deleteHabit(id); }}
        />
      )}

      {/* v2 — confirm before undoing a completed habit */}
      {uncheckTask && (
        <Sheet center title="Zrušiť splnenie?" icon={resolveIcon(uncheckTask.icon) || "target"} iconStyle={iconChipStyle(uncheckTask.icon_color)} onClose={() => setUncheckTask(null)}
          footer={<>
            <Button variant="ghost" onClick={() => setUncheckTask(null)}>Nie</Button>
            <Button variant="destructive" block onClick={() => { const id = uncheckTask.id; setUncheckTask(null); toggle(id); setChalRefreshKey((k) => k + 1); }}><BuzerIcon name="undo" size={16} color="currentColor" />Vrátiť splnenie</Button>
          </>}>
          <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5 }}>Naozaj chceš zrušiť dnešné splnenie návyku <strong>{uncheckTask.label}</strong>?{!s.lite_mode ? " Body sa odpočítajú." : ""}</div>
        </Sheet>
      )}

      {/* v25 §10.8 — potvrdenie zrušenia preskočenia */}
      {unskipTask && (
        <Sheet center title="Zrušiť preskočenie?" icon={resolveIcon(unskipTask.icon) || "target"} iconStyle={iconChipStyle(unskipTask.icon_color)} onClose={() => setUnskipTask(null)}
          footer={<div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
            <Button variant="destructive" block onClick={() => { const id = unskipTask.id; setUnskipTask(null); skipToday(id, false); }}><BuzerIcon name="undo" size={16} color="currentColor" />Zrušiť preskočenie</Button>
            <Button variant="ghost" block onClick={() => setUnskipTask(null)}>Ponechať preskočené</Button>
          </div>}>
          <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5 }}>Naozaj chceš zrušiť dnešné preskočenie návyku <strong>{unskipTask.label}</strong>? Deň prestane byť ospravedlnený.</div>
        </Sheet>
      )}

      {/* v2 — edit a habit in a top-level modal so the row stays visible underneath */}
      {editingId != null && (() => {
        const eh = (s.habits || []).find((h) => h.id === editingId) || (displayTasks || []).find((t) => t.id === editingId);
        return eh ? <AddCard key={eh.id} kind="habit" task={eh} onAdded={reload} onCancel={() => setEditingId(null)} lite={!!s.lite_mode}
          routines={s.categories || []} onDelete={() => { setEditingId(null); deleteHabit(eh.id); }} /> : null;
      })()}

      {/* v2 — ⋯ action menu for a habit (from TaskDetail); "Menu úlohy" design */}
      {menuTask && (
        <ActionMenu title={menuTask.label} icon={resolveIcon(menuTask.icon) || "target"} onClose={() => setMenuTask(null)}
          items={[
            { icon: "folder", label: "Pozastaviť", desc: "Archivovať bez vymazania", onClick: () => { setDetailTask(null); archiveHabit(menuTask.id); } },
            { icon: "trash", label: "Vymazať úlohu", danger: true, dividerBefore: true, onClick: () => { setDetailTask(null); deleteHabit(menuTask.id); } },
          ]}
        />
      )}

      {/* v2 — routine (skupina) detail + edit */}
      {detailRoutine && (
        <RoutineDetail routine={detailRoutine} lite={!!s.lite_mode} onClose={() => setDetailRoutine(null)} onEdit={() => setEditRoutine(detailRoutine)}
          onDelete={() => { setEditRoutine(detailRoutine); setRoutineStartDel(true); }} />
      )}
      {editRoutine && (
        <RoutineEdit routine={editRoutine} habits={s.habits || []} reload={reload} startDelete={routineStartDel}
          onClose={() => { setEditRoutine(null); setRoutineStartDel(false); }}
          onAddHabit={(cid) => { setAddCat(cid); openOnly("habit"); }} />
      )}


      {/* v20 §29 / v31 — mazať návyk cez zdieľané ConfirmDelete (archív = primary, jedno miesto v kóde) */}
      {deleteId && (() => {
        const delHabit = (displayTasks || []).find((t) => t.id === deleteId) || (s.habits || []).find((t) => t.id === deleteId) || {};
        return (
          <ConfirmDelete title="Zmazať návyk"
            icon={resolveIcon(delHabit.icon) || "trash"} iconStyle={iconChipStyle(delHabit.icon_color)}
            message={<>Návyk sa vymaže natrvalo. Ak ho chceš len skryť a zachovať históriu, radšej ho <strong>archivuj</strong> (nájdeš ho v Archíve).</>}
            onArchive={() => doDeleteHabit(true)}
            deleteLabel="Zmazať natrvalo" onDelete={() => doDeleteHabit(false)}
            onClose={() => setDeleteId(null)} />
        );
      })()}

      {/* v8 — Štatistiky is now a drawer destination; no bottom pill */}
      {/* v32.22 — perfektný deň: ponuka pochváliť sa priateľom (raz za deň) */}
      {perfectOffer && (
        <Modal center title="Perfektný deň!" icon="target" onClose={() => { setPerfectOffer(false); flushPendingLevelUp(); }} maxWidth={380}
          cardStyle={{ background: "var(--brand-primary-surface)", "--neutral-surface": "var(--brand-primary-surface-strong)", "--surface-solid": "var(--brand-primary-surface-strong)" }}>
          {/* C2 — hero kruh, ktorý sa animovane naplní (napĺňacia choreografia sa presunula sem z Dnes);
              secondary odtieň témy = --brand-primary-surface (v tokens.css nie je samostatný „secondary"
              tint, používam existujúci brand-soft token — pozn. pre orchestrátor) */}
          <div style={{ background: "var(--brand-primary-surface)", borderRadius: "var(--radius-lg)", padding: "6px 6px 2px", margin: "0 auto 14px", maxWidth: 300 }}>
            <DayRing tasks={dueTasks} justCompletedId={null} lite={!!s.lite_mode}
              todayPts={todayPts} dayMax={dayMax} doneCount={doneCount} allDone perfectStreak={s.perfectStreak || 0} />
          </div>
          <div style={{ ...st.taskDesc, marginBottom: 14 }}>Máš dnes všetko splnené. Ale čo z toho, keď o tom nikto nevie?</div>
          {/* C3 — flex-wrap + flex:1/min-width:0, nech sa „Zdieľať" nevytlačí mimo modalu na úzkych šírkach */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant="ghost" block style={{ flex: "1 1 120px", minWidth: 0 }} onClick={() => { setPerfectOffer(false); flushPendingLevelUp(); }}>Nechať pre seba</Button>
            <Button variant="primary" block style={{ flex: "1 1 120px", minWidth: 0 }} onClick={() => { setPerfectOffer(false); setPerfectShareOpen(true); }}>
              <BuzerIcon name="share" size={15} color="currentColor" /> Zdieľať
            </Button>
          </div>
        </Modal>
      )}
      {perfectShareOpen && (
        <ShareStatSheet stats={[{ icon: "target", label: "Perfektný deň", value: `${doneCount}/${_gTot} · 100 %`, accent: "var(--stat-growth)" }]}
          onClose={() => { setPerfectShareOpen(false); flushPendingLevelUp(); }} />
      )}
      {toast && <div style={st.toast} role="status">{toast}</div>}
    </>
  );
  // v32.17 — Prehľad je samostatný TAB (nie modál): obsah (pilulky = navigácia, tlačidlo Zdieľať
  // vedľa nich) sa renderuje priamo ako stránka tabu. Titulok „PREHĽAD" (typewriter) žije v TopBare.
  // Sekcie Prehľadu sa dajú prepínať aj horizontálnym swipeom (vľavo = ďalšia); v33i (task #4) — aj Lite.
  if (isTable) {
    const sections = s.lite_mode ? LITE_PREH_SECTIONS : PREH_SECTIONS;
    const swipeSec = (dir) => {
      const idx = sections.findIndex((x) => x.key === prehSec);
      const next = sections[idx + dir];
      if (next) changeSec(next.key);
    };
    return (
      <div style={{ minHeight: "100%" }}
        onTouchStart={(e) => { const t = e.touches[0]; prehSwipe.current = { x: t.clientX, y: t.clientY }; }}
        onTouchEnd={(e) => {
          const s0 = prehSwipe.current; prehSwipe.current = null;
          if (!s0) return;
          const t = e.changedTouches[0];
          const dx = t.clientX - s0.x, dy = t.clientY - s0.y;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) swipeSec(dx < 0 ? 1 : -1);
        }}>
        {page}
      </div>
    );
  }
  return page;
}
